// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Six routes, and the first one is the point: `/my-day` is the screen the field
// staff opens and the only one some of them ever open. It sits behind
// `workorders:report`, so a technician reaches their own van without being
// allowed to browse the whole company's work.

export const routes = [
  {
    path: '/my-day',
    name: 'my-day',
    component: () => import('./pages/MyDayPage.vue'),
    meta: { permission: 'workorders:report', title: 'work-orders.myDay.title' },
  },
  // Apuntar una faena. Una ruta y no un diálogo, como cualquier otra alta del
  // ERP: se puede enlazar, sobrevive a girar el móvil y el botón de atrás
  // pregunta antes de tirar lo escrito. Detrás de `report` y no de `write`,
  // igual que `/my-day` y por lo mismo — quien apunta lo que ha hecho es quien
  // lo ha hecho.
  //
  // Antes de `/work-orders/:id` solo por orden de lectura; no choca con nada.
  {
    path: '/work-orders/log',
    name: 'work-order-log',
    component: () => import('./pages/LogWorkPage.vue'),
    meta: { permission: 'workorders:report', title: 'work-orders.log.title' },
  },
  // ── El constructor de plantillas de parte ──────────────────────────────────
  //
  // Qué quiere esta empresa que se apunte al cerrar un servicio. Es
  // CONFIGURACIÓN y no un documento, así que va detrás de `manage` y el
  // trabajador no la abre nunca.
  //
  // Rutas estáticas: ganan a `/work-orders/:id` por ranking del enrutador, así
  // que su sitio en esta lista da igual — están aquí por orden de lectura.
  {
    path: '/work-orders/templates',
    name: 'worksheet-templates',
    component: () => import('./pages/WorksheetTemplatesPage.vue'),
    meta: { permission: ['workorders:templates', 'workorders:manage'], title: 'work-orders.worksheets.title' },
  },
  {
    path: '/work-orders/templates/new',
    name: 'worksheet-template-new',
    component: () => import('./pages/WorksheetTemplateFormPage.vue'),
    meta: { permission: ['workorders:templates', 'workorders:manage'], title: 'work-orders.worksheets.newTitle' },
  },
  {
    path: '/work-orders/templates/:id',
    name: 'worksheet-template',
    component: () => import('./pages/WorksheetTemplateFormPage.vue'),
    meta: { permission: ['workorders:templates', 'workorders:manage'], title: 'work-orders.worksheets.editTitle' },
  },
  {
    path: '/work-orders',
    name: 'work-orders',
    component: () => import('./pages/WorkOrdersPage.vue'),
    meta: { permission: 'workorders:read', title: 'work-orders.title' , ownsQuery: true /* limpia la consulta después de leerla: el armazón no la remonta al cambiar */ },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  // The planner covers the shell (`position: fixed`) rather than sitting inside
  // it: a round is the order on the left and its stops on the right, and
  // neither survives being squeezed next to a sidebar. It is still a route and
  // not a modal, so a half-planned round can be linked to, reloaded and come
  // back from the browser's back button.
  {
    path: '/work-orders/new',
    name: 'work-order-new',
    component: () => import('./pages/WorkOrderPlannerPage.vue'),
    meta: { permission: 'workorders:write', title: 'work-orders.newTitle', fullScreen: true },
  },
  {
    path: '/work-orders/:id',
    name: 'work-order',
    component: () => import('./pages/WorkOrderPage.vue'),
    // `report` as well, so «Mi día» can link straight into the file of the job
    // the technician is standing in front of.
    meta: { permission: ['workorders:read', 'workorders:report'], title: 'work-orders.detailTitle' },
  },
  // Rellenar el parte del día. Una pantalla entera, en el móvil y en el
  // escritorio, y no un diálogo encima de otra: un parte es el documento que se
  // escribe todos los días —horas, tareas, fotos y material de cada sitio— y
  // eso no cabe en una ventanita que se cierra al tocar fuera y pierde lo
  // escrito.
  //
  // Detrás de `report` y no de `read`: quien lo rellena es quien estuvo allí, y
  // no tiene por qué poder abrir la lista de órdenes del despacho.
  {
    path: '/work-orders/:id/report',
    name: 'work-order-report',
    component: () => import('./pages/ReportPage.vue'),
    meta: { permission: 'workorders:report', title: 'work-orders.parte.title' },
  },
  // El parte guiado: la conversación que rellena la plantilla de la empresa.
  // Detrás de `report`, como el parte de siempre — lo rellena quien estuvo. Es
  // una ruta y no un diálogo por lo mismo que la otra: se puede enlazar, gira
  // con el móvil y el botón de atrás no se lleva la conversación por delante.
  {
    path: '/work-orders/:id/worksheet',
    name: 'work-order-worksheet',
    component: () => import('./pages/WorksheetPage.vue'),
    meta: { permission: 'workorders:report', title: 'work-orders.worksheet.title' },
  },
  {
    path: '/work-orders/:id/edit',
    name: 'work-order-edit',
    component: () => import('./pages/WorkOrderPlannerPage.vue'),
    meta: { permission: 'workorders:write', title: 'work-orders.editTitle', fullScreen: true },
  },
]

/**
 * Two entries, and that is deliberate.
 *
 * «Mi día» is not a child of Work orders: they are different screens for
 * different people, and for half the staff «Mi día» is the whole ERP. The shell
 * filters both by permission before drawing them, so the office sees one and the
 * crew sees the other.
 */
/**
 * Lo que este módulo aporta a la DOCUMENTACIÓN del ERP.
 *
 * El armazón monta la sección de ayuda —el índice, el marco, la dirección— y
 * cada módulo escribe lo suyo: quien sabe cómo funciona el parte por plantilla
 * es este módulo, y un manual escrito en la plataforma sería la plataforma
 * sabiendo qué módulos hay.
 *
 * El `id` ES la dirección (`/help/work-orders.worksheets`), así que el «?» de
 * una cabecera enlaza ahí, se puede marcar en el navegador y se puede mandar a
 * quien empieza. Una instancia sin este módulo no tiene esta página, igual que
 * no tiene su menú, y nadie ha tenido que quitarla.
 */
export const help = [
  {
    id: 'work-orders.worksheets',
    titleKey: 'work-orders.help.title',
    summaryKey: 'work-orders.help.subtitle',
    component: () => import('./components/WorksheetHelp.vue'),
  },
]

export const navigation = [
  {
    to: '/my-day',
    labelKey: 'work-orders.myDay.title',
    permission: 'workorders:report',
    icon: 'clipboard',
    group: 'business',
    order: 10,
  },
  {
    to: '/work-orders',
    labelKey: 'work-orders.title',
    permission: 'workorders:read',
    icon: 'wrench',
    group: 'business',
  },
  // Las plantillas de parte. Detrás de `templates`, así que al armazón le sale
  // solo a quien configura: la cuadrilla no la ve, y no tiene por qué.
  //
  // Un permiso solo y no `['workorders:templates', 'workorders:manage']` como
  // las rutas: la demo calcula su alcance leyendo esta entrada como texto y una
  // lista no la entiende (platform/demo/tests/navigationCoverage.test.js). El
  // administrador recibe `templates` al arrancar; un rol propio que solo tenga
  // `manage` sigue pudiendo entrar por la dirección y por la API, y para verlo
  // en el menú se le marca `templates` en Roles.
  //
  // En `business` y no en un grupo «ajustes» propio: el armazón solo conoce
  // tres —`main`, `business` y `platform`— y una entrada con un grupo inventado
  // no sale por ninguna parte, sin error en ningún sitio. Al final del bloque
  // (`order`), que es configuración y no trabajo del día.
  {
    to: '/work-orders/templates',
    labelKey: 'work-orders.worksheets.title',
    permission: 'workorders:templates',
    // Un dibujo libre: `clipboard` ya es «Mi día», `checklist` es de tareas y
    // `sliders` es Ajustes de la plataforma. Dos con el mismo dibujo no son
    // navegación, y hay un test del armazón que lo vigila.
    icon: 'box',
    group: 'business',
    order: 80,
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16), including what this module is called on the PLATFORM's own
// screens — Roles, Settings, Audit and the assistant panel — because a module
// that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    'work-orders': {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        today: 'una orden hoy | {count} órdenes hoy',
        late: 'una orden atrasada | {count} órdenes atrasadas',
        myDay: 'una orden en tu día | {count} órdenes en tu día',
        myDayLate: '{count} órdenes en tu día, {late} atrasadas',
        replan: 'Replanificar',
        replanAsk: 'Replanifica la orden atrasada: propón una fecha nueva | Replanifica las {count} órdenes atrasadas: propón una fecha nueva para cada una',
      },
      title: 'Órdenes de trabajo',
      // Los dos montones que dibuja la ficha de un sitio: lo que falta
      // por hacer allí y lo que ya se hizo.
      related: {
        ahead: 'Por hacer aquí',
        done: 'Lo que se ha hecho',
        cancelled: 'Anuladas',
      },
      doneBanner: {
        title: 'Orden completada',
        when: 'Cerrada el {when}',
      },
      dashboard: {
        logWork: 'Apuntar trabajo sin orden',
        tabToday: 'Hoy',
        tabTomorrow: 'Mañana',
        summaryOrders: 'Un trabajo | {count} trabajos',
        summarySites: 'un sitio | {count} sitios',
        summaryDone: 'uno hecho | {count} hechos',
        summaryTeam: 'Equipo: {names}',
        summaryMates: 'Con {names}',
        emptyToday: 'Hoy no tienes órdenes',
        emptyTomorrow: 'Mañana no tienes órdenes',
        next: 'Siguiente',
        open: 'Órdenes abiertas',
        openHelp: 'Cuántas órdenes de trabajo siguen abiertas, en cualquiera de sus estados vivos.',
        today: 'Para hoy',
        todayHelp: 'Cuántas órdenes están planificadas para el día de hoy, cerradas incluidas.',
        late: 'Órdenes con retraso',
        lateHelp: 'Cuántas órdenes abiertas tenían fecha anterior a hoy y siguen sin cerrarse.',
        todayList: 'La agenda de hoy',
        todayListHelp: 'Las órdenes planificadas para hoy, con su cliente o su dirección. Estira la tarjeta y caben más.',
        myDay: 'Mi día',
        myDayHelp: 'Las órdenes que tenéis asignadas para hoy. Cada quien ve las suyas, y estirando la tarjeta caben más.',
      },
      subtitle: 'Quién va, cuándo, a dónde — y qué pasó allí',
worksheet: {
        call: {
          title: 'La llamada',
          pitch: 'Cuéntaselo de corrido y él lo va apuntando. Al final lo repasas y lo guardas tú.',
          start: 'Empezar la llamada',
          repeat: 'Repite la pregunta',
          hangUp: 'Colgar',
          phase: {
            greeting: 'Un momento…',
            asking: 'Te pregunto…',
            listening: 'Te escucho',
            thinking: 'Un momento…',
            handover: 'Esto se elige en la pantalla',
            closing: 'Repasemos',
          },
          greeting: 'Hola, vamos con el parte de hoy.',
          greetingName: 'Hola {name}, ¿qué tal el día?',
          opener: 'Cuéntame, ¿qué tal ha ido hoy?',
          onScreen: 'Esto lo tienes que elegir en la pantalla: {label}. Te espero.',
          stillThere: '¿Sigues ahí? Te lo repito.',
          notUnderstood: 'No he sacado el dato de ahí. ¿Me lo dices otra vez?',
          byHand: 'Mejor lo escribes tú, que así no lo liamos.',
          byHandNote: 'Te he dejado «{label}» para que lo escribas tú.',
          done: 'Ya está, no me falta nada. Te lo repaso.',
          review: 'Míralo en la pantalla y, si está bien, dale a guardar.',
          resume: 'Seguir hablando',
          keyboard: 'Prefiero escribirlo',
          orTap: 'O tócalo:',
          ackA: 'Vale.',
          ackB: 'Ajá.',
          ackC: 'Perfecto.',
          debug: {
            tab: 'Desarrollo',
            title: 'Desarrollo · la llamada por dentro',
            clear: 'Vaciar',
            turns: 'Vueltas',
            avg: 'media {t}',
            llm: 'EURouter · texto',
            stt: 'EURouter · oído',
            tts: 'Azure · voz',
            fromCache: '{n} de caché',
            total: 'Total de la llamada',
            silence: 'espera de silencio: {first} ms en respuestas cortas, {rest} ms al contar lo hecho',
            heard: 'Oído:',
            says: 'Dice:',
            behind: 'detrás',
            cache: 'caché',
            rest: 'red · cola · servidor',
            empty: 'Empieza la llamada y aquí se verá cada vuelta: qué tarda cada paso, cuántos tokens gasta EURouter y cuántos caracteres Azure.',
            steps: {
              stt: 'Oír (EURouter)',
              read: 'Repartir (EURouter)',
              place: 'Buscar en el ERP',
              talk: 'Frase (EURouter)',
              tts: 'Voz (Azure)',
              state: 'Estado',
              settle: 'Esperar lo de detrás',
            },
          },
          tapToPick: 'Elegir en la pantalla',
          onKeyboard: 'Colgado. Cuando quieras, seguimos hablando.',
          dontCall: 'No me llames al abrir',
          incoming: 'Te llama el encargado',
          again: 'Perdona, no te he pillado.',
          notFoundPlace: 'Ese no lo tengo.',
          askLabel: '¿{label}?',
          whichPlace: '¿En {list}?',
          whichOption: '¿{list}?',
          letsGo: 'Vamos con el parte.',
          incomingHint: 'Para que le cuentes qué tal el día. Contesta cuando puedas hablar.',
          answer: 'Contestar',
          decline: 'Ahora no',
          callBack: 'Llamar al encargado',
          willNotCall: 'Ya no sonará al abrir un parte en este móvil. El botón de llamar sigue aquí.',
          or: 'o',
          whichAsk: 'Hay más de un {label}. ¿Cuál de estos: {options}?',
          whichOne: '«{words}» puede ser más de un {label}. ¿Cuál era?',
          pickOnScreen: 'Elígelo tú en la pantalla, que ahí los tienes los dos.',
          notFound: 'No encuentro ningún {label} que se llame «{words}». Búscalo tú en la pantalla.',
        },
        title: 'Apuntar el parte',
        said: 'Lo que llevas dicho',
        now: 'Ahora',
        readBack: 'Repasemos',
        fromSystem: 'lo sé yo',
        fromSystemLocked: 'Eso lo rellena el sistema y no hace falta que lo digas.',
        speak: 'Contestar hablando',
        heard: 'Te he oído:',
        answer: 'Contestar',
        skip: 'Saltar',
        yes: 'Sí',
        no: 'No',
        pick: 'Elige de la lista',
        listen: 'Léemelo',
        confirm: 'Guardar el parte',
        done: 'Parte guardado',
        none: 'Esta empresa no tiene plantilla de parte. Se apunta como siempre.',
        left: 'No queda nada | Queda 1 cosa por decir | Quedan {n} cosas por decir',
        files: 'Ningún archivo | 1 archivo | {n} archivos',
        savedHere: 'Sin cobertura: guardado en el móvil y se manda al volver la línea.',
        queued: 'Hay {n} sin mandar. Se envían solas al volver la cobertura.',
        stillQueued: 'Quedan {n} respuestas sin mandar. Busca cobertura antes de confirmar el parte.',
      },
      help: {
        title: 'Cómo funciona el parte por plantilla',
        subtitle: 'Déjala abierta en esta pestaña mientras montas la tuya',
        lead: 'Tú escribes las PREGUNTAS que quieres que se contesten al cerrar un servicio. El asistente se las hace al trabajador una a una, en el orden que tú pusiste, y no le deja cerrar el parte hasta tenerlas todas.',
        steps: {
          clone: {
            title: 'Empieza por un ejemplo',
            body: 'En «Plantillas de parte» tienes el desplegable «Clonar un ejemplo». El primero, «Parte completo», ya trae las seis preguntas que hace casi cualquier empresa de campo: qué tareas hiciste, dónde, con qué trabajadores, si hubo alguna incidencia, qué material gastasteis y para qué cliente. Se clona apagada, así que puedes trastearla sin que aparezca en el móvil de nadie.',
            link: 'Ir a las plantillas',
          },
          write: {
            title: 'Escribe cada pregunta',
            body: 'Cada pregunta se define con cuatro cosas, y solo cuatro:',
            b1: 'QUÉ SE PREGUNTA — el nombre corto, como «Dónde ha sido».',
            b2: 'QUÉ CONTESTAN — de una lista cerrada de ocho. Es lo que permite que el asistente entienda «uno y medio» o rechace «dieciocho mil», y lo que hace que esto sea un parte y no una nota de voz.',
            b3: 'CUÁNTO INSISTE — obligatorio (no cierra sin ello), recomendado (lo pregunta una vez aunque nadie lo mencione, y se puede dejar en blanco) u opcional (solo si sale en la conversación).',
            b4: 'CÓMO LO PREGUNTA EL ASISTENTE — la frase con tus palabras. Eso es literalmente lo que dirá.',
          },
          preview: {
            title: 'Mira la conversación antes de publicar',
            body: 'A la derecha, según escribes, sale la conversación exacta que va a tener el trabajador: qué le preguntará, en qué orden, qué no le preguntará porque el sistema ya lo sabe y qué pasará al confirmar. La calcula el mismo motor que después la conduce, así que lo que ves ahí es lo que va a ocurrir.',
          },
          publish: {
            title: 'Publícala',
            body: 'Hasta que no le das a «Publicar» no sale en ningún móvil. Y «Retirar» la quita sin borrarla: los partes que ya se escribieron con ella la nombran, y borrarla dejaría un histórico ilegible.',
          },
          fill: {
            title: 'Y el trabajador la rellena hablando',
            body: 'Le sale en la orden y en cada parada de «Mi día», al lado del formulario de siempre. Y no hay que pulsar nada: la pantalla se abre llamando. Tres cosas que conviene saber:',
            b1: 'No le pregunta lo que ya se sabe: las horas salen de su fichaje y el cliente de la orden.',
            b2: 'Puede contestar hablando o tocando, siempre las dos. Si hay ruido o no hay micrófono, escribe. El emplazamiento o la obra también se dicen: si hay dos que se llaman parecido, pregunta cuál.',
            b3: 'Antes de cerrar se lo lee todo para que lo confirme, y sin cobertura la conversación no se para: se guarda en el móvil y se manda al volver la línea.',
          },
        },
        typesTitle: 'Los ocho tipos de respuesta',
        typesBody: 'La lista es cerrada a propósito. El asistente solo puede conducir la conversación porque sabe qué es cada respuesta; con texto libre no habría nada que comprobar ni nada que descontar del almacén.',
        types: {
          number_unit: 'Un número con su unidad y su rango: horas, kilos, sacos.',
          boolean: 'Sí o no.',
          choice: 'Una de las opciones que tú escribas.',
          catalogue: 'Uno de la lista de otro módulo: un emplazamiento, un trabajador, un material, un cliente. Es lo que deja que el sitio quede enlazado y que el material salga de la estantería.',
          photo: 'Una o varias, con la opción de antes y después.',
          signature: 'La firma del cliente.',
          text: 'Una nota corta. El único texto libre.',
          datetime: 'Una fecha o una hora.',
        },
        offTitle: 'Cómo quitar estos tutoriales',
        offBody: 'Los «?» de las pantallas salen porque tienes el modo tutoriales encendido en tu perfil. Es tuyo y no de la empresa: apagarlo no se lo quita a nadie más.',
        offLink: 'Ir a mi perfil',
      },
            worksheets: {
        title: 'Plantillas de parte',
        subtitle: 'Qué se apunta al cerrar un servicio, y en qué orden se pregunta',
        listTitle: 'Las plantillas de esta empresa',
        table: { name: 'Plantilla', sector: 'Sector', fields: 'Preguntas', status: 'Estado' },
        empty: 'Todavía no hay ninguna plantilla. Clona una de ejemplo y edítala.',
        emptyHint: 'Una plantilla decide qué le pregunta el asistente al trabajador cuando cierra un servicio.',
        newTitle: 'Nueva plantilla de parte',
        editTitle: 'Editar la plantilla',
        fieldsCount: 'Ninguna pregunta | 1 pregunta | {n} preguntas',
        active: 'Publicada',
        inactive: 'Retirada',
        activeHint: 'Solo las publicadas salen en el móvil.',
        actions: {
          new: 'Nueva plantilla',
          clone: 'Clonar un ejemplo',
          duplicate: 'Duplicar',
          publish: 'Publicar',
          retire: 'Retirar',
          preview: 'Ver la conversación',
          addField: 'Añadir pregunta',
          save: 'Guardar la plantilla',
        },
        form: {
          name: 'Nombre',
          namePlaceholder: 'Parte de mantenimiento',
          description: 'Para qué es',
          descriptionPlaceholder: 'Cuándo se usa esta plantilla',
          sector: 'Sector',
          sectorPlaceholder: 'jardinería, mantenimiento…',
          kinds: 'Solo para estos tipos de trabajo',
          kindsHelp: 'Vacío es «para todos». Se comprueba contra la lista de tipos de trabajo de Ajustes.',
        kindsAdd: 'Añade un tipo de trabajo…',
          roles: 'Solo para estos roles',
          rolesHelp: 'Vacío es «para todo el mundo».',
          fields: 'Las preguntas, en el orden en que se hacen',
          fieldsHelp: 'El asistente las hace de arriba abajo, y solo las que falten: lo que ya sepa o ya le hayan contado no lo vuelve a preguntar.',
          nameRequired: 'La plantilla necesita un nombre.',
          fieldsRequired: 'Una plantilla sin preguntas no pregunta nada.',
          saved: 'Plantilla guardada',
          duplicateName: 'Nombre de la copia',
        },
        field: {
          label: 'Qué se pregunta',
          labelPlaceholder: 'Sacos de abono',
          type: 'Qué contestan',
          requirement: 'Cuánto insiste',
          source: 'De dónde sale',
          autoSource: 'Quién lo rellena',
          unit: 'Unidad',
          unitPlaceholder: 'h, kg, sacos',
          options: 'Opciones',
          optionsHelp: 'Una por línea. Hacen falta al menos dos.',
          catalogue: 'Catálogo',
          catalogueHelp: 'De qué lista de otro módulo se elige.',
          min: 'Mínimo',
          max: 'Máximo',
          maxLength: 'Largo máximo',
          multiple: 'Admite varias',
          beforeAfter: 'Antes y después',
          hint: 'Cómo lo pregunta el asistente',
          hintPlaceholder: '¿Cuántos sacos de abono has gastado?',
          hintHelp: 'Con tus palabras. Si lo dejas en blanco, se pregunta con la etiqueta.',
          binds: 'Se guarda en',
          bindsHelp: 'Atarlo a una casilla del parte de siempre hace que el resto del ERP lo vea.',
          postAction: 'Al cerrar el parte',
          up: 'Subir',
          down: 'Bajar',
          remove: 'Quitar la pregunta',
        },
        types: {
          number_unit: 'Número con unidad',
          boolean: 'Sí o no',
          choice: 'Lista de opciones',
          catalogue: 'Del catálogo de otro módulo',
          photo: 'Fotos o vídeo',
          signature: 'Firma',
          text: 'Nota corta',
          datetime: 'Fecha y hora',
        },
        requirements: {
          required: 'Obligatorio',
          recommended: 'Recomendado',
          optional: 'Opcional',
        },
        requirementHelp: {
          required: 'El parte no se puede cerrar sin él.',
          recommended: 'El asistente lo pregunta una vez aunque nadie lo mencione, y se puede dejar en blanco.',
          optional: 'Solo se rellena si el trabajador lo menciona.',
        },
        sources: {
          manual: 'Lo dice el trabajador',
          auto: 'Lo rellena el sistema solo',
        },
        autoSources: {
          worker: 'Quién lo hizo',
          now: 'La fecha y la hora',
          service: 'El servicio del día',
          customer: 'El cliente',
          clocked_hours: 'Las horas del fichaje',
          location: 'Dónde fue',
        },
        bindings: {
          none: 'Solo en la plantilla',
          hours: 'Las horas del parte',
          description: 'Qué se ha hecho',
          workDate: 'El día del trabajo',
          materials: 'El material gastado',
          photos: 'Las fotos',
          targetDone: 'Sitio acabado',
        },
        postActions: {
          none: 'Nada',
          consume_stock: 'Descontar del almacén',
          raise_incident: 'Levantar una incidencia',
          complete_service: 'Dar el servicio por hecho',
        },
        preview: {
          title: 'La conversación que producirá',
          help: 'Calculada por el mismo motor que la conduce: esto es lo que le va a pasar al trabajador.',
          prefilled: 'No se pregunta, ya se sabe',
          asks: 'Lo que el asistente preguntará, en este orden',
          onlyIfMentioned: 'Solo si lo menciona',
          afterwards: 'Al confirmar',
          blocking: 'Sin esto no se puede cerrar',
          nothing: 'Nada',
          opening: 'Cuéntame qué has hecho.',
          closing: 'Te lo leo para que lo confirmes.',
        },
        seeds: {
          complete: {
            name: 'Parte completo',
            description: 'Qué se hizo, dónde, con quién, qué pasó y qué se gastó.',
            fields: {
              hours: {
                label: 'Horas',
                hint: '¿Cuánto has estado?',
              },
              tasks: {
                label: 'Qué tareas has hecho',
                hint: 'Cuéntame qué has hecho hoy.',
              },
              where: {
                label: 'Dónde ha sido',
                hint: '¿Dónde has hecho estas tareas?',
              },
              crew: {
                label: 'Con qué trabajadores',
                hint: '¿Quién ha ido contigo?',
              },
              issues: {
                label: 'Incidencias',
                hint: '¿Ha habido alguna incidencia o algún problema?',
              },
              material: {
                label: 'Material gastado',
                hint: '¿Habéis gastado algún material?',
              },
              customer: {
                label: 'Para qué cliente',
                hint: '¿A qué cliente se le asigna?',
              },
              photo: {
                label: 'Fotos',
                hint: '¿Me haces una foto de cómo ha quedado?',
              },
            },
          },
          gardening: {
            name: 'Parte de jardinería',
            description: 'Una ronda de mantenimiento de jardines.',
            fields: {
              hours: {
                label: 'Horas',
                hint: '¿Cuánto has estado?',
              },
              done: {
                label: 'Qué se ha hecho',
                hint: 'Cuéntame qué has hecho en el jardín.',
              },
              mown: {
                label: 'Se ha segado',
                hint: '¿Has segado?',
              },
              fertiliser: {
                label: 'Sacos de abono',
                hint: '¿Cuántos sacos de abono has gastado?',
              },
              material: {
                label: 'Material gastado',
                hint: '¿Has gastado algo del almacén?',
              },
              photo: {
                label: 'Fotos',
                hint: 'Hazme una foto de cómo ha quedado.',
              },
              issues: {
                label: 'Incidencias',
                hint: '¿Alguna incidencia? Algo roto, algo que haya que volver a mirar.',
              },
            },
          },
          maintenance: {
            name: 'Parte de mantenimiento',
            description: 'Una intervención sobre una máquina o una instalación.',
            fields: {
              hours: {
                label: 'Horas',
                hint: '¿Cuánto has estado?',
              },
              kind: {
                label: 'Tipo de intervención',
                hint: '¿Ha sido preventivo, correctivo o una revisión?',
                options: 'Preventivo · Correctivo · Revisión',
              },
              done: {
                label: 'Qué se ha hecho',
                hint: 'Cuéntame qué has hecho en la máquina.',
              },
              stopped: {
                label: 'La máquina ha estado parada',
                hint: '¿Ha estado parada?',
              },
              parts: {
                label: 'Recambios',
                hint: '¿Qué recambios has puesto?',
              },
              reading: {
                label: 'Lectura de presión',
                hint: '¿Qué presión marca?',
              },
              issues: {
                label: 'Incidencias',
                hint: '¿Alguna incidencia que haya que apuntar?',
              },
            },
          },
          electrician: {
            name: 'Parte de electricista',
            description: 'Una intervención eléctrica con firma del cliente.',
            fields: {
              hours: {
                label: 'Horas',
                hint: '¿Cuánto has estado?',
              },
              done: {
                label: 'Qué se ha hecho',
                hint: 'Cuéntame qué has hecho.',
              },
              measured: {
                label: 'Tensión medida',
                hint: '¿Qué tensión has medido?',
              },
              earthing: {
                label: 'Toma de tierra comprobada',
                hint: '¿Has comprobado la toma de tierra?',
              },
              material: {
                label: 'Material gastado',
                hint: '¿Qué material has puesto?',
              },
              panel: {
                label: 'Foto del cuadro',
                hint: 'Hazme una foto del cuadro.',
              },
              signature: {
                label: 'Firma del cliente',
                hint: 'Que te firme el cliente.',
              },
              issues: {
                label: 'Incidencias',
                hint: '¿Alguna incidencia?',
              },
            },
          },
        },
      },
      detailTitle: 'Orden de trabajo',
      newTitle: 'Nueva orden de trabajo',
      editTitle: 'Editar la orden',
      actions: {
        new: 'Nueva orden', edit: 'Editar', addReport: 'Apuntar parte',
        guidedReport: 'Apuntar hablando',
        complete: 'Completar', reopen: 'Reabrir', cancel: 'Cancelar',
        save: 'Guardar', remove: 'Eliminar',
      },
      status: {
        draft: 'Borrador', planned: 'Planificada', in_progress: 'En curso',
        on_hold: 'Parada', completed: 'Completada', cancelled: 'Anulada',
      },
      priority: { low: 'Baja', medium: 'Normal', high: 'Alta', critical: 'Crítica' },
      severity: { low: 'Leve', medium: 'Media', high: 'Grave', critical: 'Crítica' },
      day: { today: 'Hoy', yesterday: 'Ayer', tomorrow: 'Mañana' },
      tabs: {
        overview: 'Resumen', reports: 'Partes', incidents: 'Incidencias de la orden', history: 'Historial',
      },
      // ── Escribir la orden con ayuda ───────────────────────────────
      ai: {
        write: 'Redactar',
        writeHint: 'Escribe el cuerpo de la orden a partir de lo que haya puesto',
        summarise: 'Resumir en el título',
        summariseHint: 'Saca una línea de la descripción y la pone de título',
        task: 'Escribir la tarea',
        taskHint: 'Escribe una tarea a partir de lo que haya en el campo',
        working: 'Escribiendo…',
      },
      // ── Sobre qué va la orden, y su planificador ──────────────────
      // La tarjeta que se confirma desde el asistente (create_order) y que
      // se ve durante la llamada mientras la orden se va armando.
      assistant: {
        // Las respuestas de las consultas que contestan las reglas (queries.js).
        query: {
          agenda: {
            self: 'Tienes {count} órdenes el {when}: {list}.',
            selfNone: 'No tienes ninguna orden el {when}.',
            other: '{who} tiene {count} órdenes el {when}: {list}.',
            otherNone: '{who} no tiene ninguna orden el {when}.',
          },
          late: {
            some: 'Hay {count} órdenes con retraso: {list}.',
            none: 'No hay ninguna orden con retraso.',
          },
        },
        order: {
          action: 'Abrir una orden de trabajo',
          what: 'Qué',
          when: 'Cuándo',
          draft: 'Borrador, sin día',
          customer: 'Cliente',
          who: 'Quién va',
          noTasks: 'Sin tareas',
          more: '{count} sitios más',
          outcome: 'Orden {code} creada',
          fromPlaces: 'Lo toma de sus sitios',
          nobody: 'Nadie todavía',
          noDateNote: 'Sin día: se guardará como borrador y no le llegará a nadie',
          noCrewNote: 'Todavía no va nadie',
          noPlaceNote: 'No nombra ningún sitio',
          noTasksNote: 'Sin tareas en: {places}',
          severalPlaces: '«{name}» coincide con varios: {options}. Elígelo diciéndolo o escribiéndolo.',
          unknownPlace: '«{name}» no está entre los sitios: irá tal como se ha escrito',
          unknownPerson: 'No encuentro a «{name}» entre las personas',
          placeN: 'Sitio {n}',
          tasks: 'Tareas',
          noPlace: 'Sin sitio todavía',
          where: 'Dónde',
          priority: 'Prioridad',
          notes: 'Notas',
          noNotes: 'Nada',
          dayDoubtNote: 'El día no quedó claro: elígelo',
          from: 'Sale de',
          sourceUnreadable: 'No se puede leer ese plan o esa incidencia: la orden no puede apuntar a ello',
          unknownCustomer: '«{name}» no está entre los clientes',
          timeNote: 'Hora: {time}',
          readPlace: 'Orden en {place}',
          readDay: '{day}',
          readDayTime: '{day}, a las {time}',
          readTasks: 'Hay que {tasks}',
          readCrew: 'Van {who}',
          readOne: 'Va {who}',
          asSaid: '«{said}», tal cual',
          internal: 'Interna: sin sitio ni cliente',
        },
        // Las tarjetas de cambiar una orden que ya existe (assistantChanges.js).
        change: {
          order: 'Orden',
          noOrder: '¿Qué orden?',
          kind: 'Orden de trabajo',
          unknownOrder: 'Ninguna orden abierta coincide con «{said}»',
          severalOrders: '«{said}» coincide con varias órdenes: elige una',
          until: 'Hasta ahora',
          closedOrder: 'Esta orden ya está cerrada',
          summary: {
            reschedule: 'Pasar {order} al {day} {time}',
            assign: '{order}: va {who}',
            priority: {
              low: '{order}: prioridad baja', medium: '{order}: prioridad media', high: '{order}: prioridad alta', critical: '{order}: urgente', other: '{order}: cambiar la prioridad',
            },
            status: {
              draft: '{order}: devolver a borrador', planned: '{order}: planificar', in_progress: '{order}: empezar', on_hold: '{order}: pausar',
              completed: '{order}: completar', cancelled: '{order}: anular', other: '{order}: cambiar el estado',
            },
          },
          rescheduleAction: 'Pasar una orden a otro día',
          newDay: 'Día nuevo',
          noDay: 'Elige el día',
          pickDay: 'Elige el día nuevo',
          time: 'Hora',
          noTime: 'Sin hora',
          timeKept: '{time} (la de antes)',
          badTime: 'La hora se escribe HH:MM',
          noDayBefore: 'Sin día',
          draftPlanned: 'Es un borrador: con día pasa a planificada y le llega a la cuadrilla',
          readMove: '{code}, al {day}',
          readMoveTime: '{code}, al {day} a las {time}',
          rescheduled: '{code} pasada al {day}',
          assignAction: 'Cambiar quién va',
          who: 'Quién va',
          nobody: 'Nadie',
          team: 'Equipo',
          unknownPerson: 'No encuentro a «{name}» entre las personas',
          unknownTeam: 'No hay ningún equipo que se llame «{name}»',
          nobodyNote: 'No iría nadie: elige quién va',
          readAssign: '{code}: va {who}',
          assigned: '{code}: va {who}',
          priorityAction: 'Cambiar la prioridad',
          priority: 'Prioridad',
          noPriority: 'Elige una',
          readPriority: {
            low: '{code}, prioridad baja', medium: '{code}, prioridad media', high: '{code}, prioridad alta', critical: '{code}, urgente',
          },
          prioritised: {
            low: '{code}: prioridad baja', medium: '{code}: prioridad media', high: '{code}: prioridad alta', critical: '{code}: urgente', other: '{code}: prioridad cambiada',
          },
          statusAction: 'Cambiar el estado de una orden',
          status: 'Estado',
          noStatus: 'Elige uno',
          reason: 'Por qué',
          noReason: 'Sin decir',
          badTransition: 'Desde el estado en que está, la orden no puede pasar a ese',
          needsManage: 'Completar o anular necesita el permiso de gestionar órdenes',
          needsReason: 'Di por qué se anula',
          completeNote: 'Al completarla, sus horas pasan a quien las factura',
          readStatus: {
            draft: '{code}, a borrador', planned: '{code}, planificada', in_progress: 'Empezar {code}', on_hold: 'Pausar {code}', completed: 'Completar {code}', cancelled: 'Anular {code}',
          },
          outcomeStatus: {
            draft: '{code} devuelta a borrador', planned: '{code} planificada', in_progress: '{code} empezada', on_hold: '{code} en pausa',
            completed: '{code} completada', cancelled: '{code} anulada', other: '{code}: estado cambiado',
          },
        },
      },
      // Lo que la llamada pregunta mientras falta algo de una orden.
      voice: {
        askPlace: '¿Dónde es?',
        askReportOrder: '¿De qué orden es el parte?',
        askReportHours: '¿Cuántas horas?',
        askPlaceAgain: 'No encuentro «{said}». ¿Cómo se llama el sitio?',
        whichPlace: '«{said}» puede ser {options} o {last}. ¿Cuál?',
        thisPlace: 'No encuentro «{said}» tal cual. ¿Te refieres a {last}?',
        askTasks: '¿Qué hay que hacer?',
        askDay: '¿Para qué día?',
        askPeople: '¿Quién va?',
        askPeopleAgain: 'No encuentro a «{name}». ¿Quién va?',
        askOrder: '¿Qué orden?',
        askOrderAgain: 'No encuentro la orden «{said}». ¿Cuál es?',
        whichOrder: '«{said}» puede ser {options} o {last}. ¿Cuál?',
        thisOrder: '¿Te refieres a {last}?',
        askNewDay: '¿A qué día la paso?',
        askTeamAgain: 'No encuentro el equipo «{name}». ¿Quién va?',
        askPriority: '¿Qué prioridad: baja, media, alta o urgente?',
        askStatus: '¿La empiezo, la pauso, la completo o la anulo?',
        askCancelReason: '¿Por qué se anula?',
        whichTask: '«{said}» no está en vuestras tareas. ¿Es {options} o {last}? Si no, di «déjalo así».',
        thisTask: '«{said}» no está en vuestras tareas. ¿Es {last}? Si no, di «déjalo así».',
      },
      targets: {
        tab: 'Objetivos',
        title: 'Sobre qué',
        what: 'Qué es',
        freeText: 'Escribirlo a mano',
        name: 'Nombre',
        namePlaceholder: 'Prensa 3, Línea 2, chalet de los Puig…',
        search: 'Buscar',
        searchPlaceholder: 'Escribe para buscar…',
        wherePlaceholder: 'Dónde: nave, calle, planta…',
        add: 'Añadir',
        remove: 'Quitarlo de la orden',
        notes: 'Notas',
        emptyHint: 'Una orden sin nada aquí es un trabajo suelto, y también vale.',
        chosen: 'elegidos',
        pickManySearch: 'Acota escribiendo: Puigcerdà, Alp, nave 2…',
        pickManyLoading: 'Buscando…',
        pickManyNone: 'No sale nada. Prueba con otra palabra.',
        pickManyAll: 'Marcar los {count} que salen',
        group: 'Carpeta',
        groupAll: 'Todas',
        pickGroupAll: 'Añadir los {count} de «{group}»',
        pickManyAlready: '{count} ya están en la orden',
        pendingHere: 'Lo que había pendiente aquí',
        pendingHint: 'Apuntado otro día en este sitio. Lo que marques entra en la orden, '
          + 'y al completarla se cierra donde estaba.',
        pendingLoading: 'Mirando qué hay pendiente…',
        unit: 'Objetivo',
        units: 'objetivos',
        tasks: 'tareas',
        count: '{count} objetivos',
        progress: '{done}/{total} objetivos',
        taskProgress: '{done}/{total} tareas',
        column: 'Objetivos',
        done: 'Hecho',
        pending: 'Pendiente',
        markDone: 'Marcarlo como hecho',
        markPending: 'Volver a dejarlo pendiente',
        doneBy: 'por {who}',
        noTasks: 'Sin tareas apuntadas aquí',
      },
      planner: {
        removeTask: 'Quitar «{task}»',
        stepWho: 'Cuándo y sobre qué',
        stepWork: 'El trabajo',
        stepDetail: 'El detalle',
        stepCustomer: 'Para quién',
        stepWhen: 'Cuándo',
        stepCrew: 'Quién va',
        stepTargets: 'Sobre qué',
        stepPlaces: 'Los lugares',
        stepTasks: 'Las tareas',
        stepSteps: 'Los pasos',
        back: 'Atrás',
        // Qué se contesta en cada paso. Solo se ve en un móvil, donde una
        // pantalla puede tener un campo y nada más.
        lead: {
          dictate: 'Cuéntalo en voz alta y el resto se rellena solo. Puedes saltártelo.',
          when: '¿Qué día va la cuadrilla? Si aún no lo sabes, déjalo en blanco.',
          targets: '¿A dónde hay que ir? Uno, o los cincuenta de una ronda.',
          tasks: '¿Qué hay que hacer en cada sitio? Lo que se repita, ponlo en todos de una vez.',
          work: 'En una línea: qué trabajo es.',
          detail: 'Lo que la cuadrilla necesita saber al llegar.',
          customer: 'Para quién es y a qué obra va, si va a alguna.',
          crew: 'Quién va. Con un equipo, se copia su gente.',
          steps: 'Los pasos que se marcan al hacerlos. Solo al abrirla.',
        },
        create: 'Crear la orden',
        next: 'Continuar',
        sectionWhen: 'Cuándo',
        sectionWho: 'Quién va',
        sectionOrder: 'La orden de trabajo',
        sectionNotes: 'Notas para quien va',
        descriptionHelp: 'Lo que hay que hacer, con el detalle que haga falta. Es lo que se lee en la obra.',
        descriptionPlaceholder: 'Qué pasa, qué hay que hacer, qué hay que tener en cuenta…',
        notesHelp: 'Lo de fuera del trabajo: accesos, llaves, horarios, con quién hablar.',
        notesPlaceholder: 'Llamar antes de ir, la llave está en portería…',
        inTwoDays: 'Pasado',
        nextMonday: 'Lun. próx.',
        crewAdd: 'Añadir a alguien más…',
        crewNone: 'Va solo quien tenga la orden asignada',
        moveUp: 'Subir',
        moveDown: 'Bajar',
        viewTargets: 'Uno a uno',
        viewTasks: 'Por tarea',
        viewSummary: 'Resumen',
        bulkTitle: 'Una tarea en todos',
        bulkApply: 'Añadir a los {count}',
        taskTypeAny: 'Tipo de tarea…',
        taskTextPlaceholder: 'Qué hay que hacer…',
        addTask: 'Añadir tarea',
        noTasks: 'Sin tareas todavía',
        noTasksYet: 'Todavía no hay ninguna tarea apuntada',
        atTargets: 'en {count}',
        titleSuggested: 'Si lo dejas vacío: «{title}»',
        titleMore: '{names} y {count} más',
        bringPending: 'Traer todo lo pendiente',
        bringPendingHint: 'Lo apuntado otro día en cada sitio entra en la orden. Lo que sobre se quita sitio a sitio.',
        bringingPending: 'Mirando qué hay pendiente en {count} sitios…',
        broughtPending: '{count} pendientes traídas a la orden',
        noPending: 'No había nada pendiente en estos sitios',
        addTasksHere: 'Añadir tareas',
        noTaskTypes: 'Aún no hay tipos de tarea: cada tarea se escribe a mano. Si repites siempre las mismas (corte de césped, poda, riego…), escríbelas una vez en Ajustes y saldrán aquí en un desplegable.',
        noTaskTypesLink: 'Escribirlas en Ajustes (W.2)',
      },
      equipment: {
        title: 'Qué llevar',
        tools: 'Herramientas',
        materials: 'Material',
        forTasks: 'para {types}',
        hint: 'Sale de los tipos de tarea de la ronda (Ajustes, W.15).',
      },
      myDay: {
        title: 'Mi día',
        subtitle: 'Lo que tienes hoy, y lo que llevas hecho',
        today: 'Hoy',
        previous: 'Día anterior',
        next: 'Día siguiente',
        targets: '{count} objetivos',
        late: '{count} con retraso',
        incidents: '{count} con incidencia',
        empty: 'Hoy no tienes nada asignado',
        emptyHint: 'Si esperabas trabajo, habla con quien planifica: puede estar sin fecha todavía.',
        failed: 'No se ha podido cargar tu día',
        failedHint: 'Sin cobertura, o el servidor no contesta. No se ha perdido nada: vuelve a intentarlo en un momento.',
        retry: 'Reintentar',
      },
      // Dictar la orden. Rellena el formulario; no lo guarda.
      dictate: {
        step: 'Dictarla',
        title: 'Dictar la orden',
        subtitle: 'Cuéntalo y se rellena solo. Lo revisas antes de guardar: aquí no se guarda nada.',
        clip: 'La grabación',
        read: 'Leerlo',
        heard: 'Lo que se oyó',
        silence: 'No se oyó nada. Vuelve a grabar más cerca del micrófono.',
        which: '¿Cuál de estos es «{words}»?',
        notFound: 'No encontré nada que se llame «{words}». Ponlo a mano si hace falta.',
      },
      board: { title: 'Abiertas por tipo de trabajo' },
      filters: {
        oneCustomer: 'Solo de un cliente',

        search: 'Buscar', searchPlaceholder: 'Número, concepto, cliente, lugar…',
        status: 'Estado', allStatuses: 'Todos', date: 'Día',
        team: 'Equipo', allTeams: 'Todos',
        onlyOpen: 'Solo abiertas', onlyLate: 'Solo con retraso', onlyUnplanned: 'Solo faenas',
      },
      table: {
        code: 'Número', title: 'Trabajo', planned: 'Prevista', assignee: 'Asignada a',
        progress: 'Pasos', hours: 'Horas', status: 'Estado', crewMore: 'más',
      },
      kpi: {
        today: 'Para hoy', open: 'Abiertas', late: 'Con retraso', hours: 'Horas completadas',
      },
      empty: {
        none: 'Todavía no hay órdenes de trabajo',
        noneHint: 'Abre la primera, o acepta un presupuesto y deja que se abra sola.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
      },
      lateBy: '{days} días de retraso',
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      file: { title: 'Ficha' },
      fields: {
        code: 'Número', title: 'Concepto', kind: 'Tipo de trabajo', priority: 'Prioridad',
        customer: 'Cliente', project: 'Proyecto', location: 'Lugar', description: 'Descripción',
        plannedDate: 'Fecha prevista', assignee: 'Asignada a', crew: 'Quién más va',
        team: 'Equipo',
        quote: 'Presupuesto', hours: 'Horas apuntadas',
        source: { incident: 'Sale de la incidencia', maintenance: 'Sale del mantenimiento' },
      },
      form: {
        what: 'El trabajo', who: 'Quién y cuándo',
        titlePlaceholder: 'Cambiar la bomba del circuito 2…',
        customerPlaceholder: 'Empresa o particular',
        projectPlaceholder: 'A qué proyecto pertenece',
        projectHelp: 'Si lo indicas, las horas y el material de esta orden irán a su libro de costes.',
        locationPlaceholder: 'Dónde hay que ir',
        plannedHelp: 'Al poner fecha, la orden pasa a planificada y aparece en el día de quien vaya.',
        nobody: 'Sin asignar',
        crewHelp: 'Se eligen de uno en uno, de la lista de personas. Todos verán la orden en su día.',
        noTeam: 'Sin equipo',
        teamHelp: 'Elegir uno copia a su gente aquí abajo, y a su responsable como asignado si no hay '
          + 'nadie. Copia: quien vaya se queda apuntado aunque el equipo cambie después.',
        teamMembers: '{count} personas',
        noPeople: 'No hay personas activas para asignar.',
        checklistTitle: 'Pasos a seguir',
        checklistHelp: 'Solo al crear: después se editan en la ficha, donde cada paso guarda quién lo marcó.',
        titleRequired: 'La orden necesita un concepto',
      },
      checklist: {
        title: 'Pasos',
        empty: 'Sin pasos',
        emptyHint: 'Añade los que se repiten en este tipo de trabajo.',
        add: 'Añadir paso',
        addPlaceholder: 'Comprobar presión del circuito…',
      },
      // ── La faena: trabajo que nadie planificó ───────────────────────
      log: {
        unlinked: 'La faena está guardada, pero sin enlazar a {names}: tu cuenta no puede ver esas fichas. Avisa a la oficina para que la enlace.',
        action: 'Apuntar faena',
        title: 'Apuntar una faena',
        hint: 'Trabajo que has hecho sin que hubiera una orden. Se abre su propia orden, ya cerrada, a tu nombre.',
        where: 'Dónde ha sido',
        whereHelp: 'Un emplazamiento, una máquina, un vehículo… lo que haya en esta instancia.',
        whereRequired: 'Di dónde ha sido, o qué has hecho',
        freeText: 'Escribirlo a mano',
        search: 'Cuál',
        searchPlaceholder: 'Empieza a escribir…',
        name: 'Qué o dónde',
        namePlaceholder: 'Jardín de los Pinos, Prensa 3…',
        kind: 'Tipo de trabajo',
        hours: 'Cuánto has tardado',
        workDate: 'Qué día',
        workDateHelp: 'El día que trabajaste, no el de hoy. El sábado se puede apuntar el lunes.',
        description: 'Qué has hecho',
        descriptionPlaceholder: 'Podados los setos de la entrada y repasado el riego…',
        customerHelp: 'Esta instancia pide cliente en todas las órdenes.',
        tasks: 'Tareas hechas', addTask: 'Añadir tarea',
        addStop: 'Añadir otro emplazamiento',
        quick: {
          where: '¿Dónde has estado?',
          whereHelp: 'Toca los sitios. Los de hoy salen primero.',
          today: 'hoy',
          noSuggestions: 'No hay sitios recientes: añade uno.',
          otherSite: 'Otro sitio',
          pickFirst: 'Toca dónde has estado',
          hours: '¿Cuánto rato?',
          otherHours: 'Otra',
          what: '¿Qué has hecho?',
          photos: 'Fotos',
          issue: 'Algo roto',
          issueLabel: 'Qué pasa',
          issuePlaceholder: 'El riego del sector 2 pierde agua…',
          tell: 'Contarlo',
          more: 'Más detalles',
          less: 'Menos detalles',
          day: 'El día',
          todayPill: 'Hoy',
          yesterday: 'Ayer',
          otherDay: 'Otro día',
          with: '¿Con quién has ido?',
          alone: 'Solo',
          dayMore: 'Tipo, proyecto y cliente',
          clocked: 'de {hours} fichadas',
          send: 'Enviar',
          issueSaved: 'Incidencia apuntada en {name}',
          issueFailed: 'No se pudo apuntar la incidencia de {name}',
        },
        dropStop: 'Quitar este emplazamiento',
        stopBlank: 'Sitio sin nombre',
        whereType: 'Tipo de sitio',
        what: 'La faena',
        noTasks: 'Nada apuntado todavía.',
        noMaterials: 'No se gastó nada.',
        pendingTitle: 'Lo que había pendiente aquí',
        pendingHint: 'Marca lo que hayas hecho: entra en la faena y se da por hecho allí donde estuviera apuntado.',
        pendingFailed: 'La faena está guardada, pero «{name}» no se pudo cerrar y sigue pendiente.',
        leaveTitle: '¿Descartar la faena?',
        leaveMessage: 'Lo que has escrito no se ha guardado en ninguna parte.',
        leaveConfirm: 'Descartarla',
        leaveCancel: 'Seguir apuntando',
        taskPlaceholder: 'Poda del seto, cambio del aspersor…',
        dictate: {
          title: 'Cuéntalo y se rellena solo',
          subtitle: 'Di lo que has hecho, dónde, cuánto has estado y con qué. Lo revisas antes de guardar.',
          clip: 'Nota de voz del parte',
          read: 'Rellenar con lo que he dicho',
          heard: 'Lo que se ha oído',
          silence: 'No se oyó nada. Vuelve a grabar.',
          which: '¿Cuál de estos es «{words}»?',
          notFound: '«{words}» no lo he encontrado. Elígelo tú abajo.',
          noHours: 'No has dicho cuánto tiempo has estado. Ponlo tú: no me lo invento.',
          keeps: 'No se ha guardado nada todavía. Repasa el formulario y dale a apuntar.',
          clipFailed: 'La faena está apuntada, pero la nota de voz no se pudo colgar.',
        },
        time15: '+15 min',
        time30: '+30 min',
        time60: '+1 h',
        timeClear: 'A cero',
        crew: 'Quién iba contigo',
        crewHelp: 'Se apuntan sus horas en este parte. No le asigna trabajo a nadie.',
        crewAdd: 'Añadir a alguien',
        crewPlaceholder: 'Elige un compañero…',
        crewHours: 'Horas de {who}',
        crewNobody: 'No hay a quién añadir.',
        short: 'Apuntar',
        save: 'Apuntar faena',
        saved: 'Faena apuntada como {code}',
        savedOpen: 'Faena apuntada como {code}. Queda abierta: la oficina tiene que cerrarla.',
      },
      // Lo que la cuadrilla añadió y no estaba en el plan.
      unplanned: {
        badge: 'No planificado',
        title: 'Añadido en obra por {name}',
        origin: 'Faena',
        originHelp: 'Nadie la planificó: la apuntó quien la hizo.',
        addTaskPlaceholder: 'Lo que había que hacer y no estaba en la lista…',
      },
      // Lo que vino de otro sistema (POST /import, 0.22.0).
      imported: {
        badge: 'Importada',
        help: 'Esta orden se trajo de {system}: sus fechas, quién marcó cada tarea y sus partes son los de allí.',
        from: 'Importada de',
        createdThere: 'creada allí el {date}',
      },
      report: {
        breakdown: {
          toggle: 'Hubo avería',
          hint: 'Apunta la hora a la que paró. Es lo que nadie recuerda una semana después, y es de lo que salen el MTBF y el MTTR.',
          which: 'Qué paró',
          stoppedAt: 'Paró a las',
          stoppedAtHint: 'La hora de verdad, no la del parte',
          restartedAt: 'Volvió a las',
          restartedAtHint: 'Vacío = sigue parada',
          didStop: 'Paró producción',
          what: 'Qué pasó',
          whatHint: 'Con tus palabras. La causa y el tipo los pone mantenimiento después, contra su catálogo',
          whatPlaceholder: 'Reventó el latiguillo del cilindro de cierre…',
        },
        title: 'Parte de {code}',
        hours: 'Horas', hoursPlaceholder: 'p. ej. 6,5',
        hoursRequired: 'El parte necesita las horas trabajadas',
        workDate: 'Día del trabajo',
        workDateHelp: 'El día en que se trabajó, no el de hoy.',
        description: 'Qué se ha hecho',
        descriptionPlaceholder: 'Sustituida la bomba y purgado el circuito…',
        materials: 'Material usado', addLine: 'Añadir línea',
        measured: 'Medido hoy', addMeasure: 'Añadir medición',
        itemPlaceholder: 'Buscar la partida',
        measuredHint: 'Lo que se ha hecho HOY, no el total de la obra. La partida lo acumula sola.',
        noMaterials: 'Sin material. Añade líneas si habéis gastado algo.',
        materialPlaceholder: 'Cable, tubo, pieza…',
        qty: 'Cant.', unit: 'Ud.', price: 'Precio',
        media: 'Fotos y vídeos',
        mediaHelp: 'Del trabajo hecho. Se pueden hacer aquí mismo.',
        mediaHint: 'Hay cosas que no se pueden fotografiar: un ruido, una fuga, una máquina que solo falla en marcha.',
        photos: 'Fotos', photosHelp: 'Del trabajo hecho. Se pueden hacer con la cámara.',
        photosPending: '{count} foto(s) a subir',
        photoFailed: 'No se pudo subir «{name}»',
        save: 'Guardar parte', saved: 'Parte guardado',
        empty: 'Sin partes todavía',
        emptyHint: 'Apunta las horas y el material al acabar: es lo que después se factura y se imputa.',
        photo: 'Foto del trabajo',
        audio: 'Nota de voz',
      },
      // -- El parte del dia: una pantalla, y un bloque por emplazamiento -----
      //
      // Las palabras son las de quien lo rellena de pie en un sitio, no las de
      // la oficina: «cuánto has estado aquí» y no «horas imputadas».
      parte: {
        title: 'Parte del día',
        day: 'El día',
        whereWere: 'Los sitios de hoy',
        whereWereHelp: 'Vienen marcados los que quedan por acabar. Quita el que no toque y rellena el resto: sus horas, sus fotos y lo que se hizo allí.',
        allStops: 'Estuve en todos',
        notHere: 'No estuve',
        here: 'Estuve aquí',
        hoursHere: 'Cuánto has estado aquí',
        alreadyHere: 'ya hay {hours} apuntadas',
        tasks: 'Las tareas de este sitio',
        tasksNone: 'Aquí no había nada apuntado. Añade lo que hayas hecho.',
        taskNote: 'Qué se hizo',
        taskHours: 'Cuánto has estado en esta tarea',
        taskHoursShort: 'h',
        fromTasks: 'La suma de las tareas',
        taskNotePlaceholder: 'Cortado y recogido; el seto del fondo se queda…',
        taskPending: 'Sigue pendiente',
        foundPending: 'Hay que volver',
        addFound: 'Añadir lo que ha salido aquí',
        closeStop: 'Dejar este sitio por acabado',
        savedStop: 'Guardado',
        tally: 'sin ningún sitio marcado | en un sitio | en {count} sitios',
        tallyTasks: '{count} tarea | {count} tareas',
        tallyPhotos: '{count} foto | {count} fotos',
        nothing: 'Marca al menos un sitio donde hayas estado.',
        hoursMissing: 'Falta decir cuánto has estado en «{name}».',
        failed: 'No se pudo guardar el parte de «{name}»: {why}',
        savedMany: '{count} partes guardados',
        savedPartly: 'Se han guardado {done} de {count}. Los que faltan siguen aquí: vuelve a darle a guardar.',
        leaveTitle: '¿Descartar el parte?',
        leaveMessage: 'Lo que has escrito no se ha guardado en ninguna parte.',
        leaveConfirm: 'Descartarlo',
        leaveCancel: 'Seguir apuntando',
        closed: 'Esta orden ya está cerrada',
        closedHint: 'Un parte más pide reabrirla, y eso se hace a propósito desde su ficha.',
        gone: 'No se encuentra la orden',
        goneHint: 'Puede que la hayan archivado. Vuelve atrás y ábrela desde la lista.',
        dictateClip: 'Nota de voz de esta parada',
        mediaPending: 'Las horas están guardadas, pero faltan {count} foto(s) o vídeo(s) por subir. Siguen aquí: vuelve a darle a Guardar cuando tengas cobertura.',
        uploading: 'Subiendo {n} de {count} · {percent} %',
        draftRestored: 'Hemos recuperado el parte que tenías a medias en este móvil.',
        errClosed: 'La oficina ha cerrado esta orden mientras escribías. Lo escrito sigue aquí: pide que la reabran y vuelve a darle a Guardar.',
        errStopGone: 'Ese sitio ya no está en la orden: lo han quitado desde la oficina. Desmárcalo o pregunta a quien planifica.',
        errHours: 'Las horas tienen que ser un número entre 0 y 24.',
        errQuantity: 'Una cantidad de material no puede ser negativa.',
        errTooLong: 'Hay un texto demasiado largo. Acórtalo un poco y vuelve a darle a Guardar.',
        errOffline: 'Sin cobertura: no se ha guardado. Lo escrito sigue aquí; vuelve a darle a Guardar cuando tengas línea.',
      },
      incidents: {
        title: 'Incidencias',
        placeholder: 'Qué ha pasado…',
        add: 'Anotar',
        empty: 'Sin incidencias',
        emptyHint: 'Anota aquí lo que impide o retrasa el trabajo, para que quede escrito.',
        resolve: 'Resolver', resolved: 'Resuelta',
        resolvedBy: 'resuelta por {who}',
      },
      history: {
        title: 'Historial',
        notePlaceholder: 'Escribe una nota en la orden…',
        add: 'Anotar',
        status: 'Estado: {from} → {to}',
        report: 'Parte: {what}',
        incident: 'Incidencia: {what}',
      },
      confirm: {
        completeTitle: 'Completar la orden',
        completeBody: 'Se anunciarán {hours} y el material apuntado, que es lo que después se imputa y se factura.',
        removeReportTitle: 'Eliminar el parte',
        removeReportBody: 'Se eliminará el parte de {hours} y se recalcularán las horas de la orden.',
      },
      completed: 'Orden completada',
      reopened: 'Orden reabierta',
      createdToast: 'Orden {code} creada',
      savedToast: 'Orden guardada',
      search: { subtitle: 'Ver la orden' },
      notifications: {
        categories: { late: 'Órdenes con retraso', assigned: 'Te asignan una orden', breakdown: 'Una avería en el campo', reportAdded: 'Un parte nuevo' },
        assigned: {
          title: 'Orden nueva para ti',
          body: '{code} · {title}{where}',
        },
        // El único aviso de este módulo que cuesta dinero por minuto.
        breakdown: {
          title: 'Avería reportada',
          body: '{asset} · {code}',
          bodyWithWho: '{asset} · {code} — lo reporta {who}',
        },
        // Del campo a la oficina, a los roles del ajuste `reportAlertRoles`.
        reportAdded: {
          title: 'Parte nuevo',
          body: '{code} · {title}',
          bodyWho: '{code} · {title} — lo escribe {who}',
          bodyWhere: '{code} · {where}',
          bodyWhereWho: '{code} · {where} — lo escribe {who}',
        },
        late: {
          title: 'Órdenes de trabajo con retraso',
          body: '{count} orden(es) han pasado su fecha prevista. La primera: {first}',
        },
      },
    },
    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    modules: { 'work-orders': 'Órdenes de trabajo' },
    assistant: {
      openers: {
        'work-orders': {
          'new-order': 'Crear una orden de trabajo',
          'my-day': '¿Qué tengo hoy?',
          'whats-late': '¿Qué órdenes llevan retraso?',
          'log-hours': 'Apunta las horas de una orden',
        },
      },
    },
    permissions: {
      workorders: {
        read: 'Ver todas las órdenes de trabajo, no solo las propias',
        write: 'Planificar: abrir órdenes y editarlas',
        report: 'Ver las órdenes propias y apuntar en ellas partes, pasos e incidencias',
        manage: 'Completar, reabrir, anular y archivar (incluye las plantillas de parte)',
        templates: 'Crear y editar las plantillas de parte',
        import: 'Importar órdenes y partes de otro sistema',
      },
    },
    settings: {
      ns: {
        'work-orders': {
          title: 'Órdenes de trabajo',
          subtitle: 'Cómo trabaja vuestra empresa y qué exige para dar una orden por acabada',
          kinds: {
            label: 'Los tipos de trabajo que hacéis',
            help: 'Vuestros, no nuestros. Al renombrar uno, las órdenes que ya lo tenían siguen mostrando el nombre viejo.',
          },
          taskTypes: {
            label: 'Los tipos de tarea que repetís en una parada',
            help: 'Solo si planeáis rutas: un jardinero escribe la suya, un instalador no la necesita nunca. Déjalo vacío y las tareas son texto libre.',
          },
          createFromPortalRequest: {
            label: 'Abrir una orden cuando un cliente pide algo por el portal',
            help: 'Nace en borrador y sin fecha, nombrando la solicitud de la que viene. Solo hace algo con el portal de cliente instalado.',
          },
          portalRequestTypes: {
            label: 'Tipos de solicitud del portal que abren una orden',
            help: 'Los ids tal y como estén configurados en el portal. Vacío significa ninguno.',
          },
          createFromAcceptedQuote: {
            label: 'Abrir una orden al aceptarse un presupuesto',
            help: 'Nace en borrador y sin fecha, y sus líneas se convierten en los pasos. Solo hace algo con el módulo de presupuestos instalado.',
          },
          allowUnplanned: {
            label: 'Dejar que la cuadrilla apunte trabajo sin orden',
            help: 'Cada faena abre su propia orden, ya cerrada y marcada como no planificada. Desactivado, primero se escribe la orden en la oficina.',
          },
          requireCustomer: {
            label: 'Exigir cliente en todas las órdenes',
            help: 'Déjalo desactivado si también planificáis trabajo vuestro: una reparación en el taller, un stand.',
          },
          requireHoursToComplete: {
            label: 'Exigir al menos un parte para completar',
            help: 'Activado, porque completar sin horas anuncia un trabajo que no costó nada a quien lo imputa.',
          },
          requireChecklistToComplete: {
            label: 'Exigir todos los pasos hechos para completar',
            help: 'Desactivado por defecto: un paso que hoy no aplica bloquearía la orden.',
          },
          requireTargetsToComplete: {
            label: 'Exigir todos los objetivos cerrados para completar',
            help: 'Desactivado por defecto, por lo mismo: una ruta a la que se le acabó la tarde se cierra y el resto se replanifica, no se queda abierta.',
          },
          breakdownAlertRoles: {
            label: 'Quien se entera de que algo se ha averiado en el campo',
            help: 'En cuanto un parte dice que una maquina se ha parado. Vacio lo apaga.',
          },
          targetTypes: {
            label: 'Sobre qué puede ir una orden',
            help: 'Los tipos de ficha que salen en «Sobre qué». Vacío ofrece todo lo que el ERP sabe buscar.',
          },
          allowFreeTextTargets: {
            label: 'Permitir escribir a mano sobre qué va una orden',
            help: 'Apagado, una orden solo puede nombrar una ficha de los tipos de arriba. Con un solo tipo y esto apagado, el planificador deja de preguntar qué es y va directo a la lista.',
          },
          autoCompleteWhenTargetsDone: {
            label: 'Completar la orden sola al cerrar su última parada',
            help: 'Cuando un parte cierra la última parada abierta. Si una regla lo impide, la orden sigue abierta.',
          },
          reportAlertRoles: {
            label: 'Quién se entera de que se ha escrito un parte',
            help: 'Con la orden, el sitio y quién lo escribe. Vacío lo apaga.',
          },
          equipmentByTaskType: {
            label: 'Qué llevar, por tipo de tarea',
            help: 'Una fila por cada tipo de tarea de W.2: las herramientas y el material que carga la cuadrilla. Las órdenes lo enseñan como «Qué llevar». Un tipo sin nada no enseña nada.',
            keyLabel: 'Tipo de tarea',
            columns: { tools: 'Herramientas', materials: 'Material' },
          },
          lateAlertRoles: {
            label: 'Quién se entera de que una orden pasa su fecha',
            help: 'Se comprueba una vez cada mañana.',
          },
        },
      },
    },
    audit: {
      actions: {
        work_orders: {
          created: 'Orden de trabajo creada',
          logged: 'Faena apuntada sin orden',
          updated: 'Orden modificada',
          planned: 'Orden planificada',
          in_progress: 'Orden empezada',
          on_hold: 'Orden parada',
          completed: 'Orden completada',
          cancelled: 'Orden anulada',
          draft: 'Orden devuelta a borrador',
          archived: 'Orden archivada',
          restored: 'Orden devuelta a las listas',
          note_added: 'Nota añadida a la orden',
          report_added: 'Parte apuntado',
          report_removed: 'Parte eliminado',
          template_created: 'Plantilla de parte creada',
          template_updated: 'Plantilla de parte modificada',
          template_published: 'Plantilla de parte publicada',
          template_retired: 'Plantilla de parte retirada',
          worksheet_completed: 'Parte guiado completado',
          step_added: 'Paso añadido a la orden',
          step_done: 'Paso marcado como hecho',
          step_undone: 'Paso desmarcado',
          step_noted: 'Apuntado qué se hizo en un paso',
          target_done: 'Parada cerrada',
          target_undone: 'Parada reabierta',
          task_added: 'Tarea añadida a una parada',
          task_done: 'Tarea hecha en una parada',
          task_undone: 'Tarea desmarcada en una parada',
          task_noted: 'Apuntado qué se hizo en una tarea',
          incident_added: 'Incidencia anotada',
          incident_resolved: 'Incidencia resuelta',
          imported: 'Orden importada de otro sistema',
        },
      },
    },
  },

  ca: {
    'work-orders': {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        today: 'una ordre avui | {count} ordres avui',
        late: 'una ordre endarrerida | {count} ordres endarrerides',
        myDay: 'una ordre al teu dia | {count} ordres al teu dia',
        myDayLate: '{count} ordres al teu dia, {late} endarrerides',
        replan: 'Replanificar',
        replanAsk: 'Replanifica l’ordre endarrerida: proposa una data nova | Replanifica les {count} ordres endarrerides: proposa una data nova per a cadascuna',
      },
      title: 'Ordres de treball',
      related: {
        ahead: 'Per fer aquí',
        done: "El que s'ha fet",
        cancelled: 'Anul·lades',
      },
      doneBanner: {
        title: 'Ordre completada',
        when: 'Tancada el {when}',
      },
      dashboard: {
        logWork: 'Apuntar feina sense ordre',
        tabToday: 'Avui',
        tabTomorrow: 'Demà',
        summaryOrders: 'Una feina | {count} feines',
        summarySites: 'un lloc | {count} llocs',
        summaryDone: 'una feta | {count} fetes',
        summaryTeam: 'Equip: {names}',
        summaryMates: 'Amb {names}',
        emptyToday: 'Avui no tens ordres',
        emptyTomorrow: 'Demà no tens ordres',
        next: 'Següent',
        open: 'Ordres obertes',
        openHelp: 'Quantes ordres de treball segueixen obertes, en qualsevol dels seus estats vius.',
        today: 'Per avui',
        todayHelp: 'Quantes ordres estan planificades per avui, tancades incloses.',
        late: 'Ordres amb retard',
        lateHelp: 'Quantes ordres obertes tenien data anterior a avui i segueixen sense tancar-se.',
        todayList: 'L\'agenda d\'avui',
        todayListHelp: 'Les ordres planificades per avui, amb el seu client o la seva adreça. Estira la targeta i n\'hi caben més.',
        myDay: 'El meu dia',
        myDayHelp: 'Les ordres que teniu assignades per avui. Cadascú veu les seves, i estirant la targeta n\'hi caben més.',
      },
      subtitle: 'Qui hi va, quan, on — i què hi va passar',
worksheet: {
        call: {
          title: 'La trucada',
          pitch: 'Explica-l’hi de cop i ell ho va apuntant. Al final ho repasses i ho deses tu.',
          start: 'Començar la trucada',
          repeat: 'Repeteix la pregunta',
          hangUp: 'Penjar',
          phase: {
            greeting: 'Un moment…',
            asking: 'Et pregunto…',
            listening: 'T’escolto',
            thinking: 'Un moment…',
            handover: 'Això es tria a la pantalla',
            closing: 'Repassem',
          },
          greeting: 'Hola, anem amb el comunicat d’avui.',
          greetingName: 'Hola {name}, com ha anat el dia?',
          opener: 'Explica’m, com ha anat avui?',
          onScreen: 'Això ho has de triar a la pantalla: {label}. T’espero.',
          stillThere: 'Hi ets? T’ho repeteixo.',
          notUnderstood: 'No n’he tret la dada. M’ho pots tornar a dir?',
          byHand: 'Millor que ho escriguis tu, que així no ho embolicem.',
          byHandNote: 'T’he deixat «{label}» perquè ho escriguis tu.',
          done: 'Ja està, no em falta res. T’ho repasso.',
          review: 'Mira-ho a la pantalla i, si està bé, desa-ho.',
          resume: 'Seguim parlant',
          keyboard: 'Ho prefereixo escriure',
          orTap: 'O toca-ho:',
          ackA: 'Molt bé.',
          ackB: 'Aha.',
          ackC: 'D’acord.',
          debug: {
            tab: 'Desenvolupament',
            title: 'Desenvolupament · la trucada per dins',
            clear: 'Buidar',
            turns: 'Torns',
            avg: 'mitjana {t}',
            llm: 'EURouter · text',
            stt: 'EURouter · oïda',
            tts: 'Azure · veu',
            fromCache: '{n} de memòria cau',
            total: 'Total de la trucada',
            silence: 'espera de silenci: {first} ms en respostes curtes, {rest} ms en explicar la feina',
            heard: 'Sentit:',
            says: 'Diu:',
            behind: 'darrere',
            cache: 'memòria cau',
            rest: 'xarxa · cua · servidor',
            empty: 'Comença la trucada i aquí es veurà cada torn: què triga cada pas, quants tokens gasta EURouter i quants caràcters Azure.',
            steps: {
              stt: 'Sentir (EURouter)',
              read: 'Repartir (EURouter)',
              place: 'Cercar a l’ERP',
              talk: 'Frase (EURouter)',
              tts: 'Veu (Azure)',
              state: 'Estat',
              settle: 'Esperar el de darrere',
            },
          },
          tapToPick: 'Triar a la pantalla',
          onKeyboard: 'Penjat. Quan vulguis, seguim parlant.',
          dontCall: 'No em truquis en obrir',
          incoming: 'Et truca l’encarregat',
          again: 'Perdona, no t’he entès.',
          notFoundPlace: 'Aquest no el tinc.',
          askLabel: '{label}?',
          whichPlace: 'A {list}?',
          whichOption: '{list}?',
          letsGo: 'Anem amb el comunicat.',
          incomingHint: 'Perquè li expliquis com ha anat el dia. Contesta quan puguis parlar.',
          answer: 'Contestar',
          decline: 'Ara no',
          callBack: 'Trucar a l’encarregat',
          willNotCall: 'Ja no sonarà en obrir un comunicat en aquest mòbil. El botó de trucar continua aquí.',
          or: 'o',
          whichAsk: 'Hi ha més d’un {label}. Quin d’aquests: {options}?',
          whichOne: '«{words}» pot ser més d’un {label}. Quin era?',
          pickOnScreen: 'Tria’l tu a la pantalla, que allà els tens tots dos.',
          notFound: 'No trobo cap {label} que es digui «{words}». Busca’l tu a la pantalla.',
        },
        title: 'Apuntar el comunicat',
        said: 'El que portes dit',
        now: 'Ara',
        readBack: 'Repassem',
        fromSystem: 'ja ho sé',
        fromSystemLocked: 'Això ho omple el sistema i no cal que ho diguis.',
        speak: 'Contesta parlant',
        heard: 'T’he sentit:',
        answer: 'Contesta',
        skip: 'Salta',
        yes: 'Sí',
        no: 'No',
        pick: 'Tria de la llista',
        listen: 'Llegeix-m’ho',
        confirm: 'Desa el comunicat',
        done: 'Comunicat desat',
        none: 'Aquesta empresa no té plantilla de comunicat. S’apunta com sempre.',
        left: 'No queda res | Queda 1 cosa per dir | Queden {n} coses per dir',
        files: 'Cap fitxer | 1 fitxer | {n} fitxers',
        savedHere: 'Sense cobertura: desat al mòbil i s’enviarà quan torni la línia.',
        queued: 'N’hi ha {n} sense enviar. S’envien soles quan torni la cobertura.',
        stillQueued: 'Queden {n} respostes sense enviar. Busca cobertura abans de confirmar el part.',
      },
      help: {
        title: 'Com funciona el comunicat per plantilla',
        subtitle: 'Deixa-la oberta en aquesta pestanya mentre muntes la teva',
        lead: 'Tu escrius les PREGUNTES que vols que es contestin en tancar un servei. L’assistent les fa al treballador una a una, en l’ordre que has posat, i no el deixa tancar el comunicat fins tenir-les totes.',
        steps: {
          clone: {
            title: 'Comença per un exemple',
            body: 'A «Plantilles de comunicat» tens el desplegable «Clona un exemple». El primer, «Comunicat complet», ja porta les sis preguntes que fa gairebé qualsevol empresa de camp: quines tasques has fet, on, amb quins treballadors, si hi ha hagut cap incidència, quin material heu gastat i per a quin client. Es clona apagada, així que pots tocar-la sense que aparegui al mòbil de ningú.',
            link: 'Vés a les plantilles',
          },
          write: {
            title: 'Escriu cada pregunta',
            body: 'Cada pregunta es defineix amb quatre coses, i només quatre:',
            b1: 'QUÈ ES PREGUNTA — el nom curt, com ara «On ha estat».',
            b2: 'QUÈ CONTESTEN — d’una llista tancada de vuit. És el que permet que l’assistent entengui «un i mig» o rebutgi «divuit mil», i el que fa que això sigui un comunicat i no una nota de veu.',
            b3: 'QUANT INSISTEIX — obligatori (no tanca sense), recomanat (ho pregunta un cop encara que ningú ho esmenti, i es pot deixar en blanc) o opcional (només si surt a la conversa).',
            b4: 'COM HO PREGUNTA L’ASSISTENT — la frase amb les teves paraules. Això és literalment el que dirà.',
          },
          preview: {
            title: 'Mira la conversa abans de publicar',
            body: 'A la dreta, a mesura que escrius, surt la conversa exacta que tindrà el treballador: què li preguntarà, en quin ordre, què no li preguntarà perquè el sistema ja ho sap i què passarà en confirmar. La calcula el mateix motor que després la condueix.',
          },
          publish: {
            title: 'Publica-la',
            body: 'Fins que no li dónes a «Publica» no surt a cap mòbil. I «Retira» la treu sense esborrar-la: els comunicats ja escrits la nomenen, i esborrar-la deixaria un històric illegible.',
          },
          fill: {
            title: 'I el treballador la omple parlant',
            body: 'Li surt a l’ordre i a cada parada d’«El meu dia», al costat del formulari de sempre. I no cal prémer res: la pantalla s’obre trucant. Tres coses que convé saber:',
            b1: 'No li pregunta el que ja se sap: les hores surten del seu fitxatge i el client de l’ordre.',
            b2: 'Pot contestar parlant o tocant, sempre les dues. Si hi ha soroll o no hi ha micròfon, escriu. L’emplaçament o l’obra també es diuen: si n’hi ha dos que s’assemblen, pregunta quin.',
            b3: 'Abans de tancar li ho llegeix tot perquè ho confirmi, i sense cobertura la conversa no s’atura: es desa al mòbil i s’envia quan torna la línia.',
          },
        },
        typesTitle: 'Els vuit tipus de resposta',
        typesBody: 'La llista és tancada a propòsit. L’assistent només pot conduir la conversa perquè sap què és cada resposta; amb text lliure no hi hauria res a comprovar ni res a descomptar del magatzem.',
        types: {
          number_unit: 'Un nombre amb la seva unitat i el seu rang: hores, quilos, sacs.',
          boolean: 'Sí o no.',
          choice: 'Una de les opcions que tu escriguis.',
          catalogue: 'Un de la llista d’un altre mòdul: un emplaçament, un treballador, un material, un client. És el que deixa que el lloc quedi enllaçat i que el material surti de la prestatgeria.',
          photo: 'Una o diverses, amb l’opció d’abans i després.',
          signature: 'La signatura del client.',
          text: 'Una nota curta. L’únic text lliure.',
          datetime: 'Una data o una hora.',
        },
        offTitle: 'Com treure aquests tutorials',
        offBody: 'Els «?» de les pantalles surten perquè tens el mode tutorials encès al teu perfil. És teu i no de l’empresa: apagar-lo no l’hi treu a ningú més.',
        offLink: 'Vés al meu perfil',
      },
            worksheets: {
        title: 'Plantilles de comunicat',
        subtitle: 'Què s’apunta en tancar un servei, i en quin ordre es pregunta',
        listTitle: 'Les plantilles d’aquesta empresa',
        table: { name: 'Plantilla', sector: 'Sector', fields: 'Preguntes', status: 'Estat' },
        empty: 'Encara no hi ha cap plantilla. Clona’n una d’exemple i edita-la.',
        emptyHint: 'Una plantilla decideix què li pregunta l’assistent al treballador en tancar un servei.',
        newTitle: 'Nova plantilla de comunicat',
        editTitle: 'Edita la plantilla',
        fieldsCount: 'Cap pregunta | 1 pregunta | {n} preguntes',
        active: 'Publicada',
        inactive: 'Retirada',
        activeHint: 'Només les publicades surten al mòbil.',
        actions: {
          new: 'Nova plantilla',
          clone: 'Clona un exemple',
          duplicate: 'Duplica',
          publish: 'Publica',
          retire: 'Retira',
          preview: 'Mira la conversa',
          addField: 'Afegeix pregunta',
          save: 'Desa la plantilla',
        },
        form: {
          name: 'Nom',
          namePlaceholder: 'Comunicat de manteniment',
          description: 'Per a què és',
          descriptionPlaceholder: 'Quan es fa servir aquesta plantilla',
          sector: 'Sector',
          sectorPlaceholder: 'jardineria, manteniment…',
          kinds: 'Només per a aquests tipus de feina',
          kindsHelp: 'Buit vol dir «per a tots». Es comprova contra la llista de tipus de feina d’Ajustos.',
        kindsAdd: 'Afegeix un tipus de feina…',
          roles: 'Només per a aquests rols',
          rolesHelp: 'Buit vol dir «per a tothom».',
          fields: 'Les preguntes, en l’ordre en què es fan',
          fieldsHelp: 'L’assistent les fa de dalt a baix, i només les que faltin: el que ja sàpiga o ja li hagin explicat no ho torna a preguntar.',
          nameRequired: 'La plantilla necessita un nom.',
          fieldsRequired: 'Una plantilla sense preguntes no pregunta res.',
          saved: 'Plantilla desada',
          duplicateName: 'Nom de la còpia',
        },
        field: {
          label: 'Què es pregunta',
          labelPlaceholder: 'Sacs d’adob',
          type: 'Què contesten',
          requirement: 'Quant insisteix',
          source: 'D’on surt',
          autoSource: 'Qui l’omple',
          unit: 'Unitat',
          unitPlaceholder: 'h, kg, sacs',
          options: 'Opcions',
          optionsHelp: 'Una per línia. En calen almenys dues.',
          catalogue: 'Catàleg',
          catalogueHelp: 'De quina llista d’un altre mòdul es tria.',
          min: 'Mínim',
          max: 'Màxim',
          maxLength: 'Llarg màxim',
          multiple: 'Admet diverses',
          beforeAfter: 'Abans i després',
          hint: 'Com ho pregunta l’assistent',
          hintPlaceholder: 'Quants sacs d’adob has gastat?',
          hintHelp: 'Amb les teves paraules. Si ho deixes en blanc, es pregunta amb l’etiqueta.',
          binds: 'Es desa a',
          bindsHelp: 'Lligar-ho a una casella del comunicat de sempre fa que la resta de l’ERP ho vegi.',
          postAction: 'En tancar el comunicat',
          up: 'Puja',
          down: 'Baixa',
          remove: 'Treu la pregunta',
        },
        types: {
          number_unit: 'Nombre amb unitat',
          boolean: 'Sí o no',
          choice: 'Llista d’opcions',
          catalogue: 'Del catàleg d’un altre mòdul',
          photo: 'Fotos o vídeo',
          signature: 'Signatura',
          text: 'Nota curta',
          datetime: 'Data i hora',
        },
        requirements: {
          required: 'Obligatori',
          recommended: 'Recomanat',
          optional: 'Opcional',
        },
        requirementHelp: {
          required: 'El comunicat no es pot tancar sense.',
          recommended: 'L’assistent ho pregunta un cop encara que ningú ho esmenti, i es pot deixar en blanc.',
          optional: 'Només s’omple si el treballador ho esmenta.',
        },
        sources: {
          manual: 'Ho diu el treballador',
          auto: 'Ho omple el sistema tot sol',
        },
        autoSources: {
          worker: 'Qui ho va fer',
          now: 'La data i l’hora',
          service: 'El servei del dia',
          customer: 'El client',
          clocked_hours: 'Les hores del fitxatge',
          location: 'On va ser',
        },
        bindings: {
          none: 'Només a la plantilla',
          hours: 'Les hores del comunicat',
          description: 'Què s’ha fet',
          workDate: 'El dia de la feina',
          materials: 'El material gastat',
          photos: 'Les fotos',
          targetDone: 'Lloc acabat',
        },
        postActions: {
          none: 'Res',
          consume_stock: 'Descomptar del magatzem',
          raise_incident: 'Obrir una incidència',
          complete_service: 'Donar el servei per fet',
        },
        preview: {
          title: 'La conversa que produirà',
          help: 'Calculada pel mateix motor que la condueix: això és el que li passarà al treballador.',
          prefilled: 'No es pregunta, ja se sap',
          asks: 'El que l’assistent preguntarà, en aquest ordre',
          onlyIfMentioned: 'Només si ho esmenta',
          afterwards: 'En confirmar',
          blocking: 'Sense això no es pot tancar',
          nothing: 'Res',
          opening: 'Explica’m què has fet.',
          closing: 'T’ho llegeixo perquè ho confirmis.',
        },
        seeds: {
          complete: {
            name: 'Comunicat complet',
            description: 'Què s’ha fet, on, amb qui, què ha passat i què s’ha gastat.',
            fields: {
              hours: {
                label: 'Hores',
                hint: 'Quanta estona hi has estat?',
              },
              tasks: {
                label: 'Quines tasques has fet',
                hint: 'Explica’m què has fet avui.',
              },
              where: {
                label: 'On ha estat',
                hint: 'On has fet aquestes tasques?',
              },
              crew: {
                label: 'Amb quins treballadors',
                hint: 'Qui hi ha anat amb tu?',
              },
              issues: {
                label: 'Incidències',
                hint: 'Hi ha hagut cap incidència o cap problema?',
              },
              material: {
                label: 'Material gastat',
                hint: 'Heu gastat cap material?',
              },
              customer: {
                label: 'Per a quin client',
                hint: 'A quin client s’assigna?',
              },
              photo: {
                label: 'Fotos',
                hint: 'Em fas una foto de com ha quedat?',
              },
            },
          },
          gardening: {
            name: 'Comunicat de jardineria',
            description: 'Una ronda de manteniment de jardins.',
            fields: {
              hours: {
                label: 'Hores',
                hint: 'Quanta estona hi has estat?',
              },
              done: {
                label: 'Què s’ha fet',
                hint: 'Explica’m què has fet al jardí.',
              },
              mown: {
                label: 'S’ha segat',
                hint: 'Has segat?',
              },
              fertiliser: {
                label: 'Sacs d’adob',
                hint: 'Quants sacs d’adob has gastat?',
              },
              material: {
                label: 'Material gastat',
                hint: 'Has gastat res del magatzem?',
              },
              photo: {
                label: 'Fotos',
                hint: 'Fes-me una foto de com ha quedat.',
              },
              issues: {
                label: 'Incidències',
                hint: 'Cap incidència? Res trencat, res a tornar a mirar.',
              },
            },
          },
          maintenance: {
            name: 'Comunicat de manteniment',
            description: 'Una intervenció sobre una màquina o una instal·lació.',
            fields: {
              hours: {
                label: 'Hores',
                hint: 'Quanta estona hi has estat?',
              },
              kind: {
                label: 'Tipus d’intervenció',
                hint: 'Ha estat preventiu, correctiu o una revisió?',
                options: 'Preventiu · Correctiu · Revisió',
              },
              done: {
                label: 'Què s’ha fet',
                hint: 'Explica’m què has fet a la màquina.',
              },
              stopped: {
                label: 'La màquina ha estat aturada',
                hint: 'Ha estat aturada?',
              },
              parts: {
                label: 'Recanvis',
                hint: 'Quins recanvis has posat?',
              },
              reading: {
                label: 'Lectura de pressió',
                hint: 'Quina pressió marca?',
              },
              issues: {
                label: 'Incidències',
                hint: 'Cap incidència per apuntar?',
              },
            },
          },
          electrician: {
            name: 'Comunicat d’electricista',
            description: 'Una intervenció elèctrica amb signatura del client.',
            fields: {
              hours: {
                label: 'Hores',
                hint: 'Quanta estona hi has estat?',
              },
              done: {
                label: 'Què s’ha fet',
                hint: 'Explica’m què has fet.',
              },
              measured: {
                label: 'Tensió mesurada',
                hint: 'Quina tensió has mesurat?',
              },
              earthing: {
                label: 'Presa de terra comprovada',
                hint: 'Has comprovat la presa de terra?',
              },
              material: {
                label: 'Material gastat',
                hint: 'Quin material has posat?',
              },
              panel: {
                label: 'Foto del quadre',
                hint: 'Fes-me una foto del quadre.',
              },
              signature: {
                label: 'Signatura del client',
                hint: 'Que t’ho signi el client.',
              },
              issues: {
                label: 'Incidències',
                hint: 'Cap incidència?',
              },
            },
          },
        },
      },
      detailTitle: 'Ordre de treball',
      newTitle: 'Nova ordre de treball',
      editTitle: "Editar l'ordre",
      actions: {
        new: 'Nova ordre', edit: 'Editar', addReport: 'Apuntar comunicat',
        guidedReport: 'Apuntar parlant',
        complete: 'Completar', reopen: 'Reobrir', cancel: 'Cancel·lar',
        save: 'Desar', remove: 'Eliminar',
      },
      status: {
        draft: 'Esborrany', planned: 'Planificada', in_progress: 'En curs',
        on_hold: 'Aturada', completed: 'Completada', cancelled: 'Anul·lada',
      },
      priority: { low: 'Baixa', medium: 'Normal', high: 'Alta', critical: 'Crítica' },
      severity: { low: 'Lleu', medium: 'Mitjana', high: 'Greu', critical: 'Crítica' },
      day: { today: 'Avui', yesterday: 'Ahir', tomorrow: 'Demà' },
      tabs: {
        overview: 'Resum', reports: 'Comunicats', incidents: 'Incidències de l’ordre', history: 'Historial',
      },
      // ── Escriure l'ordre amb ajuda ────────────────────────────────
      ai: {
        write: 'Redactar',
        writeHint: 'Escriu el cos de l’ordre a partir del que hi hagi posat',
        summarise: 'Resumir al títol',
        summariseHint: 'En treu una línia i la posa de títol',
        task: 'Escriure la tasca',
        taskHint: 'Escriu una tasca a partir del que hi hagi al camp',
        working: 'Escrivint…',
      },
      // ── Sobre què va l'ordre, i el seu planificador ───────────────
      // La tarjeta que se confirma desde el asistente (create_order) y que
      // se ve durante la llamada mientras la orden se va armando.
      assistant: {
        // Las respuestas de las consultas que contestan las reglas (queries.js).
        query: {
          agenda: {
            self: 'Tens {count} ordres el {when}: {list}.',
            selfNone: 'No tens cap ordre el {when}.',
            other: '{who} té {count} ordres el {when}: {list}.',
            otherNone: '{who} no té cap ordre el {when}.',
          },
          late: {
            some: 'Hi ha {count} ordres amb retard: {list}.',
            none: 'No hi ha cap ordre amb retard.',
          },
        },
        order: {
          action: 'Obrir una ordre de treball',
          what: 'Què',
          when: 'Quan',
          draft: 'Esborrany, sense dia',
          customer: 'Client',
          who: 'Qui hi va',
          noTasks: 'Sense tasques',
          more: '{count} llocs més',
          outcome: 'Ordre {code} creada',
          fromPlaces: 'Ho pren dels seus llocs',
          nobody: 'Ningú encara',
          noDateNote: 'Sense dia: es desarà com a esborrany i no arribarà a ningú',
          noCrewNote: 'Encara no hi va ningú',
          noPlaceNote: 'No esmenta cap lloc',
          noTasksNote: 'Sense tasques a: {places}',
          severalPlaces: '«{name}» coincideix amb diversos: {options}. Tria’l dient-ho o escrivint-ho.',
          unknownPlace: '«{name}» no és entre els llocs: hi anirà tal com s’ha escrit',
          unknownPerson: 'No trobo «{name}» entre les persones',
          placeN: 'Lloc {n}',
          tasks: 'Tasques',
          noPlace: 'Encara sense lloc',
          where: 'On',
          priority: 'Prioritat',
          notes: 'Notes',
          noNotes: 'Res',
          dayDoubtNote: 'El dia no ha quedat clar: tria’l',
          from: 'Surt de',
          sourceUnreadable: 'No es pot llegir aquest pla o aquesta incidència: l’ordre no hi pot apuntar',
          unknownCustomer: '«{name}» no és entre els clients',
          timeNote: 'Hora: {time}',
          readPlace: 'Ordre a {place}',
          readDay: '{day}',
          readDayTime: '{day}, a les {time}',
          readTasks: 'Cal {tasks}',
          readCrew: 'Hi van {who}',
          readOne: 'Hi va {who}',
          asSaid: '«{said}», tal qual',
          internal: 'Interna: sense lloc ni client',
        },
        change: {
          order: 'Ordre',
          noOrder: 'Quina ordre?',
          kind: 'Ordre de treball',
          unknownOrder: 'Cap ordre oberta no coincideix amb «{said}»',
          severalOrders: '«{said}» coincideix amb diverses ordres: tria’n una',
          until: 'Fins ara',
          closedOrder: 'Aquesta ordre ja està tancada',
          summary: {
            reschedule: 'Passar {order} al {day} {time}',
            assign: '{order}: hi va {who}',
            priority: {
              low: '{order}: prioritat baixa', medium: '{order}: prioritat mitjana', high: '{order}: prioritat alta', critical: '{order}: urgent', other: '{order}: canviar la prioritat',
            },
            status: {
              draft: '{order}: tornar a esborrany', planned: '{order}: planificar', in_progress: '{order}: començar', on_hold: '{order}: posar en pausa',
              completed: '{order}: completar', cancelled: '{order}: anul·lar', other: '{order}: canviar l’estat',
            },
          },
          rescheduleAction: 'Passar una ordre a un altre dia',
          newDay: 'Dia nou',
          noDay: 'Tria el dia',
          pickDay: 'Tria el dia nou',
          time: 'Hora',
          noTime: 'Sense hora',
          timeKept: '{time} (la d’abans)',
          badTime: 'L’hora s’escriu HH:MM',
          noDayBefore: 'Sense dia',
          draftPlanned: 'És un esborrany: amb dia passa a planificada i arriba a la colla',
          readMove: '{code}, al {day}',
          readMoveTime: '{code}, al {day} a les {time}',
          rescheduled: '{code} passada al {day}',
          assignAction: 'Canviar qui hi va',
          who: 'Qui hi va',
          nobody: 'Ningú',
          team: 'Equip',
          unknownPerson: 'No trobo «{name}» entre les persones',
          unknownTeam: 'No hi ha cap equip que es digui «{name}»',
          nobodyNote: 'No hi aniria ningú: tria qui hi va',
          readAssign: '{code}: hi va {who}',
          assigned: '{code}: hi va {who}',
          priorityAction: 'Canviar la prioritat',
          priority: 'Prioritat',
          noPriority: 'Tria’n una',
          readPriority: {
            low: '{code}, prioritat baixa', medium: '{code}, prioritat mitjana', high: '{code}, prioritat alta', critical: '{code}, urgent',
          },
          prioritised: {
            low: '{code}: prioritat baixa', medium: '{code}: prioritat mitjana', high: '{code}: prioritat alta', critical: '{code}: urgent', other: '{code}: prioritat canviada',
          },
          statusAction: 'Canviar l’estat d’una ordre',
          status: 'Estat',
          noStatus: 'Tria’n un',
          reason: 'Per què',
          noReason: 'No s’ha dit',
          badTransition: 'Des de l’estat en què és, l’ordre no pot passar a aquest',
          needsManage: 'Completar o anul·lar necessita el permís de gestionar ordres',
          needsReason: 'Digues per què s’anul·la',
          completeNote: 'En completar-la, les seves hores passen a qui les factura',
          readStatus: {
            draft: '{code}, a esborrany', planned: '{code}, planificada', in_progress: 'Començar {code}', on_hold: 'Posar en pausa {code}', completed: 'Completar {code}', cancelled: 'Anul·lar {code}',
          },
          outcomeStatus: {
            draft: '{code} tornada a esborrany', planned: '{code} planificada', in_progress: '{code} començada', on_hold: '{code} en pausa',
            completed: '{code} completada', cancelled: '{code} anul·lada', other: '{code}: estat canviat',
          },
        },
      },
      // Lo que la llamada pregunta mientras falta algo de una orden.
      voice: {
        askPlace: 'On és?',
        askReportOrder: 'De quina ordre és el part?',
        askReportHours: 'Quantes hores?',
        askPlaceAgain: 'No trobo «{said}». Com es diu el lloc?',
        whichPlace: '«{said}» pot ser {options} o {last}. Quin?',
        thisPlace: 'No trobo «{said}» tal qual. Vols dir {last}?',
        askTasks: 'Què cal fer?',
        askDay: 'Per a quin dia?',
        askPeople: 'Qui hi va?',
        askPeopleAgain: 'No trobo «{name}». Qui hi va?',
        askOrder: 'Quina ordre?',
        askOrderAgain: 'No trobo l’ordre «{said}». Quina és?',
        whichOrder: '«{said}» pot ser {options} o {last}. Quina?',
        thisOrder: 'Vols dir {last}?',
        askNewDay: 'A quin dia la passo?',
        askTeamAgain: 'No trobo l’equip «{name}». Qui hi va?',
        askPriority: 'Quina prioritat: baixa, mitjana, alta o urgent?',
        askStatus: 'La començo, la poso en pausa, la completo o l’anul·lo?',
        askCancelReason: 'Per què s’anul·la?',
        whichTask: '«{said}» no és a les vostres tasques. És {options} o {last}? Si no, digues «deixa-ho així».',
        thisTask: '«{said}» no és a les vostres tasques. És {last}? Si no, digues «deixa-ho així».',
      },
      targets: {
        tab: 'Objectius',
        title: 'Sobre què',
        what: 'Què és',
        freeText: 'Escriure-ho a mà',
        name: 'Nom',
        namePlaceholder: 'Premsa 3, Línia 2, xalet dels Puig…',
        search: 'Cercar',
        searchPlaceholder: 'Escriu per cercar…',
        wherePlaceholder: 'On: nau, carrer, planta…',
        add: 'Afegir',
        remove: 'Treure’l de l’ordre',
        notes: 'Notes',
        emptyHint: 'Una ordre sense res aquí és una feina solta, i també val.',
        chosen: 'triats',
        pickManySearch: 'Acota escrivint: Puigcerdà, Alp, nau 2…',
        pickManyLoading: 'Cercant…',
        pickManyNone: 'No en surt cap. Prova amb una altra paraula.',
        pickManyAll: 'Marcar els {count} que surten',
        group: 'Carpeta',
        groupAll: 'Totes',
        pickGroupAll: 'Afegir els {count} de «{group}»',
        pickManyAlready: '{count} ja són a l’ordre',
        pendingHere: 'El que hi havia pendent aquí',
        pendingHint: 'Apuntat un altre dia en aquest lloc. El que marquis entra a l’ordre, '
          + 'i en completar-la es tanca on era.',
        pendingLoading: 'Mirant què hi ha pendent…',
        unit: 'Objectiu',
        units: 'objectius',
        tasks: 'tasques',
        count: '{count} objectius',
        progress: '{done}/{total} objectius',
        taskProgress: '{done}/{total} tasques',
        column: 'Objectius',
        done: 'Fet',
        pending: 'Pendent',
        markDone: 'Marcar-ho com a fet',
        markPending: 'Tornar-ho a deixar pendent',
        doneBy: 'per {who}',
        noTasks: 'Sense tasques apuntades aquí',
      },
      planner: {
        removeTask: 'Treure «{task}»',
        stepWho: 'Quan i sobre què',
        stepWork: 'La feina',
        stepDetail: 'El detall',
        stepCustomer: 'Per a qui',
        stepWhen: 'Quan',
        stepCrew: 'Qui hi va',
        stepTargets: 'Sobre què',
        stepPlaces: 'Els llocs',
        stepTasks: 'Les tasques',
        stepSteps: 'Els passos',
        back: 'Enrere',
        lead: {
          dictate: 'Explica-ho en veu alta i la resta s’omple sol. Te’l pots saltar.',
          when: 'Quin dia hi va la colla? Si encara no ho saps, deixa-ho en blanc.',
          targets: 'On s’ha d’anar? Un, o els cinquanta d’una ronda.',
          tasks: 'Què s’ha de fer a cada lloc? El que es repeteixi, posa-ho a tots de cop.',
          work: 'En una línia: quina feina és.',
          detail: 'El que la colla necessita saber en arribar.',
          customer: 'Per a qui és i a quina obra va, si va a alguna.',
          crew: 'Qui hi va. Amb un equip, se’n copia la gent.',
          steps: 'Els passos que es marquen en fer-los. Només en obrir-la.',
        },
        create: 'Crear l’ordre',
        next: 'Continuar',
        sectionWhen: 'Quan',
        sectionWho: 'Qui hi va',
        sectionOrder: 'L’ordre de treball',
        sectionNotes: 'Notes per a qui hi va',
        descriptionHelp: 'El que s’ha de fer, amb el detall que calgui. És el que es llegeix a l’obra.',
        descriptionPlaceholder: 'Què passa, què s’ha de fer, què s’ha de tenir en compte…',
        notesHelp: 'El de fora de la feina: accessos, claus, horaris, amb qui parlar.',
        notesPlaceholder: 'Trucar abans d’anar-hi, la clau és a consergeria…',
        inTwoDays: 'Demà passat',
        nextMonday: 'Dl. vinent',
        crewAdd: 'Afegir algú més…',
        crewNone: 'Hi va només qui tingui l’ordre assignada',
        moveUp: 'Pujar',
        moveDown: 'Baixar',
        viewTargets: 'Un a un',
        viewTasks: 'Per tasca',
        viewSummary: 'Resum',
        bulkTitle: 'Una tasca a tots',
        bulkApply: 'Afegir als {count}',
        taskTypeAny: 'Tipus de tasca…',
        taskTextPlaceholder: 'Què s’ha de fer…',
        addTask: 'Afegir tasca',
        noTasks: 'Sense tasques encara',
        noTasksYet: 'Encara no hi ha cap tasca apuntada',
        atTargets: 'a {count}',
        titleSuggested: 'Si el deixes buit: «{title}»',
        titleMore: '{names} i {count} més',
        bringPending: 'Portar tot el que hi ha pendent',
        bringPendingHint: 'El que es va apuntar un altre dia a cada lloc entra a l’ordre. El que sobri es treu lloc a lloc.',
        bringingPending: 'Mirant què hi ha pendent a {count} llocs…',
        broughtPending: '{count} pendents portades a l’ordre',
        noPending: 'No hi havia res pendent en aquests llocs',
        addTasksHere: 'Afegir tasques',
        noTaskTypes: 'Encara no hi ha tipus de tasca: cada tasca s’escriu a mà. Si repeteixes sempre les mateixes (tall de gespa, poda, reg…), escriu-les un cop a Configuració i sortiran aquí en un desplegable.',
        noTaskTypesLink: 'Escriure-les a Configuració (W.2)',
      },
      equipment: {
        title: 'Què cal portar',
        tools: 'Eines',
        materials: 'Material',
        forTasks: 'per a {types}',
        hint: 'Surt dels tipus de tasca de la ruta (Configuració, W.15).',
      },
      myDay: {
        title: 'El meu dia',
        subtitle: 'Què tens avui, i què portes fet',
        today: 'Avui',
        previous: 'Dia anterior',
        next: 'Dia següent',
        targets: '{count} objectius',
        late: '{count} amb retard',
        incidents: '{count} amb incidència',
        empty: 'Avui no tens res assignat',
        emptyHint: 'Si esperaves feina, parla amb qui planifica: pot ser que encara no tingui data.',
        failed: 'No s’ha pogut carregar el teu dia',
        failedHint: 'Sense cobertura, o el servidor no respon. No s’ha perdut res: torna-ho a provar d’aquí a un moment.',
        retry: 'Tornar-ho a provar',
      },
      dictate: {
        step: 'Dictar-la',
        title: 'Dictar l’ordre',
        subtitle: 'Explica-ho i s’omple sol. Ho revises abans de desar: aquí no es desa res.',
        clip: 'L’enregistrament',
        read: 'Llegir-ho',
        heard: 'El que s’ha sentit',
        silence: 'No s’ha sentit res. Torna a enregistrar més a prop del micròfon.',
        which: 'Quin d’aquests és «{words}»?',
        notFound: 'No he trobat res que es digui «{words}». Posa-ho a mà si cal.',
      },
      board: { title: 'Obertes per tipus de feina' },
      filters: {
        oneCustomer: "Només d'un client",

        search: 'Cercar', searchPlaceholder: 'Número, concepte, client, lloc…',
        status: 'Estat', allStatuses: 'Tots', date: 'Dia',
        team: 'Equip', allTeams: 'Tots',
        onlyOpen: 'Només obertes', onlyLate: 'Només amb retard', onlyUnplanned: 'Només feines',
      },
      table: {
        code: 'Número', title: 'Feina', planned: 'Prevista', assignee: 'Assignada a',
        progress: 'Passos', hours: 'Hores', status: 'Estat', crewMore: 'més',
      },
      kpi: {
        today: 'Per avui', open: 'Obertes', late: 'Amb retard', hours: 'Hores completades',
      },
      empty: {
        none: 'Encara no hi ha ordres de treball',
        noneHint: "Obre la primera, o accepta un pressupost i deixa que s'obri sola.",
        filtered: 'Sense resultats',
        filteredHint: 'Prova a treure algun filtre o a cercar una altra cosa.',
      },
      lateBy: '{days} dies de retard',
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      file: { title: 'Fitxa' },
      fields: {
        code: 'Número', title: 'Concepte', kind: 'Tipus de feina', priority: 'Prioritat',
        customer: 'Client', project: 'Projecte', location: 'Lloc', description: 'Descripció',
        plannedDate: 'Data prevista', assignee: 'Assignada a', crew: 'Qui més hi va',
        team: 'Equip',
        quote: 'Pressupost', hours: 'Hores apuntades',
        source: { incident: 'Surt de la incidència', maintenance: 'Surt del manteniment' },
      },
      form: {
        what: 'La feina', who: 'Qui i quan',
        titlePlaceholder: 'Canviar la bomba del circuit 2…',
        customerPlaceholder: 'Empresa o particular',
        projectPlaceholder: 'A quin projecte pertany',
        projectHelp: "Si ho indiques, les hores i el material d'aquesta ordre aniran al seu llibre de costos.",
        locationPlaceholder: 'On cal anar',
        plannedHelp: 'En posar data, l’ordre passa a planificada i apareix en el dia de qui hi vagi.',
        nobody: 'Sense assignar',
        crewHelp: 'Es trien d’un en un, de la llista de persones. Tots veuran l’ordre en el seu dia.',
        noTeam: 'Sense equip',
        teamHelp: 'Triar-ne un copia la seva gent aquí sota, i el seu responsable com a assignat si no hi ha '
          + 'ningú. Còpia: qui hi vagi es queda apuntat encara que l’equip canviï després.',
        teamMembers: '{count} persones',
        noPeople: 'No hi ha persones actives per assignar.',
        checklistTitle: 'Passos a seguir',
        checklistHelp: 'Només al crear: després s’editen a la fitxa, on cada pas desa qui el va marcar.',
        titleRequired: "L'ordre necessita un concepte",
      },
      checklist: {
        title: 'Passos',
        empty: 'Sense passos',
        emptyHint: 'Afegeix els que es repeteixen en aquest tipus de feina.',
        add: 'Afegir pas',
        addPlaceholder: 'Comprovar pressió del circuit…',
      },
      // ── La feina: treball que ningú va planificar ───────────────────
      log: {
        unlinked: 'La feina està desada, però sense enllaçar a {names}: el teu compte no pot veure aquestes fitxes. Avisa l’oficina perquè l’enllaci.',
        action: 'Apuntar feina',
        title: 'Apuntar una feina',
        hint: 'Treball que has fet sense que hi hagués una ordre. Obre la seva pròpia ordre, ja tancada, al teu nom.',
        where: 'On ha estat',
        whereHelp: 'Un emplaçament, una màquina, un vehicle… el que hi hagi en aquesta instància.',
        whereRequired: 'Digues on ha estat, o què has fet',
        freeText: 'Escriure-ho a mà',
        search: 'Quin',
        searchPlaceholder: 'Comença a escriure…',
        name: 'Què o on',
        namePlaceholder: 'Jardí dels Pins, Premsa 3…',
        kind: 'Tipus de treball',
        hours: 'Quant has trigat',
        workDate: 'Quin dia',
        workDateHelp: 'El dia que vas treballar, no el d avui. El dissabte es pot apuntar el dilluns.',
        description: 'Què has fet',
        descriptionPlaceholder: 'Podats els setos de l entrada i repassat el reg…',
        customerHelp: 'Aquesta instància demana client a totes les ordres.',
        tasks: 'Tasques fetes', addTask: 'Afegir tasca',
        addStop: 'Afegir un altre emplacament',
        quick: {
          where: 'On has estat?',
          whereHelp: 'Toca els llocs. Els d\'avui surten primer.',
          today: 'avui',
          noSuggestions: 'No hi ha llocs recents: afegeix-ne un.',
          otherSite: 'Un altre lloc',
          pickFirst: 'Toca on has estat',
          hours: 'Quanta estona?',
          otherHours: 'Una altra',
          what: 'Què has fet?',
          photos: 'Fotos',
          issue: 'Alguna cosa trencada',
          issueLabel: 'Què passa',
          issuePlaceholder: 'El reg del sector 2 perd aigua…',
          tell: 'Explicar-ho',
          more: 'Més detalls',
          less: 'Menys detalls',
          day: 'El dia',
          todayPill: 'Avui',
          yesterday: 'Ahir',
          otherDay: 'Un altre dia',
          with: 'Amb qui has anat?',
          alone: 'Sol',
          dayMore: 'Tipus, projecte i client',
          clocked: 'de {hours} fitxades',
          send: 'Enviar',
          issueSaved: 'Incidència apuntada a {name}',
          issueFailed: 'No s\'ha pogut apuntar la incidència de {name}',
        },
        dropStop: 'Treure aquest emplacament',
        stopBlank: 'Lloc sense nom',
        whereType: 'Tipus de lloc',
        what: 'La feina',
        noTasks: 'Encara no hi ha res apuntat.',
        noMaterials: 'No es va gastar res.',
        pendingTitle: 'El que hi havia pendent aquí',
        pendingHint: 'Marca el que hagis fet: entra a la feina i es dóna per fet allà on estigués apuntat.',
        pendingFailed: "La feina està desada, però «{name}» no s'ha pogut tancar i continua pendent.",
        leaveTitle: 'Descartar la feina?',
        leaveMessage: "El que has escrit no s'ha desat enlloc.",
        leaveConfirm: 'Descartar-la',
        leaveCancel: 'Continuar apuntant',
        taskPlaceholder: 'Poda del seto, canvi de l aspersor…',
        dictate: {
          title: 'Explica-ho i s’omple sol',
          subtitle: 'Digues què has fet, on, quanta estona i amb què. Ho revises abans de desar.',
          clip: 'Nota de veu del comunicat',
          read: 'Omplir amb el que he dit',
          heard: 'El que s’ha sentit',
          silence: 'No s’ha sentit res. Torna a gravar.',
          which: 'Quin d’aquests és «{words}»?',
          notFound: '«{words}» no l’he trobat. Tria’l tu a sota.',
          noHours: 'No has dit quanta estona has estat. Posa-ho tu: no m’ho invento.',
          keeps: 'Encara no s’ha desat res. Repassa el formulari i dona-li a apuntar.',
          clipFailed: 'La feina està apuntada, però la nota de veu no s’ha pogut penjar.',
        },
        time15: '+15 min',
        time30: '+30 min',
        time60: '+1 h',
        timeClear: 'A zero',
        crew: 'Qui anava amb tu',
        crewHelp: 'S’apunten les seves hores en aquest comunicat. No assigna feina a ningú.',
        crewAdd: 'Afegir algú',
        crewPlaceholder: 'Tria un company…',
        crewHours: 'Hores de {who}',
        crewNobody: 'No hi ha ningú per afegir.',
        short: 'Apuntar',
        save: 'Apuntar feina',
        saved: 'Feina apuntada com a {code}',
        savedOpen: 'Feina apuntada com a {code}. Queda oberta: la oficina l ha de tancar.',
      },
      unplanned: {
        badge: 'No planificat',
        title: 'Afegit a obra per {name}',
        origin: 'Feina',
        originHelp: 'Ningú la va planificar: la va apuntar qui la va fer.',
        addTaskPlaceholder: 'El que calia fer i no era a la llista…',
      },
      imported: {
        badge: 'Importada',
        help: "Aquesta ordre es va portar de {system}: les dates, qui va marcar cada tasca i els comunicats són els d'allà.",
        from: 'Importada de',
        createdThere: 'creada allà el {date}',
      },
      report: {
        breakdown: {
          toggle: 'Hi va haver avaria',
          hint: 'Apunta l’hora en què va parar. És el que ningú no recorda una setmana després, i és d’on surten l’MTBF i l’MTTR.',
          which: 'Què va parar',
          stoppedAt: 'Va parar a les',
          stoppedAtHint: 'L’hora de debò, no la del comunicat',
          restartedAt: 'Va tornar a les',
          restartedAtHint: 'Buit = segueix parada',
          didStop: 'Va parar producció',
          what: 'Què va passar',
          whatHint: 'Amb les teves paraules. La causa i el tipus els posa manteniment després, contra el seu catàleg',
          whatPlaceholder: 'Va rebentar el llatiguillo del cilindre de tancament…',
        },
        title: 'Comunicat de {code}',
        hours: 'Hores', hoursPlaceholder: 'p. ex. 6,5',
        hoursRequired: 'El comunicat necessita les hores treballades',
        workDate: 'Dia de la feina',
        workDateHelp: "El dia en què es va treballar, no el d'avui.",
        description: 'Què s’ha fet',
        descriptionPlaceholder: 'Substituïda la bomba i purgat el circuit…',
        materials: 'Material usat', addLine: 'Afegir línia',
        measured: 'Amidat avui', addMeasure: 'Afegir amidament',
        itemPlaceholder: 'Cercar la partida',
        measuredHint: "El que s'ha fet AVUI, no el total de l'obra. La partida ho acumula sola.",
        noMaterials: 'Sense material. Afegeix línies si heu gastat alguna cosa.',
        materialPlaceholder: 'Cable, tub, peça…',
        qty: 'Quant.', unit: 'Ud.', price: 'Preu',
        media: 'Fotos i vídeos',
        mediaHelp: 'De la feina feta. Es poden fer aquí mateix.',
        mediaHint: 'Hi ha coses que no es poden fotografiar: un soroll, una fuita, una màquina que només falla en marxa.',
        photos: 'Fotos', photosHelp: 'De la feina feta. Es poden fer amb la càmera.',
        photosPending: '{count} foto(s) a pujar',
        photoFailed: 'No s’ha pogut pujar «{name}»',
        save: 'Desar el comunicat', saved: 'Comunicat desat',
        empty: 'Encara no hi ha comunicats',
        emptyHint: "Apunta les hores i el material en acabar: és el que després es factura i s'imputa.",
        photo: 'Foto de la feina',
        audio: 'Nota de veu',
      },
      parte: {
        title: 'Comunicat del dia',
        day: 'El dia',
        whereWere: 'Els llocs d’avui',
        whereWereHelp: 'Vénen marcats els que queden per acabar. Treu el que no toqui i omple la resta: les seves hores, les seves fotos i el que s’hi va fer.',
        allStops: 'He estat a tots',
        notHere: 'No hi vaig ser',
        here: 'Hi vaig ser',
        hoursHere: 'Quanta estona hi has estat',
        alreadyHere: 'ja hi ha {hours} apuntades',
        tasks: 'Les tasques d’aquest lloc',
        tasksNone: 'Aquí no hi havia res apuntat. Afegeix el que hagis fet.',
        taskNote: 'Què s’hi va fer',
        taskHours: 'Quanta estona has estat en aquesta tasca',
        taskHoursShort: 'h',
        fromTasks: 'La suma de les tasques',
        taskNotePlaceholder: 'Tallat i recollit; la bardissa del fons es queda…',
        taskPending: 'Segueix pendent',
        foundPending: 'Cal tornar-hi',
        addFound: 'Afegir el que ha sortit aquí',
        closeStop: 'Deixar aquest lloc per acabat',
        savedStop: 'Desat',
        tally: 'sense cap lloc marcat | a un lloc | a {count} llocs',
        tallyTasks: '{count} tasca | {count} tasques',
        tallyPhotos: '{count} foto | {count} fotos',
        nothing: 'Marca almenys un lloc on hagis estat.',
        hoursMissing: 'Falta dir quanta estona has estat a «{name}».',
        failed: 'No s’ha pogut desar el comunicat de «{name}»: {why}',
        savedMany: '{count} comunicats desats',
        savedPartly: 'S’han desat {done} de {count}. Els que falten segueixen aquí: torna a desar.',
        leaveTitle: 'Descartar el comunicat?',
        leaveMessage: 'El que has escrit no s’ha desat enlloc.',
        leaveConfirm: 'Descartar-lo',
        leaveCancel: 'Seguir apuntant',
        closed: 'Aquesta ordre ja està tancada',
        closedHint: 'Un comunicat més demana reobrir-la, i això es fa a propòsit des de la seva fitxa.',
        gone: 'No es troba l’ordre',
        goneHint: 'Potser l’han arxivada. Torna enrere i obre-la des de la llista.',
        dictateClip: 'Nota de veu d’aquesta parada',
        mediaPending: 'Les hores estan desades, però falten {count} foto(s) o vídeo(s) per pujar. Segueixen aquí: torna a desar quan tinguis cobertura.',
        uploading: 'Pujant {n} de {count} · {percent} %',
        draftRestored: 'Hem recuperat el part que tenies a mitges en aquest mòbil.',
        errClosed: 'L’oficina ha tancat aquesta ordre mentre escrivies. El que has escrit segueix aquí: demana que la reobrin i torna a desar.',
        errStopGone: 'Aquest lloc ja no és a l’ordre: l’han tret des de l’oficina. Desmarca’l o pregunta a qui planifica.',
        errHours: 'Les hores han de ser un número entre 0 i 24.',
        errQuantity: 'Una quantitat de material no pot ser negativa.',
        errTooLong: 'Hi ha un text massa llarg. Escurça’l una mica i torna a desar.',
        errOffline: 'Sense cobertura: no s’ha desat. El que has escrit segueix aquí; torna a desar quan tinguis línia.',
      },
      incidents: {
        title: 'Incidències',
        placeholder: 'Què ha passat…',
        add: 'Anotar',
        empty: 'Sense incidències',
        emptyHint: 'Anota-hi el que impedeix o retarda la feina, perquè quedi escrit.',
        resolve: 'Resoldre', resolved: 'Resolta',
        resolvedBy: 'resolta per {who}',
      },
      history: {
        title: 'Historial',
        notePlaceholder: 'Escriu una nota a l’ordre…',
        add: 'Anotar',
        status: 'Estat: {from} → {to}',
        report: 'Comunicat: {what}',
        incident: 'Incidència: {what}',
      },
      confirm: {
        completeTitle: 'Completar l’ordre',
        completeBody: "S'anunciaran {hours} i el material apuntat, que és el que després s'imputa i es factura.",
        removeReportTitle: 'Eliminar el comunicat',
        removeReportBody: 'S’eliminarà el comunicat de {hours} i es recalcularan les hores de l’ordre.',
      },
      completed: 'Ordre completada',
      reopened: 'Ordre reoberta',
      createdToast: 'Ordre {code} creada',
      savedToast: 'Ordre desada',
      search: { subtitle: 'Veure l’ordre' },
      notifications: {
        categories: { late: 'Ordres amb retard', assigned: 'T\'assignen un ordre', breakdown: 'Una avaria al camp', reportAdded: 'Un comunicat nou' },
        assigned: {
          title: 'Ordre nou per a tu',
          body: '{code} · {title}{where}',
        },
        breakdown: {
          title: 'Avaria reportada',
          body: '{asset} · {code}',
          bodyWithWho: '{asset} · {code} — ho reporta {who}',
        },
        // Del campo a la oficina, a los roles del ajuste `reportAlertRoles`.
        reportAdded: {
          title: 'Comunicat nou',
          body: '{code} · {title}',
          bodyWho: '{code} · {title} — l’escriu {who}',
          bodyWhere: '{code} · {where}',
          bodyWhereWho: '{code} · {where} — l’escriu {who}',
        },
        late: {
          title: 'Ordres de treball amb retard',
          body: '{count} ordre(s) han passat la data prevista. La primera: {first}',
        },
      },
    },
    modules: { 'work-orders': 'Ordres de treball' },
    assistant: {
      openers: {
        'work-orders': {
          'new-order': 'Crear una ordre de treball',
          'my-day': 'Què tinc avui?',
          'whats-late': 'Quines ordres porten retard?',
          'log-hours': 'Apunta les hores d’una ordre',
        },
      },
    },
    permissions: {
      workorders: {
        read: 'Veure totes les ordres de treball, no només les pròpies',
        write: 'Planificar: obrir ordres i editar-les',
        report: 'Veure les ordres pròpies i apuntar-hi comunicats, passos i incidències',
        manage: 'Completar, reobrir, anul·lar i arxivar (inclou les plantilles de comunicat)',
        templates: 'Crear i editar les plantilles de comunicat',
        import: "Importar ordres i comunicats d'un altre sistema",
      },
    },
    settings: {
      ns: {
        'work-orders': {
          title: 'Ordres de treball',
          subtitle: 'Com treballa la vostra empresa i què exigeix per donar una ordre per acabada',
          kinds: {
            label: 'Els tipus de feina que feu',
            help: 'Vostres, no nostres. En renombrar-ne un, les ordres que ja el tenien continuen mostrant el nom antic.',
          },
          taskTypes: {
            label: 'Els tipus de tasca que repetiu en una parada',
            help: 'Només si planifiqueu rutes: un jardiner escriu la seva, un instal·lador no la necessita mai. Deixa-ho buit i les tasques són text lliure.',
          },
          createFromPortalRequest: {
            label: 'Obrir una ordre quan un client demana alguna cosa pel portal',
            help: 'Neix en esborrany i sense data, anomenant la sol·licitud de la qual ve. Només fa alguna cosa amb el portal de client instal·lat.',
          },
          portalRequestTypes: {
            label: 'Tipus de sol·licitud del portal que obren una ordre',
            help: 'Els ids tal com estiguin configurats al portal. Buit significa cap.',
          },
          createFromAcceptedQuote: {
            label: 'Obrir una ordre en acceptar-se un pressupost',
            help: 'Neix en esborrany i sense data, i les seves línies es converteixen en els passos. Només fa alguna cosa amb el mòdul de pressupostos instal·lat.',
          },
          allowUnplanned: {
            label: 'Deixar que la colla apunti feina sense ordre',
            help: 'Cada feina obre la seva pròpia ordre, ja tancada i marcada com a no planificada. Desactivat, primer s escriu l ordre a l oficina.',
          },
          requireCustomer: {
            label: 'Exigir client a totes les ordres',
            help: 'Deixa-ho desactivat si també planifiqueu feina vostra: una reparació al taller, un estand.',
          },
          requireHoursToComplete: {
            label: 'Exigir com a mínim un comunicat per completar',
            help: 'Activat, perquè completar sense hores anuncia una feina que no va costar res a qui la imputa.',
          },
          requireChecklistToComplete: {
            label: 'Exigir tots els passos fets per completar',
            help: 'Desactivat per defecte: un pas que avui no aplica bloquejaria l’ordre.',
          },
          requireTargetsToComplete: {
            label: 'Exigir tots els objectius tancats per completar',
            help: 'Desactivat per defecte, pel mateix motiu: una ruta a qui se li ha acabat la tarda es tanca i la resta es replanifica, no es queda oberta.',
          },
          breakdownAlertRoles: {
            label: 'Qui s’assabenta que alguna cosa s’ha avariat al camp',
            help: 'Tan bon punt un comunicat diu que una maquina s’ha aturat. Buit ho apaga.',
          },
          targetTypes: {
            label: 'Sobre què pot anar una ordre',
            help: 'Els tipus de fitxa que surten a «Sobre què». Buit ofereix tot el que l’ERP sap cercar.',
          },
          allowFreeTextTargets: {
            label: 'Permetre escriure a mà sobre què va una ordre',
            help: 'Apagat, una ordre només pot anomenar una fitxa dels tipus de dalt. Amb un sol tipus i això apagat, el planificador deixa de preguntar què és i va directe a la llista.',
          },
          autoCompleteWhenTargetsDone: {
            label: 'Completar l’ordre sola en tancar la darrera parada',
            help: 'Quan un comunicat tanca la darrera parada oberta. Si una regla ho impedeix, l’ordre continua oberta.',
          },
          reportAlertRoles: {
            label: 'Qui s’assabenta que s’ha escrit un comunicat',
            help: 'Amb l’ordre, el lloc i qui l’escriu. Buit ho apaga.',
          },
          equipmentByTaskType: {
            label: 'Què cal portar, per tipus de tasca',
            help: 'Una fila per cada tipus de tasca de W.2: les eines i el material que carrega la colla. Les ordres ho mostren com a «Què cal portar». Un tipus sense res no mostra res.',
            keyLabel: 'Tipus de tasca',
            columns: { tools: 'Eines', materials: 'Material' },
          },
          lateAlertRoles: {
            label: 'Qui s’assabenta que una ordre passa la seva data',
            help: 'Es comprova un cop cada matí.',
          },
        },
      },
    },
    audit: {
      actions: {
        work_orders: {
          created: 'Ordre de treball creada',
          logged: 'Feina apuntada sense ordre',
          updated: 'Ordre modificada',
          planned: 'Ordre planificada',
          in_progress: 'Ordre començada',
          on_hold: 'Ordre aturada',
          completed: 'Ordre completada',
          cancelled: 'Ordre anul·lada',
          draft: 'Ordre tornada a esborrany',
          archived: 'Ordre arxivada',
          restored: 'Ordre retornada a les llistes',
          note_added: 'Nota afegida a l’ordre',
          report_added: 'Comunicat apuntat',
          report_removed: 'Comunicat eliminat',
          template_created: 'Plantilla de comunicat creada',
          template_updated: 'Plantilla de comunicat modificada',
          template_published: 'Plantilla de comunicat publicada',
          template_retired: 'Plantilla de comunicat retirada',
          worksheet_completed: 'Comunicat guiat completat',
          step_added: 'Pas afegit a l’ordre',
          step_done: 'Pas marcat com a fet',
          step_undone: 'Pas desmarcat',
          step_noted: 'Apuntat què es va fer en un pas',
          target_done: 'Parada tancada',
          target_undone: 'Parada reoberta',
          task_added: 'Tasca afegida a una parada',
          task_done: 'Tasca feta en una parada',
          task_undone: 'Tasca desmarcada en una parada',
          task_noted: 'Apuntat què es va fer en una tasca',
          incident_added: 'Incidència anotada',
          incident_resolved: 'Incidència resolta',
          imported: "Ordre importada d'un altre sistema",
        },
      },
    },
  },

  en: {
    'work-orders': {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        today: 'one work order today | {count} work orders today',
        late: 'one late work order | {count} late work orders',
        myDay: 'one work order on your day | {count} work orders on your day',
        myDayLate: '{count} work orders on your day, {late} late',
        replan: 'Replan',
        replanAsk: 'Replan the late work order: propose a new date | Replan the {count} late work orders: propose a new date for each one',
      },
      title: 'Work orders',
      related: {
        ahead: 'Still to do here',
        done: 'What was done',
        cancelled: 'Cancelled',
      },
      doneBanner: {
        title: 'Order completed',
        when: 'Closed on {when}',
      },
      dashboard: {
        logWork: 'Log work without an order',
        tabToday: 'Today',
        tabTomorrow: 'Tomorrow',
        summaryOrders: 'One job | {count} jobs',
        summarySites: 'one site | {count} sites',
        summaryDone: 'one done | {count} done',
        summaryTeam: 'Team: {names}',
        summaryMates: 'With {names}',
        emptyToday: 'No orders for you today',
        emptyTomorrow: 'No orders for you tomorrow',
        next: 'Next',
        open: 'Open orders',
        openHelp: 'How many work orders are still open, in any of their live states.',
        today: 'For today',
        todayHelp: 'How many orders are planned for today, closed ones included.',
        late: 'Orders running late',
        lateHelp: 'How many open orders were dated before today and are still not closed.',
        todayList: 'Today\'s round',
        todayListHelp: 'The orders planned for today, with their customer or address. Stretch the card and more fit.',
        myDay: 'My day',
        myDayHelp: 'The orders assigned to you for today. Everybody sees their own, and stretching the card fits more.',
      },
      subtitle: 'Who goes, when, where — and what happened there',
worksheet: {
        call: {
          title: 'The call',
          pitch: 'Tell it in one go and it writes it down. You check it at the end and save it yourself.',
          start: 'Start the call',
          repeat: 'Say that again',
          hangUp: 'Hang up',
          phase: {
            greeting: 'One moment…',
            asking: 'Asking…',
            listening: 'Listening',
            thinking: 'One moment…',
            handover: 'This one is picked on screen',
            closing: 'Let us go over it',
          },
          greeting: 'Right, let us do today’s report.',
          greetingName: 'Hi {name}, how did the day go?',
          opener: 'Go on, how was today?',
          onScreen: 'You have to pick this one on the screen: {label}. I will wait.',
          stillThere: 'Still there? I will ask again.',
          notUnderstood: 'I did not get the answer out of that. Could you say it again?',
          byHand: 'Better if you write this one yourself.',
          byHandNote: 'I have left «{label}» for you to write.',
          done: 'That is everything. Let me read it back.',
          review: 'Check it on the screen and, if it is right, save it.',
          resume: 'Carry on talking',
          keyboard: 'I would rather type it',
          orTap: 'Or tap it:',
          ackA: 'Okay.',
          ackB: 'Right.',
          ackC: 'Got it.',
          debug: {
            tab: 'Development',
            title: 'Development · the call from the inside',
            clear: 'Clear',
            turns: 'Turns',
            avg: 'average {t}',
            llm: 'EURouter · text',
            stt: 'EURouter · hearing',
            tts: 'Azure · voice',
            fromCache: '{n} from cache',
            total: 'Whole call',
            silence: 'silence wait: {first} ms on short answers, {rest} ms when describing the work',
            heard: 'Heard:',
            says: 'Says:',
            behind: 'behind',
            cache: 'cache',
            rest: 'network · queue · server',
            empty: 'Start the call and every turn shows up here: how long each step takes, how many tokens EURouter uses and how many characters Azure.',
            steps: {
              stt: 'Hear (EURouter)',
              read: 'Place (EURouter)',
              place: 'Look up in the ERP',
              talk: 'Sentence (EURouter)',
              tts: 'Voice (Azure)',
              state: 'State',
              settle: 'Wait for what runs behind',
            },
          },
          tapToPick: 'Pick it on screen',
          onKeyboard: 'Hung up. We can carry on talking whenever you like.',
          dontCall: 'Do not call me on opening',
          incoming: 'Your foreman is calling',
          again: 'Sorry, I didn’t catch that.',
          notFoundPlace: 'I don’t have that one.',
          askLabel: '{label}?',
          whichPlace: 'At {list}?',
          whichOption: '{list}?',
          letsGo: 'Let’s do the report.',
          incomingHint: 'To hear how the day went. Answer when you can talk.',
          answer: 'Answer',
          decline: 'Not now',
          callBack: 'Call the foreman',
          willNotCall: 'It will no longer ring when you open a report on this phone. The call button is still here.',
          or: 'or',
          whichAsk: 'There is more than one {label}. Which of these: {options}?',
          whichOne: '«{words}» could be more than one {label}. Which was it?',
          pickOnScreen: 'Pick it on the screen — they are both there.',
          notFound: 'I cannot find any {label} called «{words}». Have a look for it on the screen.',
        },
        title: 'Write the report',
        said: 'What you have said',
        now: 'Now',
        readBack: 'Let me read it back',
        fromSystem: 'I know this one',
        fromSystemLocked: 'The system fills that one in — you do not have to say it.',
        speak: 'Answer out loud',
        heard: 'I heard:',
        answer: 'Answer',
        skip: 'Skip',
        yes: 'Yes',
        no: 'No',
        pick: 'Pick from the list',
        listen: 'Read it to me',
        confirm: 'Save the report',
        done: 'Report saved',
        none: 'This company has no report template. It is written the usual way.',
        left: 'Nothing left | 1 thing left to say | {n} things left to say',
        files: 'No files | 1 file | {n} files',
        savedHere: 'No signal: kept on the phone and sent when the line comes back.',
        queued: '{n} still to send. They go on their own when the signal is back.',
        stillQueued: '{n} answers still to send. Find signal before confirming the report.',
      },
      help: {
        title: 'How the templated report works',
        subtitle: 'Leave this tab open while you build your own',
        lead: 'You write the QUESTIONS you want answered when a service closes. The assistant asks the worker one at a time, in the order you set, and will not let the report close until they are all answered.',
        steps: {
          clone: {
            title: 'Start from an example',
            body: 'On «Worksheet templates» there is a «Clone an example» dropdown. The first one, «Full report», already carries the six questions almost every field company asks: what you did, where, with whom, whether anything went wrong, what material you used and which customer it goes to. It clones retired, so you can play with it without it showing up on anybody’s phone.',
            link: 'Go to the templates',
          },
          write: {
            title: 'Write each question',
            body: 'Every question is defined by four things, and only four:',
            b1: 'WHAT IS ASKED — the short name, like «Where it was».',
            b2: 'WHAT THEY ANSWER — from a closed list of eight. It is what lets the assistant understand «one and a half» or refuse «eighteen thousand», and what makes this a report rather than a voice note.',
            b3: 'HOW HARD IT INSISTS — required (will not close without it), recommended (asked once even if nobody mentions it, and can be left blank) or optional (only if it comes up).',
            b4: 'HOW THE ASSISTANT ASKS — the sentence in your own words. That is literally what it will say.',
          },
          preview: {
            title: 'See the conversation before publishing',
            body: 'On the right, as you write, the exact conversation appears: what it will ask, in what order, what it will not ask because the system already knows it, and what happens on confirming. Worked out by the same engine that runs it.',
          },
          publish: {
            title: 'Publish it',
            body: 'Until you press «Publish» it reaches no phone. And «Retire» takes it out without deleting it: the reports already written name it, and deleting it would leave a history nobody can read.',
          },
          fill: {
            title: 'And the worker fills it in out loud',
            body: 'They get it on the order and on every stop of «My day», next to the usual form. And there is nothing to press: the screen rings as it opens. Three things worth knowing:',
            b1: 'It does not ask what is already known: the hours come from their clock-in and the customer from the order.',
            b2: 'They can answer by speaking or by tapping, always both. Noise, or no microphone, and they type. The site or the job is said out loud too: when two of them sound alike, it asks which.',
            b3: 'Before closing it reads everything back for confirmation, and with no signal the conversation does not stop: it is kept on the phone and sent when the line is back.',
          },
        },
        typesTitle: 'The eight kinds of answer',
        typesBody: 'The list is closed on purpose. The assistant can only run the conversation because it knows what each answer is; with free text there would be nothing to check and nothing to take off a shelf.',
        types: {
          number_unit: 'A number with its unit and its range: hours, kilos, bags.',
          boolean: 'Yes or no.',
          choice: 'One of the options you write.',
          catalogue: 'One from another module’s list: a site, a worker, a material, a customer. It is what lets the place stay linked and the material come off the shelf.',
          photo: 'One or several, with a before-and-after option.',
          signature: 'The customer’s signature.',
          text: 'A short note. The only free text.',
          datetime: 'A date or a time.',
        },
        offTitle: 'How to turn these tutorials off',
        offBody: 'The «?» marks appear because tutorial mode is on in your profile. It is yours and not the company’s: turning it off takes it from nobody else.',
        offLink: 'Go to my profile',
      },
            worksheets: {
        title: 'Worksheet templates',
        subtitle: 'What gets written down when a service closes, and in what order it is asked',
        listTitle: 'This company’s templates',
        table: { name: 'Template', sector: 'Sector', fields: 'Questions', status: 'Status' },
        empty: 'No templates yet. Clone one of the examples and edit it.',
        emptyHint: 'A template decides what the assistant asks the worker when a service closes.',
        newTitle: 'New worksheet template',
        editTitle: 'Edit the template',
        fieldsCount: 'No questions | 1 question | {n} questions',
        active: 'Published',
        inactive: 'Retired',
        activeHint: 'Only published ones reach the phone.',
        actions: {
          new: 'New template',
          clone: 'Clone an example',
          duplicate: 'Duplicate',
          publish: 'Publish',
          retire: 'Retire',
          preview: 'See the conversation',
          addField: 'Add a question',
          save: 'Save the template',
        },
        form: {
          name: 'Name',
          namePlaceholder: 'Maintenance worksheet',
          description: 'What it is for',
          descriptionPlaceholder: 'When this template is used',
          sector: 'Sector',
          sectorPlaceholder: 'gardening, maintenance…',
          kinds: 'Only for these kinds of work',
          kindsHelp: 'Empty means «for all of them». Checked against the kinds of work in Settings.',
        kindsAdd: 'Add a kind of work…',
          roles: 'Only for these roles',
          rolesHelp: 'Empty means «for everybody».',
          fields: 'The questions, in the order they are asked',
          fieldsHelp: 'The assistant works down the list, and only asks what is missing: what it already knows, or was already told, it does not ask again.',
          nameRequired: 'The template needs a name.',
          fieldsRequired: 'A template with no questions asks nothing.',
          saved: 'Template saved',
          duplicateName: 'Name for the copy',
        },
        field: {
          label: 'What is asked',
          labelPlaceholder: 'Bags of fertiliser',
          type: 'What they answer',
          requirement: 'How hard it insists',
          source: 'Where it comes from',
          autoSource: 'Who fills it in',
          unit: 'Unit',
          unitPlaceholder: 'h, kg, bags',
          options: 'Options',
          optionsHelp: 'One per line. At least two are needed.',
          catalogue: 'Catalogue',
          catalogueHelp: 'Which list from another module it is picked from.',
          min: 'Smallest',
          max: 'Largest',
          maxLength: 'Longest',
          multiple: 'Several allowed',
          beforeAfter: 'Before and after',
          hint: 'How the assistant asks for it',
          hintPlaceholder: 'How many bags of fertiliser did you use?',
          hintHelp: 'In your own words. Left blank, it is asked with the label.',
          binds: 'Stored in',
          bindsHelp: 'Tying it to a box the parte already has is what makes the rest of the ERP see it.',
          postAction: 'When the parte closes',
          up: 'Move up',
          down: 'Move down',
          remove: 'Remove the question',
        },
        types: {
          number_unit: 'Number with a unit',
          boolean: 'Yes or no',
          choice: 'List of options',
          catalogue: 'From another module’s catalogue',
          photo: 'Photos or video',
          signature: 'Signature',
          text: 'Short note',
          datetime: 'Date and time',
        },
        requirements: {
          required: 'Required',
          recommended: 'Recommended',
          optional: 'Optional',
        },
        requirementHelp: {
          required: 'The parte cannot close without it.',
          recommended: 'The assistant asks once even if nobody mentions it, and it can be left blank.',
          optional: 'Only filled in if the worker mentions it.',
        },
        sources: {
          manual: 'The worker says it',
          auto: 'The system fills it in',
        },
        autoSources: {
          worker: 'Who did it',
          now: 'The date and time',
          service: 'The service of the day',
          customer: 'The customer',
          clocked_hours: 'The hours from the clock-in',
          location: 'Where it was',
        },
        bindings: {
          none: 'Only on the template',
          hours: 'The parte’s hours',
          description: 'What was done',
          workDate: 'The day of the work',
          materials: 'The material used',
          photos: 'The photos',
          targetDone: 'Stop finished',
        },
        postActions: {
          none: 'Nothing',
          consume_stock: 'Take it off the shelf',
          raise_incident: 'Raise an incident',
          complete_service: 'Mark the service done',
        },
        preview: {
          title: 'The conversation it will produce',
          help: 'Worked out by the same engine that runs it: this is what will happen to the worker.',
          prefilled: 'Not asked, already known',
          asks: 'What the assistant will ask, in this order',
          onlyIfMentioned: 'Only if mentioned',
          afterwards: 'On confirming',
          blocking: 'Cannot close without these',
          nothing: 'Nothing',
          opening: 'Tell me what you did.',
          closing: 'Let me read it back to you.',
        },
        seeds: {
          complete: {
            name: 'Full report',
            description: 'What was done, where, with whom, what happened and what was used.',
            fields: {
              hours: {
                label: 'Hours',
                hint: 'How long were you there?',
              },
              tasks: {
                label: 'What you did',
                hint: 'Tell me what you did today.',
              },
              where: {
                label: 'Where it was',
                hint: 'Where did you do it?',
              },
              crew: {
                label: 'Who was with you',
                hint: 'Who went with you?',
              },
              issues: {
                label: 'Issues',
                hint: 'Was there any issue or any problem?',
              },
              material: {
                label: 'Material used',
                hint: 'Did you use any material?',
              },
              customer: {
                label: 'Which customer',
                hint: 'Which customer does this go to?',
              },
              photo: {
                label: 'Photos',
                hint: 'Take me a photo of how it turned out?',
              },
            },
          },
          gardening: {
            name: 'Gardening worksheet',
            description: 'A round of garden maintenance.',
            fields: {
              hours: {
                label: 'Hours',
                hint: 'How long were you there?',
              },
              done: {
                label: 'What was done',
                hint: 'Tell me what you did in the garden.',
              },
              mown: {
                label: 'Mown',
                hint: 'Did you mow?',
              },
              fertiliser: {
                label: 'Bags of fertiliser',
                hint: 'How many bags of fertiliser did you use?',
              },
              material: {
                label: 'Material used',
                hint: 'Did you use anything from the store?',
              },
              photo: {
                label: 'Photos',
                hint: 'Take me a photo of how it turned out.',
              },
              issues: {
                label: 'Issues',
                hint: 'Anything wrong? Something broken, something to come back to.',
              },
            },
          },
          maintenance: {
            name: 'Maintenance worksheet',
            description: 'A job on a machine or an installation.',
            fields: {
              hours: {
                label: 'Hours',
                hint: 'How long were you there?',
              },
              kind: {
                label: 'Kind of job',
                hint: 'Was it preventive, corrective or an inspection?',
                options: 'Preventive · Corrective · Inspection',
              },
              done: {
                label: 'What was done',
                hint: 'Tell me what you did on the machine.',
              },
              stopped: {
                label: 'The machine was stopped',
                hint: 'Was it stopped?',
              },
              parts: {
                label: 'Spare parts',
                hint: 'Which spares did you fit?',
              },
              reading: {
                label: 'Pressure reading',
                hint: 'What pressure does it read?',
              },
              issues: {
                label: 'Issues',
                hint: 'Anything to write down?',
              },
            },
          },
          electrician: {
            name: 'Electrician worksheet',
            description: 'An electrical job, signed off by the customer.',
            fields: {
              hours: {
                label: 'Hours',
                hint: 'How long were you there?',
              },
              done: {
                label: 'What was done',
                hint: 'Tell me what you did.',
              },
              measured: {
                label: 'Voltage measured',
                hint: 'What voltage did you measure?',
              },
              earthing: {
                label: 'Earthing checked',
                hint: 'Did you check the earthing?',
              },
              material: {
                label: 'Material used',
                hint: 'What material did you fit?',
              },
              panel: {
                label: 'Photo of the panel',
                hint: 'Take me a photo of the panel.',
              },
              signature: {
                label: 'Customer signature',
                hint: 'Get the customer to sign.',
              },
              issues: {
                label: 'Issues',
                hint: 'Anything wrong?',
              },
            },
          },
        },
      },
      detailTitle: 'Work order',
      newTitle: 'New work order',
      editTitle: 'Edit the order',
      actions: {
        new: 'New order', edit: 'Edit', addReport: 'Add report',
        guidedReport: 'Say it out loud',
        complete: 'Complete', reopen: 'Reopen', cancel: 'Cancel',
        save: 'Save', remove: 'Remove',
      },
      status: {
        draft: 'Draft', planned: 'Planned', in_progress: 'In progress',
        on_hold: 'On hold', completed: 'Completed', cancelled: 'Cancelled',
      },
      priority: { low: 'Low', medium: 'Normal', high: 'High', critical: 'Critical' },
      severity: { low: 'Minor', medium: 'Medium', high: 'Serious', critical: 'Critical' },
      day: { today: 'Today', yesterday: 'Yesterday', tomorrow: 'Tomorrow' },
      tabs: {
        overview: 'Overview', reports: 'Reports', incidents: 'Incidents on this order', history: 'History',
      },
      // ── Writing the order, with help ──────────────────────────────
      ai: {
        write: 'Draft it',
        writeHint: 'Writes the body of the order from whatever is there',
        summarise: 'Summarise into the title',
        summariseHint: 'Takes one line out of the description and makes it the title',
        task: 'Write the task',
        taskHint: 'Writes one task from whatever is in the box',
        working: 'Writing…',
      },
      // ── What the order is about, and its planner ──────────────────
      // La tarjeta que se confirma desde el asistente (create_order) y que
      // se ve durante la llamada mientras la orden se va armando.
      assistant: {
        // Las respuestas de las consultas que contestan las reglas (queries.js).
        query: {
          agenda: {
            self: 'You have {count} orders on {when}: {list}.',
            selfNone: 'You have no orders on {when}.',
            other: '{who} has {count} orders on {when}: {list}.',
            otherNone: '{who} has no orders on {when}.',
          },
          late: {
            some: '{count} orders are late: {list}.',
            none: 'No order is late.',
          },
        },
        order: {
          action: 'Open a work order',
          what: 'What',
          when: 'When',
          draft: 'Draft, no date',
          customer: 'Customer',
          who: 'Who goes',
          noTasks: 'No tasks',
          more: '{count} more places',
          outcome: 'Order {code} created',
          fromPlaces: 'Taken from its places',
          nobody: 'Nobody yet',
          noDateNote: 'No day: it will be saved as a draft nobody is sent to',
          noCrewNote: 'Nobody is assigned yet',
          noPlaceNote: 'It names no place',
          noTasksNote: 'No tasks at: {places}',
          severalPlaces: '«{name}» matches several: {options}. Pick one by saying or typing it.',
          unknownPlace: '«{name}» is not a known place: it goes as written',
          unknownPerson: '«{name}» was not found among the people',
          placeN: 'Place {n}',
          tasks: 'Tasks',
          noPlace: 'No place yet',
          where: 'Where',
          priority: 'Priority',
          notes: 'Notes',
          noNotes: 'Nothing',
          dayDoubtNote: 'The day was not clear: pick it',
          from: 'From',
          sourceUnreadable: 'That plan or incident cannot be read: the order cannot point at it',
          unknownCustomer: '«{name}» is not a known customer',
          timeNote: 'Time: {time}',
          readPlace: 'Order at {place}',
          readDay: '{day}',
          readDayTime: '{day} at {time}',
          readTasks: 'To do: {tasks}',
          readCrew: '{who} are going',
          readOne: '{who} is going',
          asSaid: '«{said}», as said',
          internal: 'Internal: no place or customer',
        },
        change: {
          order: 'Order',
          noOrder: 'Which order?',
          kind: 'Work order',
          unknownOrder: 'No open order matches «{said}»',
          severalOrders: '«{said}» matches several orders: pick one',
          until: 'Until now',
          closedOrder: 'This order is already closed',
          summary: {
            reschedule: 'Move {order} to {day} {time}',
            assign: '{order}: {who} goes',
            priority: {
              low: '{order}: low priority', medium: '{order}: medium priority', high: '{order}: high priority', critical: '{order}: urgent', other: '{order}: change the priority',
            },
            status: {
              draft: '{order}: back to draft', planned: '{order}: plan it', in_progress: '{order}: start it', on_hold: '{order}: pause it',
              completed: '{order}: complete it', cancelled: '{order}: cancel it', other: '{order}: change its status',
            },
          },
          rescheduleAction: 'Move an order to another day',
          newDay: 'New day',
          noDay: 'Pick a day',
          pickDay: 'Pick the new day',
          time: 'Time',
          noTime: 'No time',
          timeKept: '{time} (as before)',
          badTime: 'The time is written HH:MM',
          noDayBefore: 'No day',
          draftPlanned: 'It is a draft: with a day it becomes planned and reaches the crew',
          readMove: '{code}, to {day}',
          readMoveTime: '{code}, to {day} at {time}',
          rescheduled: '{code} moved to {day}',
          assignAction: 'Change who goes',
          who: 'Who goes',
          nobody: 'Nobody',
          team: 'Team',
          unknownPerson: '«{name}» was not found among the people',
          unknownTeam: 'No team is called «{name}»',
          nobodyNote: 'Nobody would go: pick who',
          readAssign: '{code}: {who} goes',
          assigned: '{code}: {who} goes',
          priorityAction: 'Change the priority',
          priority: 'Priority',
          noPriority: 'Pick one',
          readPriority: {
            low: '{code}, low priority', medium: '{code}, medium priority', high: '{code}, high priority', critical: '{code}, urgent',
          },
          prioritised: {
            low: '{code}: low priority', medium: '{code}: medium priority', high: '{code}: high priority', critical: '{code}: urgent', other: '{code}: priority changed',
          },
          statusAction: 'Change the status of an order',
          status: 'Status',
          noStatus: 'Pick one',
          reason: 'Why',
          noReason: 'Not said',
          badTransition: 'From the status it is in, the order cannot move to that one',
          needsManage: 'Completing or cancelling needs the permission to manage orders',
          needsReason: 'Say why it is cancelled',
          completeNote: 'Completing it sends its hours on to whoever bills them',
          readStatus: {
            draft: '{code}, back to draft', planned: '{code}, planned', in_progress: 'Start {code}', on_hold: 'Pause {code}', completed: 'Complete {code}', cancelled: 'Cancel {code}',
          },
          outcomeStatus: {
            draft: '{code} back to draft', planned: '{code} planned', in_progress: '{code} started', on_hold: '{code} paused',
            completed: '{code} completed', cancelled: '{code} cancelled', other: '{code}: status changed',
          },
        },
      },
      // Lo que la llamada pregunta mientras falta algo de una orden.
      voice: {
        askPlace: 'Where is it?',
        askReportOrder: 'Which order is the report for?',
        askReportHours: 'How many hours?',
        askPlaceAgain: 'I cannot find «{said}». What is the place called?',
        whichPlace: '«{said}» could be {options} or {last}. Which one?',
        thisPlace: 'I cannot find «{said}» as such. Do you mean {last}?',
        askTasks: 'What has to be done?',
        askDay: 'Which day?',
        askPeople: 'Who is going?',
        askPeopleAgain: 'I cannot find «{name}». Who is going?',
        askOrder: 'Which order?',
        askOrderAgain: 'I cannot find the order «{said}». Which one is it?',
        whichOrder: '«{said}» could be {options} or {last}. Which one?',
        thisOrder: 'Do you mean {last}?',
        askNewDay: 'To which day?',
        askTeamAgain: 'I cannot find the team «{name}». Who is going?',
        askPriority: 'Which priority: low, medium, high or urgent?',
        askStatus: 'Start it, pause it, complete it or cancel it?',
        askCancelReason: 'Why is it being cancelled?',
        whichTask: '«{said}» is not one of your tasks. Is it {options} or {last}? If not, say «as it is».',
        thisTask: '«{said}» is not one of your tasks. Is it {last}? If not, say «as it is».',
      },
      targets: {
        tab: 'Targets',
        title: 'What it is about',
        what: 'What kind',
        freeText: 'Type it by hand',
        name: 'Name',
        namePlaceholder: 'Press 3, Line 2, the Puig house…',
        search: 'Search',
        searchPlaceholder: 'Type to search…',
        wherePlaceholder: 'Where: bay, street, floor…',
        add: 'Add',
        remove: 'Take it off the order',
        notes: 'Notes',
        emptyHint: 'An order with nothing here is a single job, which is fine too.',
        chosen: 'chosen',
        pickManySearch: 'Narrow it down: Puigcerdà, Alp, bay 2…',
        pickManyLoading: 'Searching…',
        pickManyNone: 'Nothing comes up. Try another word.',
        pickManyAll: 'Mark the {count} shown',
        group: 'Folder',
        groupAll: 'All',
        pickGroupAll: 'Add the {count} in «{group}»',
        pickManyAlready: '{count} already on the order',
        pendingHere: 'What was already pending here',
        pendingHint: 'Written down another day at this place. What you tick joins the order, '
          + 'and completing it closes it where it was.',
        pendingLoading: 'Looking for what is pending…',
        unit: 'Target',
        units: 'targets',
        tasks: 'tasks',
        count: '{count} targets',
        progress: '{done}/{total} targets',
        taskProgress: '{done}/{total} tasks',
        column: 'Targets',
        done: 'Done',
        pending: 'Pending',
        markDone: 'Mark it done',
        markPending: 'Leave it pending again',
        doneBy: 'by {who}',
        noTasks: 'No tasks written down here',
      },
      planner: {
        removeTask: 'Remove “{task}”',
        stepWho: 'When and about what',
        stepWork: 'The job',
        stepDetail: 'The detail',
        stepCustomer: 'Who for',
        stepWhen: 'When',
        stepCrew: 'Who goes',
        stepTargets: 'About what',
        stepPlaces: 'The places',
        stepTasks: 'The tasks',
        stepSteps: 'The steps',
        back: 'Back',
        lead: {
          dictate: 'Say it out loud and the rest fills itself in. You can skip this.',
          when: 'What day does the crew go? Leave it blank if you do not know yet.',
          targets: 'Where does the work happen? One place, or the fifty of a round.',
          tasks: 'What has to be done at each? Whatever repeats, add it to all at once.',
          work: 'In one line: what job this is.',
          detail: 'What the crew needs to know on arrival.',
          customer: 'Who it is for, and which job it belongs to, if any.',
          crew: 'Who goes. With a crew, its people are copied in.',
          steps: 'The steps ticked off as they are done. Only when opening it.',
        },
        create: 'Open the order',
        next: 'Continue',
        sectionWhen: 'When',
        sectionWho: 'Who goes',
        sectionOrder: 'The work order',
        sectionNotes: 'Notes for whoever goes',
        descriptionHelp: 'What has to be done, in as much detail as it takes. This is what gets read on site.',
        descriptionPlaceholder: 'What is happening, what has to be done, what to watch out for…',
        notesHelp: 'Everything around the work: access, keys, hours, who to ask for.',
        notesPlaceholder: 'Call before going, the key is at the porter’s desk…',
        inTwoDays: 'In two days',
        nextMonday: 'Next Mon.',
        crewAdd: 'Add somebody else…',
        crewNone: 'Only whoever the order is assigned to',
        moveUp: 'Move up',
        moveDown: 'Move down',
        viewTargets: 'One by one',
        viewTasks: 'By task',
        viewSummary: 'Summary',
        bulkTitle: 'One task on all of them',
        bulkApply: 'Add to all {count}',
        taskTypeAny: 'Type of task…',
        taskTextPlaceholder: 'What has to be done…',
        addTask: 'Add task',
        noTasks: 'No tasks yet',
        noTasksYet: 'No task written down yet',
        atTargets: 'on {count}',
        titleSuggested: 'If left empty: «{title}»',
        titleMore: '{names} and {count} more',
        bringPending: 'Bring everything pending',
        bringPendingHint: 'What was noted another day at each place goes into the order. Remove what is not needed place by place.',
        bringingPending: 'Checking what is pending at {count} places…',
        broughtPending: '{count} pending items brought into the order',
        noPending: 'Nothing was pending at these places',
        addTasksHere: 'Add tasks',
        noTaskTypes: 'No task types yet: every task is typed by hand. If you always repeat the same ones (mowing, pruning, watering…), write them once in Settings and they will show here as a dropdown.',
        noTaskTypesLink: 'Write them in Settings (W.2)',
      },
      equipment: {
        title: 'What to take',
        tools: 'Tools',
        materials: 'Materials',
        forTasks: 'for {types}',
        hint: 'Worked out from the task types of the round (Settings, W.15).',
      },
      myDay: {
        title: 'My day',
        subtitle: 'What you have today, and what you have logged',
        today: 'Today',
        previous: 'Previous day',
        next: 'Next day',
        targets: '{count} targets',
        late: '{count} late',
        incidents: '{count} with an incident',
        empty: 'Nothing assigned to you today',
        emptyHint: 'If you expected work, ask whoever plans: it may not have a date yet.',
        failed: 'Your day could not be loaded',
        failedHint: 'No signal, or the server is not answering. Nothing is lost: try again in a moment.',
        retry: 'Try again',
      },
      dictate: {
        step: 'Dictate it',
        title: 'Dictate the order',
        subtitle: 'Say it and the form fills itself. You review it before saving: nothing is saved here.',
        clip: 'The recording',
        read: 'Read it',
        heard: 'What was heard',
        silence: 'Nothing was audible. Record again, closer to the microphone.',
        which: 'Which of these is «{words}»?',
        notFound: 'I found nothing called «{words}». Type it in if you need it.',
      },
      board: { title: 'Open by kind of work' },
      filters: {
        oneCustomer: 'One customer only',

        search: 'Search', searchPlaceholder: 'Number, job, customer, place…',
        status: 'Status', allStatuses: 'All', date: 'Day',
        team: 'Crew', allTeams: 'All',
        onlyOpen: 'Only open', onlyLate: 'Only late', onlyUnplanned: 'Only logged work',
      },
      table: {
        code: 'Number', title: 'Job', planned: 'Planned', assignee: 'Assigned to',
        progress: 'Steps', hours: 'Hours', status: 'Status', crewMore: 'more',
      },
      kpi: { today: 'For today', open: 'Open', late: 'Late', hours: 'Completed hours' },
      empty: {
        none: 'No work orders yet',
        noneHint: 'Open the first one, or accept a quote and let it open by itself.',
        filtered: 'No results',
        filteredHint: 'Try removing a filter or searching for something else.',
      },
      lateBy: '{days} days late',
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      file: { title: 'Details' },
      fields: {
        code: 'Number', title: 'Job', kind: 'Kind of work', priority: 'Priority',
        customer: 'Customer', project: 'Project', location: 'Place', description: 'Description',
        plannedDate: 'Planned date', assignee: 'Assigned to', crew: 'Who else goes',
        team: 'Crew',
        quote: 'Quote', hours: 'Hours reported',
        source: { incident: 'From the incident', maintenance: 'From the maintenance plan' },
      },
      form: {
        what: 'The job', who: 'Who and when',
        titlePlaceholder: 'Replace the pump on circuit 2…',
        customerPlaceholder: 'Company or private customer',
        projectPlaceholder: 'Which project it belongs to',
        projectHelp: "If you set it, this order's hours and materials go to that job's cost ledger.",
        locationPlaceholder: 'Where to go',
        plannedHelp: 'Setting a date makes the order planned and puts it in the day of whoever goes.',
        nobody: 'Unassigned',
        crewHelp: 'Picked one at a time from the list of people. They all see the order in their day.',
        noTeam: 'No crew',
        teamHelp: 'Picking one copies its people below, and its lead as the assignee if nobody is set. '
          + 'A copy: whoever goes stays written down even if the crew changes later.',
        teamMembers: '{count} people',
        noPeople: 'No active people to assign.',
        checklistTitle: 'Steps',
        checklistHelp: 'Only on create: afterwards they are edited on the file, where each step keeps who ticked it.',
        titleRequired: 'The order needs a job description',
      },
      checklist: {
        title: 'Steps',
        empty: 'No steps',
        emptyHint: 'Add the ones that repeat on this kind of work.',
        add: 'Add step',
        addPlaceholder: 'Check circuit pressure…',
      },
      // ── Logged work: a job nobody planned ───────────────────────────
      log: {
        unlinked: 'The job is saved, but not linked to {names}: your account cannot see those records. Tell the office so they can link it.',
        action: 'Log work',
        title: 'Log work you did',
        hint: 'Work you did with no order behind it. It opens its own order, already closed, in your name.',
        where: 'Where it was',
        whereHelp: 'A site, a machine, a van — whatever this instance can name.',
        whereRequired: 'Say where it was, or what you did',
        freeText: 'Type it in',
        search: 'Which one',
        searchPlaceholder: 'Start typing…',
        name: 'What or where',
        namePlaceholder: 'Pine Gardens, Press 3…',
        kind: 'Kind of work',
        hours: 'How long it took',
        workDate: 'Which day',
        workDateHelp: 'The day you worked, not today. Saturday can be written up on Monday.',
        description: 'What you did',
        descriptionPlaceholder: 'Trimmed the front hedges and checked the irrigation…',
        customerHelp: 'This instance requires a customer on every order.',
        tasks: 'Tasks done', addTask: 'Add task',
        addStop: 'Add another place',
        quick: {
          where: 'Where have you been?',
          whereHelp: 'Tap the places. Today\'s come first.',
          today: 'today',
          noSuggestions: 'No recent places: add one.',
          otherSite: 'Another place',
          pickFirst: 'Tap where you have been',
          hours: 'How long?',
          otherHours: 'Other',
          what: 'What did you do?',
          photos: 'Photos',
          issue: 'Something broken',
          issueLabel: 'What is wrong',
          issuePlaceholder: 'The irrigation in sector 2 is leaking…',
          tell: 'Tell it',
          more: 'More details',
          less: 'Fewer details',
          day: 'The day',
          todayPill: 'Today',
          yesterday: 'Yesterday',
          otherDay: 'Another day',
          with: 'Who was with you?',
          alone: 'Alone',
          dayMore: 'Type, project and customer',
          clocked: 'of {hours} clocked',
          send: 'Send',
          issueSaved: 'Incident logged at {name}',
          issueFailed: 'Could not log the incident at {name}',
        },
        dropStop: 'Remove this place',
        stopBlank: 'Unnamed place',
        whereType: 'Kind of place',
        what: 'The job',
        noTasks: 'Nothing written down yet.',
        noMaterials: 'Nothing was used.',
        pendingTitle: 'What was outstanding here',
        pendingHint: 'Tick what you did: it joins the job and is marked done wherever it was written down.',
        pendingFailed: 'The job is saved, but «{name}» could not be closed and is still outstanding.',
        leaveTitle: 'Discard this job?',
        leaveMessage: 'What you have written has not been saved anywhere.',
        leaveConfirm: 'Discard it',
        leaveCancel: 'Keep writing',
        taskPlaceholder: 'Hedge trimmed, sprinkler replaced…',
        dictate: {
          title: 'Say it and the form fills itself',
          subtitle: 'What you did, where, how long you were there and what you used. You check it before saving.',
          clip: 'Voice note for the report',
          read: 'Fill it in from what I said',
          heard: 'What was heard',
          silence: 'Nothing was heard. Record it again.',
          which: 'Which of these is “{words}”?',
          notFound: 'I could not find “{words}”. Pick it below.',
          noHours: 'You did not say how long you were there. Put it in yourself: I will not guess it.',
          keeps: 'Nothing is saved yet. Check the form and press log.',
          clipFailed: 'The work is logged, but the voice note could not be attached.',
        },
        time15: '+15 min',
        time30: '+30 min',
        time60: '+1 h',
        timeClear: 'Clear',
        crew: 'Who was with you',
        crewHelp: 'Their hours go on this report. It does not assign anybody any work.',
        crewAdd: 'Add somebody',
        crewPlaceholder: 'Pick a colleague…',
        crewHours: 'Hours for {who}',
        crewNobody: 'Nobody to add.',
        short: 'Log work',
        save: 'Log it',
        saved: 'Logged as {code}',
        savedOpen: 'Logged as {code}. It stays open: the office has to close it.',
      },
      unplanned: {
        badge: 'Unplanned',
        title: 'Added on site by {name}',
        origin: 'Logged',
        originHelp: 'Nobody planned it: whoever did the work wrote it down.',
        addTaskPlaceholder: 'What needed doing and was not on the list…',
      },
      imported: {
        badge: 'Imported',
        help: 'This order was brought in from {system}: its dates, who ticked each task and its reports are the ones kept there.',
        from: 'Imported from',
        createdThere: 'created there on {date}',
      },
      report: {
        breakdown: {
          toggle: 'Something broke',
          hint: 'Write down the hour it stopped. It is what nobody remembers a week later, and it is where MTBF and MTTR come from.',
          which: 'What stopped',
          stoppedAt: 'Stopped at',
          stoppedAtHint: 'The real hour, not the hour of the report',
          restartedAt: 'Back at',
          restartedAtHint: 'Empty = still down',
          didStop: 'Production stopped',
          what: 'What happened',
          whatHint: 'In your own words. Maintenance codes the kind and the cause later, against their catalogue',
          whatPlaceholder: 'The hose on the closing cylinder burst…',
        },
        title: 'Report on {code}',
        hours: 'Hours', hoursPlaceholder: 'e.g. 6.5',
        hoursRequired: 'A report needs the hours worked',
        workDate: 'Day of the work',
        workDateHelp: 'The day the work happened, not today.',
        description: 'What was done',
        descriptionPlaceholder: 'Pump replaced and circuit bled…',
        materials: 'Materials used', addLine: 'Add line',
        measured: 'Measured today', addMeasure: 'Add a measurement',
        itemPlaceholder: 'Find the item',
        measuredHint: 'What was done TODAY, not the running total. The item adds it up on its own.',
        noMaterials: 'No materials. Add lines if anything was used.',
        materialPlaceholder: 'Cable, pipe, part…',
        qty: 'Qty', unit: 'Unit', price: 'Price',
        media: 'Photos and video',
        mediaHelp: 'Of the finished work. You can take them right here.',
        mediaHint: 'Some things cannot be photographed: a noise, a leak, a machine that only misbehaves while it runs.',
        photos: 'Photos', photosHelp: 'Of the finished work. The camera works too.',
        photosPending: '{count} photo(s) to upload',
        photoFailed: 'Could not upload “{name}”',
        save: 'Save report', saved: 'Report saved',
        empty: 'No reports yet',
        emptyHint: 'Log the hours and materials when you finish: that is what gets billed and costed.',
        photo: 'Photo of the work',
        audio: 'Voice note',
      },
      parte: {
        title: 'The day’s report',
        day: 'The day',
        whereWere: 'Today’s stops',
        whereWereHelp: 'The places still to finish come ticked already. Untick the one that does not apply and fill in the rest: its hours, its photos and what was done there.',
        allStops: 'I went to all of them',
        notHere: 'Was not here',
        here: 'Was here',
        hoursHere: 'How long you were here',
        alreadyHere: '{hours} already logged',
        tasks: 'The tasks at this place',
        tasksNone: 'Nothing was listed here. Add whatever you did.',
        taskNote: 'What was done',
        taskHours: 'How long you spent on this task',
        taskHoursShort: 'h',
        fromTasks: 'The tasks add up to this',
        taskNotePlaceholder: 'Cut and cleared; the back hedge left for next time…',
        taskPending: 'Still pending',
        foundPending: 'Have to come back',
        addFound: 'Add what came up here',
        closeStop: 'Mark this place finished',
        savedStop: 'Saved',
        tally: 'no place ticked yet | at one place | at {count} places',
        tallyTasks: '{count} task | {count} tasks',
        tallyPhotos: '{count} photo | {count} photos',
        nothing: 'Tick at least one place you have been to.',
        hoursMissing: 'Say how long you were at “{name}”.',
        failed: 'Could not save the report for “{name}”: {why}',
        savedMany: '{count} reports saved',
        savedPartly: '{done} of {count} saved. The rest are still here: press save again.',
        leaveTitle: 'Discard the report?',
        leaveMessage: 'Nothing you have written has been saved anywhere.',
        leaveConfirm: 'Discard it',
        leaveCancel: 'Keep writing',
        closed: 'This order is already closed',
        closedHint: 'One more report means reopening it, and that is done on purpose from its file.',
        gone: 'The order cannot be found',
        goneHint: 'It may have been archived. Go back and open it from the list.',
        dictateClip: 'Voice note for this stop',
        mediaPending: 'The hours are saved, but {count} photo(s) or video(s) are still to upload. They are still here: press save again once you have signal.',
        uploading: 'Uploading {n} of {count} · {percent} %',
        draftRestored: 'We recovered the report you had half written on this phone.',
        errClosed: 'The office closed this order while you were writing. What you wrote is still here: ask them to reopen it and press save again.',
        errStopGone: 'That place is no longer on the order: the office removed it. Untick it or ask whoever plans.',
        errHours: 'Hours must be a number between 0 and 24.',
        errQuantity: 'A material quantity cannot be negative.',
        errTooLong: 'One of the texts is too long. Shorten it a little and press save again.',
        errOffline: 'No signal: nothing was saved. What you wrote is still here; press save again once you have signal.',
      },
      incidents: {
        title: 'Incidents',
        placeholder: 'What happened…',
        add: 'Log',
        empty: 'No incidents',
        emptyHint: 'Log whatever blocks or delays the work, so it is written down.',
        resolve: 'Resolve', resolved: 'Resolved',
        resolvedBy: 'resolved by {who}',
      },
      history: {
        title: 'History',
        notePlaceholder: 'Write a note on the order…',
        add: 'Add note',
        status: 'Status: {from} → {to}',
        report: 'Report: {what}',
        incident: 'Incident: {what}',
      },
      confirm: {
        completeTitle: 'Complete the order',
        completeBody: 'It will announce {hours} and the materials logged, which is what gets costed and billed.',
        removeReportTitle: 'Remove the report',
        removeReportBody: "The {hours} report will be removed and the order's hours recomputed.",
      },
      completed: 'Order completed',
      reopened: 'Order reopened',
      createdToast: 'Order {code} created',
      savedToast: 'Order saved',
      search: { subtitle: 'Open the order' },
      notifications: {
        categories: { late: 'Late work orders', assigned: 'An order is assigned to you', breakdown: 'A breakdown in the field', reportAdded: 'A new field report' },
        assigned: {
          title: 'New order for you',
          body: '{code} · {title}{where}',
        },
        breakdown: {
          title: 'Breakdown reported',
          body: '{asset} · {code}',
          bodyWithWho: '{asset} · {code} — reported by {who}',
        },
        // Del campo a la oficina, a los roles del ajuste `reportAlertRoles`.
        reportAdded: {
          title: 'New field report',
          body: '{code} · {title}',
          bodyWho: '{code} · {title} — written by {who}',
          bodyWhere: '{code} · {where}',
          bodyWhereWho: '{code} · {where} — written by {who}',
        },
        late: {
          title: 'Work orders past their day',
          body: '{count} order(s) are past their planned date. First: {first}',
        },
      },
    },
    modules: { 'work-orders': 'Work orders' },
    assistant: {
      openers: {
        'work-orders': {
          'my-day': 'What have I got today?',
          'whats-late': 'Which orders are late?',
          'new-order': 'Open a work order',
          'log-hours': 'Log the hours on an order',
        },
      },
    },
    permissions: {
      workorders: {
        read: 'See every work order, not only your own',
        write: 'Plan: open orders and edit them',
        report: 'See your own orders and write reports, steps and incidents on them',
        manage: 'Complete, reopen, cancel and archive (includes worksheet templates)',
        templates: 'Create and edit worksheet templates',
        import: 'Import orders and reports from another system',
      },
    },
    settings: {
      ns: {
        'work-orders': {
          title: 'Work orders',
          subtitle: 'How your company works, and what it demands before an order counts as done',
          kinds: {
            label: 'The kinds of work you do',
            help: 'Yours, not ours. Renaming one leaves the orders already on it showing the old name.',
          },
          taskTypes: {
            label: 'The types of task you repeat at a stop',
            help: 'Only if you plan rounds: a gardener writes his own, a fitter never needs one. Leave it empty and tasks are plain text.',
          },
          createFromPortalRequest: {
            label: 'Open an order when a customer asks for something in the portal',
            help: 'It opens as a draft with no date, naming the request it came from. Only does anything with the customer portal installed.',
          },
          portalRequestTypes: {
            label: 'Portal request types that open an order',
            help: 'The ids as they are configured in the portal. Empty means none of them.',
          },
          createFromAcceptedQuote: {
            label: 'Open a work order when a quote is accepted',
            help: 'It opens as a draft with no date, and its lines become the steps. Only does anything with the quotes module installed.',
          },
          allowUnplanned: {
            label: 'Let the crew log work with no order',
            help: 'Each faena opens its own order, already closed and marked unplanned. Off means the office writes the order first.',
          },
          requireCustomer: {
            label: 'Require a customer on every order',
            help: 'Leave it off if you also plan your own work — a workshop repair, a stand.',
          },
          requireHoursToComplete: {
            label: 'Require at least one report before completing',
            help: 'On, because completing with no hours announces a job that cost nothing to whoever costs it.',
          },
          requireChecklistToComplete: {
            label: 'Require every step done before completing',
            help: 'Off by default: a step that does not apply today would block the order.',
          },
          requireTargetsToComplete: {
            label: 'Require every target closed before completing',
            help: 'Off by default, for the same reason: a round that ran out of afternoon is closed and the rest replanned, not held open.',
          },
          breakdownAlertRoles: {
            label: 'Who hears that something broke down in the field',
            help: 'The moment a parte says a machine stopped. Empty switches it off.',
          },
          targetTypes: {
            label: 'What an order can be about',
            help: 'The kinds of record offered under «About what». Empty offers everything the ERP can look up.',
          },
          allowFreeTextTargets: {
            label: 'Let an order name something typed by hand',
            help: 'Off, an order can only name a record of the kinds above. With a single kind and this off, the planner stops asking which kind and goes straight to the list.',
          },
          autoCompleteWhenTargetsDone: {
            label: 'Complete an order on its own when its last stop is closed',
            help: 'When a report closes the last open stop. If a rule refuses, the order stays open.',
          },
          reportAlertRoles: {
            label: 'Who hears that a field report was added',
            help: 'With the order, the place and who wrote it. Empty switches it off.',
          },
          equipmentByTaskType: {
            label: 'What to take, by type of task',
            help: 'One row per type of task in W.2: the tools and the material the crew loads for it. Orders show it as «What to take». A type left empty shows nothing.',
            keyLabel: 'Type of task',
            columns: { tools: 'Tools', materials: 'Material' },
          },
          lateAlertRoles: {
            label: 'Who hears that an order is past its date',
            help: 'Checked once each morning.',
          },
        },
      },
    },
    audit: {
      actions: {
        work_orders: {
          created: 'Work order created',
          logged: 'Work logged without an order',
          updated: 'Order edited',
          planned: 'Order planned',
          in_progress: 'Order started',
          on_hold: 'Order put on hold',
          completed: 'Order completed',
          cancelled: 'Order cancelled',
          draft: 'Order back to draft',
          archived: 'Order archived',
          restored: 'Order brought back',
          note_added: 'Note added to the order',
          report_added: 'Report added',
          report_removed: 'Report removed',
          template_created: 'Worksheet template created',
          template_updated: 'Worksheet template changed',
          template_published: 'Worksheet template published',
          template_retired: 'Worksheet template retired',
          worksheet_completed: 'Guided report completed',
          step_added: 'Step added to the order',
          step_done: 'Step ticked',
          step_undone: 'Step unticked',
          step_noted: 'What was done noted on a step',
          target_done: 'Stop closed',
          target_undone: 'Stop reopened',
          task_added: 'Task added to a stop',
          task_done: 'Task done at a stop',
          task_undone: 'Task unticked at a stop',
          task_noted: 'What was done noted on a task',
          incident_added: 'Incident logged',
          incident_resolved: 'Incident resolved',
          imported: 'Order imported from another system',
        },
      },
    },
  },

  fr: {
    'work-orders': {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        today: 'un ordre aujourd’hui | {count} ordres aujourd’hui',
        late: 'un ordre en retard | {count} ordres en retard',
        myDay: 'un ordre dans ta journée | {count} ordres dans ta journée',
        myDayLate: '{count} ordres dans ta journée, {late} en retard',
        replan: 'Replanifier',
        replanAsk: 'Replanifie l’ordre en retard : propose une nouvelle date | Replanifie les {count} ordres en retard : propose une nouvelle date pour chacun',
      },
      title: 'Ordres de travail',
      related: {
        ahead: 'À faire ici',
        done: 'Ce qui a été fait',
        cancelled: 'Annulés',
      },
      doneBanner: {
        title: 'Ordre terminé',
        when: 'Clôturé le {when}',
      },
      dashboard: {
        logWork: 'Saisir un travail sans ordre',
        tabToday: 'Aujourd’hui',
        tabTomorrow: 'Demain',
        summaryOrders: 'Un travail | {count} travaux',
        summarySites: 'un site | {count} sites',
        summaryDone: 'un fait | {count} faits',
        summaryTeam: 'Équipe : {names}',
        summaryMates: 'Avec {names}',
        emptyToday: 'Aucun ordre pour vous aujourd’hui',
        emptyTomorrow: 'Aucun ordre pour vous demain',
        next: 'Suivant',
        open: 'Ordres ouverts',
        openHelp: 'Combien d\'ordres de travail sont encore ouverts, dans n\'importe lequel de leurs états vifs.',
        today: 'Pour aujourd\'hui',
        todayHelp: 'Combien d\'ordres sont planifiés pour aujourd\'hui, clôturés compris.',
        late: 'Ordres en retard',
        lateHelp: 'Combien d\'ordres ouverts étaient datés avant aujourd\'hui et ne sont toujours pas clôturés.',
        todayList: 'La tournée du jour',
        todayListHelp: 'Les ordres planifiés pour aujourd\'hui, avec leur client ou leur adresse. Étirez la carte et il en tient plus.',
        myDay: 'Ma journée',
        myDayHelp: 'Les ordres qui vous sont affectés pour aujourd\'hui. Chacun voit les siens, et étirer la carte en fait tenir plus.',
      },
      subtitle: 'Qui y va, quand, où — et ce qui s’y est passé',
worksheet: {
        call: {
          title: 'L’appel',
          pitch: 'Racontez tout d’une traite, il note. Vous vérifiez à la fin et vous enregistrez.',
          start: 'Commencer l’appel',
          repeat: 'Répétez la question',
          hangUp: 'Raccrocher',
          phase: {
            greeting: 'Un instant…',
            asking: 'Je demande…',
            listening: 'Je vous écoute',
            thinking: 'Un instant…',
            handover: 'Celle-ci se choisit à l’écran',
            closing: 'Reprenons',
          },
          greeting: 'Bien, passons au rapport d’aujourd’hui.',
          greetingName: 'Bonjour {name}, comment s’est passée la journée ?',
          opener: 'Racontez-moi, comment ça s’est passé aujourd’hui ?',
          onScreen: 'Celle-ci, il faut la choisir à l’écran : {label}. Je vous attends.',
          stillThere: 'Vous êtes là ? Je répète.',
          notUnderstood: 'Je n’en ai pas tiré la donnée. Pouvez-vous le redire ?',
          byHand: 'Mieux vaut que vous l’écriviez vous-même.',
          byHandNote: 'Je vous ai laissé « {label} » à écrire.',
          done: 'C’est tout, il ne me manque rien. Je vous relis.',
          review: 'Vérifiez à l’écran et, si c’est bon, enregistrez.',
          resume: 'On continue à parler',
          keyboard: 'Je préfère l’écrire',
          orTap: 'Ou touchez :',
          ackA: 'D’accord.',
          ackB: 'Oui.',
          ackC: 'Très bien.',
          debug: {
            tab: 'Développement',
            title: 'Développement · l’appel vu de l’intérieur',
            clear: 'Vider',
            turns: 'Tours',
            avg: 'moyenne {t}',
            llm: 'EURouter · texte',
            stt: 'EURouter · écoute',
            tts: 'Azure · voix',
            fromCache: '{n} en cache',
            total: 'Total de l’appel',
            silence: 'attente de silence : {first} ms pour les réponses courtes, {rest} ms pour décrire le travail',
            heard: 'Entendu :',
            says: 'Dit :',
            behind: 'en arrière-plan',
            cache: 'cache',
            rest: 'réseau · file · serveur',
            empty: 'Lancez l’appel et chaque tour apparaîtra ici : la durée de chaque étape, les tokens EURouter et les caractères Azure.',
            steps: {
              stt: 'Écouter (EURouter)',
              read: 'Répartir (EURouter)',
              place: 'Chercher dans l’ERP',
              talk: 'Phrase (EURouter)',
              tts: 'Voix (Azure)',
              state: 'État',
              settle: 'Attendre l’arrière-plan',
            },
          },
          tapToPick: 'Choisir à l’écran',
          onKeyboard: 'Raccroché. On reprend quand vous voulez.',
          dontCall: 'Ne m’appelle pas à l’ouverture',
          incoming: 'Votre chef d’équipe vous appelle',
          again: 'Pardon, je n’ai pas compris.',
          notFoundPlace: 'Celui-là, je ne l’ai pas.',
          askLabel: '{label} ?',
          whichPlace: 'À {list} ?',
          whichOption: '{list} ?',
          letsGo: 'Passons au rapport.',
          incomingHint: 'Pour que vous lui racontiez votre journée. Répondez quand vous pouvez parler.',
          answer: 'Répondre',
          decline: 'Pas maintenant',
          callBack: 'Appeler le chef d’équipe',
          willNotCall: 'Cela ne sonnera plus à l’ouverture d’un rapport sur ce téléphone. Le bouton d’appel reste là.',
          or: 'ou',
          whichAsk: 'Il y a plus d’un {label}. Lequel : {options} ?',
          whichOne: '«{words}» peut être plus d’un {label}. Lequel était-ce ?',
          pickOnScreen: 'Choisissez-le à l’écran, ils y sont tous les deux.',
          notFound: 'Je ne trouve aucun {label} qui s’appelle «{words}». Cherchez-le à l’écran.',
        },
        title: 'Rédiger le rapport',
        said: 'Ce que vous avez dit',
        now: 'Maintenant',
        readBack: 'Je vous relis',
        fromSystem: 'je le sais déjà',
        fromSystemLocked: 'Le système remplit celui-là, inutile de le dire.',
        speak: 'Répondre à voix haute',
        heard: 'J’ai entendu :',
        answer: 'Répondre',
        skip: 'Passer',
        yes: 'Oui',
        no: 'Non',
        pick: 'Choisissez dans la liste',
        listen: 'Lisez-le-moi',
        confirm: 'Enregistrer le rapport',
        done: 'Rapport enregistré',
        none: 'Cette entreprise n’a pas de modèle de rapport. On le rédige comme d’habitude.',
        left: 'Il ne reste rien | Il reste 1 chose à dire | Il reste {n} choses à dire',
        files: 'Aucun fichier | 1 fichier | {n} fichiers',
        savedHere: 'Pas de réseau : gardé sur le mobile et envoyé au retour de la ligne.',
        queued: '{n} en attente. Elles partent seules au retour du réseau.',
        stillQueued: '{n} réponses pas encore envoyées. Trouvez du réseau avant de confirmer le rapport.',
      },
      help: {
        title: 'Comment fonctionne le rapport par modèle',
        subtitle: 'Laissez cet onglet ouvert pendant que vous montez le vôtre',
        lead: 'Vous écrivez les QUESTIONS auxquelles vous voulez qu’on réponde à la clôture d’une intervention. L’assistant les pose une par une, dans l’ordre que vous avez fixé, et ne laisse pas clore le rapport tant qu’il en manque une.',
        steps: {
          clone: {
            title: 'Partez d’un exemple',
            body: 'Sur « Modèles de rapport » il y a le menu « Cloner un exemple ». Le premier, « Rapport complet », porte déjà les six questions que pose presque toute entreprise de terrain : ce que vous avez fait, où, avec qui, s’il y a eu un incident, quel matériel a été utilisé et pour quel client. Il se clone retiré, vous pouvez donc le manipuler sans qu’il apparaisse sur le mobile de personne.',
            link: 'Aller aux modèles',
          },
          write: {
            title: 'Écrivez chaque question',
            body: 'Chaque question se définit avec quatre choses, et seulement quatre :',
            b1: 'CE QU’ON DEMANDE — le nom court, comme « Où c’était ».',
            b2: 'CE QU’ON RÉPOND — dans une liste fermée de huit. C’est ce qui permet à l’assistant de comprendre « un et demi » ou de refuser « dix-huit mille », et ce qui fait de ceci un rapport et non une note vocale.',
            b3: 'INSISTANCE — obligatoire (ne clôt pas sans), recommandé (demandé une fois même si personne ne l’évoque, et peut rester vide) ou facultatif (seulement si cela sort dans la conversation).',
            b4: 'COMMENT L’ASSISTANT LE DEMANDE — la phrase avec vos mots. C’est littéralement ce qu’il dira.',
          },
          preview: {
            title: 'Voyez la conversation avant de publier',
            body: 'À droite, au fur et à mesure, apparaît la conversation exacte : ce qu’il demandera, dans quel ordre, ce qu’il ne demandera pas parce que le système le sait déjà, et ce qui se passera à la confirmation. Calculée par le moteur qui la mène.',
          },
          publish: {
            title: 'Publiez-le',
            body: 'Tant que vous n’appuyez pas sur « Publier », il n’arrive sur aucun mobile. Et « Retirer » l’enlève sans le supprimer : les rapports déjà écrits le nomment, et le supprimer laisserait un historique illisible.',
          },
          fill: {
            title: 'Et l’intervenant le remplit à voix haute',
            body: 'Cela apparaît sur l’ordre et sur chaque étape de « Ma journée », à côté du formulaire habituel. Et rien à presser : l’écran appelle en s’ouvrant. Trois choses à savoir :',
            b1: 'Il ne demande pas ce qu’on sait déjà : les heures viennent du pointage et le client de l’ordre.',
            b2: 'On peut répondre en parlant ou en touchant, toujours les deux. Du bruit, ou pas de micro, et on tape. Le site ou le chantier se disent aussi : si deux se ressemblent, il demande lequel.',
            b3: 'Avant de clore il relit tout pour confirmation, et sans réseau la conversation ne s’arrête pas : elle est gardée sur le mobile et envoyée au retour de la ligne.',
          },
        },
        typesTitle: 'Les huit types de réponse',
        typesBody: 'La liste est fermée exprès. L’assistant ne peut mener la conversation que parce qu’il sait ce qu’est chaque réponse ; avec du texte libre il n’y aurait rien à vérifier et rien à sortir du stock.',
        types: {
          number_unit: 'Un nombre avec son unité et sa plage : heures, kilos, sacs.',
          boolean: 'Oui ou non.',
          choice: 'L’une des options que vous écrivez.',
          catalogue: 'Un élément de la liste d’un autre module : un site, un intervenant, un matériel, un client. C’est ce qui permet au lieu de rester lié et au matériel de sortir du stock.',
          photo: 'Une ou plusieurs, avec l’option avant et après.',
          signature: 'La signature du client.',
          text: 'Une note courte. Le seul texte libre.',
          datetime: 'Une date ou une heure.',
        },
        offTitle: 'Comment retirer ces tutoriels',
        offBody: 'Les « ? » apparaissent parce que le mode tutoriels est activé dans votre profil. Il est à vous et non à l’entreprise : le désactiver ne l’enlève à personne d’autre.',
        offLink: 'Aller à mon profil',
      },
            worksheets: {
        title: 'Modèles de rapport',
        subtitle: 'Ce qu’on note à la clôture d’une intervention, et dans quel ordre on le demande',
        listTitle: 'Les modèles de cette entreprise',
        table: { name: 'Modèle', sector: 'Secteur', fields: 'Questions', status: 'État' },
        empty: 'Aucun modèle pour l’instant. Clonez un exemple et modifiez-le.',
        emptyHint: 'Un modèle décide de ce que l’assistant demande à l’intervenant à la clôture.',
        newTitle: 'Nouveau modèle de rapport',
        editTitle: 'Modifier le modèle',
        fieldsCount: 'Aucune question | 1 question | {n} questions',
        active: 'Publié',
        inactive: 'Retiré',
        activeHint: 'Seuls les modèles publiés arrivent sur le mobile.',
        actions: {
          new: 'Nouveau modèle',
          clone: 'Cloner un exemple',
          duplicate: 'Dupliquer',
          publish: 'Publier',
          retire: 'Retirer',
          preview: 'Voir la conversation',
          addField: 'Ajouter une question',
          save: 'Enregistrer le modèle',
        },
        form: {
          name: 'Nom',
          namePlaceholder: 'Rapport de maintenance',
          description: 'À quoi il sert',
          descriptionPlaceholder: 'Quand ce modèle est utilisé',
          sector: 'Secteur',
          sectorPlaceholder: 'espaces verts, maintenance…',
          kinds: 'Seulement pour ces types de travaux',
          kindsHelp: 'Vide signifie « pour tous ». Vérifié sur la liste des types de travaux des réglages.',
        kindsAdd: 'Ajouter un type de travaux…',
          roles: 'Seulement pour ces rôles',
          rolesHelp: 'Vide signifie « pour tout le monde ».',
          fields: 'Les questions, dans l’ordre où elles sont posées',
          fieldsHelp: 'L’assistant descend la liste et ne pose que ce qui manque : ce qu’il sait déjà, ou qu’on vient de lui dire, il ne le redemande pas.',
          nameRequired: 'Le modèle a besoin d’un nom.',
          fieldsRequired: 'Un modèle sans question ne demande rien.',
          saved: 'Modèle enregistré',
          duplicateName: 'Nom de la copie',
        },
        field: {
          label: 'Ce qu’on demande',
          labelPlaceholder: 'Sacs d’engrais',
          type: 'Ce qu’on répond',
          requirement: 'Insistance',
          source: 'D’où ça vient',
          autoSource: 'Qui le remplit',
          unit: 'Unité',
          unitPlaceholder: 'h, kg, sacs',
          options: 'Options',
          optionsHelp: 'Une par ligne. Il en faut au moins deux.',
          catalogue: 'Catalogue',
          catalogueHelp: 'Dans quelle liste d’un autre module on choisit.',
          min: 'Minimum',
          max: 'Maximum',
          maxLength: 'Longueur maximale',
          multiple: 'Plusieurs possibles',
          beforeAfter: 'Avant et après',
          hint: 'Comment l’assistant le demande',
          hintPlaceholder: 'Combien de sacs d’engrais avez-vous utilisés ?',
          hintHelp: 'Avec vos mots. Laissé vide, on le demande avec le libellé.',
          binds: 'Enregistré dans',
          bindsHelp: 'Le lier à une case du rapport habituel, c’est ce qui le rend visible au reste de l’ERP.',
          postAction: 'À la clôture du rapport',
          up: 'Monter',
          down: 'Descendre',
          remove: 'Retirer la question',
        },
        types: {
          number_unit: 'Nombre avec unité',
          boolean: 'Oui ou non',
          choice: 'Liste d’options',
          catalogue: 'Du catalogue d’un autre module',
          photo: 'Photos ou vidéo',
          signature: 'Signature',
          text: 'Note courte',
          datetime: 'Date et heure',
        },
        requirements: {
          required: 'Obligatoire',
          recommended: 'Recommandé',
          optional: 'Facultatif',
        },
        requirementHelp: {
          required: 'Le rapport ne peut pas se clore sans.',
          recommended: 'L’assistant le demande une fois même si personne ne l’évoque, et on peut le laisser vide.',
          optional: 'Rempli seulement si l’intervenant l’évoque.',
        },
        sources: {
          manual: 'C’est l’intervenant qui le dit',
          auto: 'Le système le remplit tout seul',
        },
        autoSources: {
          worker: 'Qui l’a fait',
          now: 'La date et l’heure',
          service: 'L’intervention du jour',
          customer: 'Le client',
          clocked_hours: 'Les heures du pointage',
          location: 'Où c’était',
        },
        bindings: {
          none: 'Seulement sur le modèle',
          hours: 'Les heures du rapport',
          description: 'Ce qui a été fait',
          workDate: 'Le jour des travaux',
          materials: 'Le matériel utilisé',
          photos: 'Les photos',
          targetDone: 'Site terminé',
        },
        postActions: {
          none: 'Rien',
          consume_stock: 'Sortir du stock',
          raise_incident: 'Ouvrir un incident',
          complete_service: 'Marquer l’intervention faite',
        },
        preview: {
          title: 'La conversation qu’il produira',
          help: 'Calculée par le moteur qui la mène : voilà ce qui arrivera à l’intervenant.',
          prefilled: 'Pas demandé, déjà connu',
          asks: 'Ce que l’assistant demandera, dans cet ordre',
          onlyIfMentioned: 'Seulement si c’est évoqué',
          afterwards: 'À la confirmation',
          blocking: 'Impossible de clore sans',
          nothing: 'Rien',
          opening: 'Racontez-moi ce que vous avez fait.',
          closing: 'Je vous le relis pour confirmation.',
        },
        seeds: {
          complete: {
            name: 'Rapport complet',
            description: 'Ce qui a été fait, où, avec qui, ce qui s’est passé et ce qui a été utilisé.',
            fields: {
              hours: {
                label: 'Heures',
                hint: 'Combien de temps y êtes-vous resté ?',
              },
              tasks: {
                label: 'Ce que vous avez fait',
                hint: 'Racontez-moi ce que vous avez fait aujourd’hui.',
              },
              where: {
                label: 'Où c’était',
                hint: 'Où avez-vous fait ces tâches ?',
              },
              crew: {
                label: 'Avec quels intervenants',
                hint: 'Qui vous a accompagné ?',
              },
              issues: {
                label: 'Incidents',
                hint: 'Y a-t-il eu un incident ou un problème ?',
              },
              material: {
                label: 'Matériel utilisé',
                hint: 'Avez-vous utilisé du matériel ?',
              },
              customer: {
                label: 'Pour quel client',
                hint: 'À quel client cela est-il affecté ?',
              },
              photo: {
                label: 'Photos',
                hint: 'Prenez-moi une photo du résultat ?',
              },
            },
          },
          gardening: {
            name: 'Rapport espaces verts',
            description: 'Une tournée d’entretien de jardins.',
            fields: {
              hours: {
                label: 'Heures',
                hint: 'Combien de temps y êtes-vous resté ?',
              },
              done: {
                label: 'Ce qui a été fait',
                hint: 'Racontez-moi ce que vous avez fait au jardin.',
              },
              mown: {
                label: 'Tonte faite',
                hint: 'Avez-vous tondu ?',
              },
              fertiliser: {
                label: 'Sacs d’engrais',
                hint: 'Combien de sacs d’engrais avez-vous utilisés ?',
              },
              material: {
                label: 'Matériel utilisé',
                hint: 'Avez-vous pris quelque chose au dépôt ?',
              },
              photo: {
                label: 'Photos',
                hint: 'Prenez-moi une photo du résultat.',
              },
              issues: {
                label: 'Incidents',
                hint: 'Un souci ? Quelque chose de cassé, quelque chose à revoir.',
              },
            },
          },
          maintenance: {
            name: 'Rapport de maintenance',
            description: 'Une intervention sur une machine ou une installation.',
            fields: {
              hours: {
                label: 'Heures',
                hint: 'Combien de temps y êtes-vous resté ?',
              },
              kind: {
                label: 'Type d’intervention',
                hint: 'Préventif, correctif ou contrôle ?',
                options: 'Préventif · Correctif · Contrôle',
              },
              done: {
                label: 'Ce qui a été fait',
                hint: 'Racontez-moi ce que vous avez fait sur la machine.',
              },
              stopped: {
                label: 'La machine était à l’arrêt',
                hint: 'Était-elle à l’arrêt ?',
              },
              parts: {
                label: 'Pièces',
                hint: 'Quelles pièces avez-vous montées ?',
              },
              reading: {
                label: 'Relevé de pression',
                hint: 'Quelle pression affiche-t-elle ?',
              },
              issues: {
                label: 'Incidents',
                hint: 'Quelque chose à signaler ?',
              },
            },
          },
          electrician: {
            name: 'Rapport d’électricien',
            description: 'Une intervention électrique signée par le client.',
            fields: {
              hours: {
                label: 'Heures',
                hint: 'Combien de temps y êtes-vous resté ?',
              },
              done: {
                label: 'Ce qui a été fait',
                hint: 'Racontez-moi ce que vous avez fait.',
              },
              measured: {
                label: 'Tension mesurée',
                hint: 'Quelle tension avez-vous mesurée ?',
              },
              earthing: {
                label: 'Mise à la terre vérifiée',
                hint: 'Avez-vous vérifié la mise à la terre ?',
              },
              material: {
                label: 'Matériel utilisé',
                hint: 'Quel matériel avez-vous posé ?',
              },
              panel: {
                label: 'Photo du tableau',
                hint: 'Prenez-moi une photo du tableau.',
              },
              signature: {
                label: 'Signature du client',
                hint: 'Faites signer le client.',
              },
              issues: {
                label: 'Incidents',
                hint: 'Un souci ?',
              },
            },
          },
        },
      },
      detailTitle: 'Ordre de travail',
      newTitle: 'Nouvel ordre de travail',
      editTitle: 'Modifier l’ordre',
      actions: {
        new: 'Nouvel ordre', edit: 'Modifier', addReport: 'Saisir un rapport',
        guidedReport: 'Le dire à voix haute',
        complete: 'Terminer', reopen: 'Réouvrir', cancel: 'Annuler',
        save: 'Enregistrer', remove: 'Supprimer',
      },
      status: {
        draft: 'Brouillon', planned: 'Planifié', in_progress: 'En cours',
        on_hold: 'En attente', completed: 'Terminé', cancelled: 'Annulé',
      },
      priority: { low: 'Basse', medium: 'Normale', high: 'Haute', critical: 'Critique' },
      severity: { low: 'Légère', medium: 'Moyenne', high: 'Grave', critical: 'Critique' },
      day: { today: 'Aujourd’hui', yesterday: 'Hier', tomorrow: 'Demain' },
      tabs: {
        overview: 'Résumé', reports: 'Rapports', incidents: 'Incidents de l’ordre', history: 'Historique',
      },
      // ── Écrire l'ordre, avec de l'aide ────────────────────────────
      ai: {
        write: 'Rédiger',
        writeHint: 'Écrit le corps de l’ordre à partir de ce qui est saisi',
        summarise: 'Résumer dans le titre',
        summariseHint: 'En tire une ligne et la met en titre',
        task: 'Écrire la tâche',
        taskHint: 'Écrit une tâche à partir de ce qui est dans le champ',
        working: 'Rédaction…',
      },
      // ── Sur quoi porte l'ordre, et son planificateur ──────────────
      // La tarjeta que se confirma desde el asistente (create_order) y que
      // se ve durante la llamada mientras la orden se va armando.
      assistant: {
        // Las respuestas de las consultas que contestan las reglas (queries.js).
        query: {
          agenda: {
            self: 'Vous avez {count} ordres le {when} : {list}.',
            selfNone: 'Vous n’avez aucun ordre le {when}.',
            other: '{who} a {count} ordres le {when} : {list}.',
            otherNone: '{who} n’a aucun ordre le {when}.',
          },
          late: {
            some: '{count} ordres sont en retard : {list}.',
            none: 'Aucun ordre n’est en retard.',
          },
        },
        order: {
          action: 'Ouvrir un ordre de travail',
          what: 'Quoi',
          when: 'Quand',
          draft: 'Brouillon, sans date',
          customer: 'Client',
          who: 'Qui y va',
          noTasks: 'Aucune tâche',
          more: '{count} lieux de plus',
          outcome: 'Ordre {code} créé',
          fromPlaces: 'Repris de ses lieux',
          nobody: 'Personne pour l’instant',
          noDateNote: 'Sans date : il sera enregistré comme brouillon et n’arrivera à personne',
          noCrewNote: 'Personne n’y va encore',
          noPlaceNote: 'Il ne nomme aucun lieu',
          noTasksNote: 'Aucune tâche à : {places}',
          severalPlaces: '« {name} » correspond à plusieurs : {options}. Choisissez en le disant ou en l’écrivant.',
          unknownPlace: '« {name} » n’est pas un lieu connu : il partira tel qu’écrit',
          unknownPerson: '« {name} » est introuvable parmi les personnes',
          placeN: 'Lieu {n}',
          tasks: 'Tâches',
          noPlace: 'Pas encore de lieu',
          where: 'Où',
          priority: 'Priorité',
          notes: 'Notes',
          noNotes: 'Rien',
          dayDoubtNote: 'Le jour n’était pas clair : choisissez-le',
          from: 'Issu de',
          sourceUnreadable: 'Ce plan ou cet incident est illisible : l’ordre ne peut pas y renvoyer',
          unknownCustomer: '« {name} » n’est pas un client connu',
          timeNote: 'Heure : {time}',
          readPlace: 'Ordre à {place}',
          readDay: '{day}',
          readDayTime: '{day}, à {time}',
          readTasks: 'À faire : {tasks}',
          readCrew: '{who} y vont',
          readOne: '{who} y va',
          asSaid: '« {said} », tel quel',
          internal: 'Interne : sans lieu ni client',
        },
        change: {
          order: 'Ordre',
          noOrder: 'Quel ordre ?',
          kind: 'Ordre de travail',
          unknownOrder: 'Aucun ordre ouvert ne correspond à « {said} »',
          severalOrders: '« {said} » correspond à plusieurs ordres : choisissez-en un',
          until: 'Jusqu’ici',
          closedOrder: 'Cet ordre est déjà clos',
          summary: {
            reschedule: 'Déplacer {order} au {day} {time}',
            assign: '{order} : {who} y va',
            priority: {
              low: '{order} : priorité basse', medium: '{order} : priorité moyenne', high: '{order} : priorité haute', critical: '{order} : urgent', other: '{order} : changer la priorité',
            },
            status: {
              draft: '{order} : repasser en brouillon', planned: '{order} : planifier', in_progress: '{order} : démarrer', on_hold: '{order} : mettre en pause',
              completed: '{order} : terminer', cancelled: '{order} : annuler', other: '{order} : changer le statut',
            },
          },
          rescheduleAction: 'Déplacer un ordre à un autre jour',
          newDay: 'Nouveau jour',
          noDay: 'Choisissez le jour',
          pickDay: 'Choisissez le nouveau jour',
          time: 'Heure',
          noTime: 'Sans heure',
          timeKept: '{time} (comme avant)',
          badTime: 'L’heure s’écrit HH:MM',
          noDayBefore: 'Sans jour',
          draftPlanned: 'C’est un brouillon : avec un jour il devient planifié et arrive à l’équipe',
          readMove: '{code}, au {day}',
          readMoveTime: '{code}, au {day} à {time}',
          rescheduled: '{code} déplacé au {day}',
          assignAction: 'Changer qui y va',
          who: 'Qui y va',
          nobody: 'Personne',
          team: 'Équipe',
          unknownPerson: 'Je ne trouve pas « {name} » parmi les personnes',
          unknownTeam: 'Aucune équipe ne s’appelle « {name} »',
          nobodyNote: 'Personne n’irait : choisissez qui y va',
          readAssign: '{code} : {who} y va',
          assigned: '{code} : {who} y va',
          priorityAction: 'Changer la priorité',
          priority: 'Priorité',
          noPriority: 'Choisissez-en une',
          readPriority: {
            low: '{code}, priorité basse', medium: '{code}, priorité moyenne', high: '{code}, priorité haute', critical: '{code}, urgent',
          },
          prioritised: {
            low: '{code} : priorité basse', medium: '{code} : priorité moyenne', high: '{code} : priorité haute', critical: '{code} : urgent', other: '{code} : priorité changée',
          },
          statusAction: 'Changer le statut d’un ordre',
          status: 'Statut',
          noStatus: 'Choisissez-en un',
          reason: 'Pourquoi',
          noReason: 'Non dit',
          badTransition: 'Depuis son statut actuel, l’ordre ne peut pas passer à celui-ci',
          needsManage: 'Terminer ou annuler demande la permission de gérer les ordres',
          needsReason: 'Dites pourquoi il est annulé',
          completeNote: 'En le terminant, ses heures partent vers qui les facture',
          readStatus: {
            draft: '{code}, en brouillon', planned: '{code}, planifié', in_progress: 'Démarrer {code}', on_hold: 'Mettre {code} en pause', completed: 'Terminer {code}', cancelled: 'Annuler {code}',
          },
          outcomeStatus: {
            draft: '{code} repassé en brouillon', planned: '{code} planifié', in_progress: '{code} démarré', on_hold: '{code} en pause',
            completed: '{code} terminé', cancelled: '{code} annulé', other: '{code} : statut changé',
          },
        },
      },
      // Lo que la llamada pregunta mientras falta algo de una orden.
      voice: {
        askPlace: 'C’est où ?',
        askReportOrder: 'Pour quel ordre est ce rapport ?',
        askReportHours: 'Combien d’heures ?',
        askPlaceAgain: 'Je ne trouve pas « {said} ». Comment s’appelle le lieu ?',
        whichPlace: '« {said} » peut être {options} ou {last}. Lequel ?',
        thisPlace: 'Je ne trouve pas « {said} » tel quel. Vous voulez dire {last} ?',
        askTasks: 'Qu’est-ce qu’il faut faire ?',
        askDay: 'Pour quel jour ?',
        askPeople: 'Qui y va ?',
        askPeopleAgain: 'Je ne trouve pas « {name} ». Qui y va ?',
        askOrder: 'Quel ordre ?',
        askOrderAgain: 'Je ne trouve pas l’ordre « {said} ». Lequel est-ce ?',
        whichOrder: '« {said} » peut être {options} ou {last}. Lequel ?',
        thisOrder: 'Vous voulez dire {last} ?',
        askNewDay: 'À quel jour ?',
        askTeamAgain: 'Je ne trouve pas l’équipe « {name} ». Qui y va ?',
        askPriority: 'Quelle priorité : basse, moyenne, haute ou urgente ?',
        askStatus: 'Je le démarre, le mets en pause, le termine ou l’annule ?',
        askCancelReason: 'Pourquoi est-il annulé ?',
        whichTask: '« {said} » n’est pas dans vos tâches. C’est {options} ou {last} ? Sinon, dites « tel quel ».',
        thisTask: '« {said} » n’est pas dans vos tâches. C’est {last} ? Sinon, dites « tel quel ».',
      },
      targets: {
        tab: 'Objets',
        title: 'Sur quoi',
        what: 'De quel type',
        freeText: 'L’écrire à la main',
        name: 'Nom',
        namePlaceholder: 'Presse 3, Ligne 2, la maison Puig…',
        search: 'Rechercher',
        searchPlaceholder: 'Tapez pour chercher…',
        wherePlaceholder: 'Où : hall, rue, étage…',
        add: 'Ajouter',
        remove: 'Le retirer de l’ordre',
        notes: 'Notes',
        emptyHint: 'Un ordre sans rien ici est un travail isolé, et c’est bien aussi.',
        chosen: 'choisis',
        pickManySearch: 'Affinez : Puigcerdà, Alp, hall 2…',
        pickManyLoading: 'Recherche…',
        pickManyNone: 'Rien ne sort. Essayez un autre mot.',
        pickManyAll: 'Cocher les {count} affichés',
        group: 'Dossier',
        groupAll: 'Tous',
        pickGroupAll: 'Ajouter les {count} de « {group} »',
        pickManyAlready: '{count} déjà sur l’ordre',
        pendingHere: 'Ce qui était déjà en attente ici',
        pendingHint: 'Noté un autre jour sur ce lieu. Ce que vous cochez rejoint l’ordre, '
          + 'et le compléter le clôt là où il était.',
        pendingLoading: 'Recherche de ce qui est en attente…',
        unit: 'Objet',
        units: 'objets',
        tasks: 'tâches',
        count: '{count} objets',
        progress: '{done}/{total} objets',
        taskProgress: '{done}/{total} tâches',
        column: 'Objets',
        done: 'Fait',
        pending: 'En attente',
        markDone: 'Le marquer comme fait',
        markPending: 'Le remettre en attente',
        doneBy: 'par {who}',
        noTasks: 'Aucune tâche notée ici',
      },
      planner: {
        removeTask: 'Retirer « {task} »',
        stepWho: 'Quand et sur quoi',
        stepWork: 'Le travail',
        stepDetail: 'Le détail',
        stepCustomer: 'Pour qui',
        stepWhen: 'Quand',
        stepCrew: 'Qui y va',
        stepTargets: 'Sur quoi',
        stepPlaces: 'Les lieux',
        stepTasks: 'Les tâches',
        stepSteps: 'Les étapes',
        back: 'Retour',
        lead: {
          dictate: 'Dites-le à voix haute et le reste se remplit tout seul. Vous pouvez passer.',
          when: 'Quel jour l’équipe y va ? Laissez vide si vous ne le savez pas encore.',
          targets: 'Où faut-il aller ? Un lieu, ou les cinquante d’une tournée.',
          tasks: 'Que faut-il faire à chaque endroit ? Ce qui se répète, ajoutez-le partout d’un coup.',
          work: 'En une ligne : quel travail c’est.',
          detail: 'Ce que l’équipe doit savoir en arrivant.',
          customer: 'Pour qui c’est, et à quel chantier ça se rattache, le cas échéant.',
          crew: 'Qui y va. Avec une équipe, ses personnes sont copiées.',
          steps: 'Les étapes cochées au fur et à mesure. Seulement à la création.',
        },
        create: 'Créer l’ordre',
        next: 'Continuer',
        sectionWhen: 'Quand',
        sectionWho: 'Qui y va',
        sectionOrder: 'L’ordre de travail',
        sectionNotes: 'Notes pour qui y va',
        descriptionHelp: 'Ce qu’il faut faire, avec le détail nécessaire. C’est ce qui se lit sur place.',
        descriptionPlaceholder: 'Ce qui se passe, ce qu’il faut faire, ce à quoi faire attention…',
        notesHelp: 'Tout ce qui entoure le travail : accès, clés, horaires, qui demander.',
        notesPlaceholder: 'Appeler avant de venir, la clé est à la loge…',
        inTwoDays: 'Après-demain',
        nextMonday: 'Lun. proch.',
        crewAdd: 'Ajouter quelqu’un…',
        crewNone: 'Seulement la personne à qui l’ordre est assigné',
        moveUp: 'Monter',
        moveDown: 'Descendre',
        viewTargets: 'Un par un',
        viewTasks: 'Par tâche',
        viewSummary: 'Résumé',
        bulkTitle: 'Une tâche sur tous',
        bulkApply: 'Ajouter aux {count}',
        taskTypeAny: 'Type de tâche…',
        taskTextPlaceholder: 'Ce qu’il faut faire…',
        addTask: 'Ajouter une tâche',
        noTasks: 'Pas encore de tâches',
        noTasksYet: 'Aucune tâche notée pour l’instant',
        atTargets: 'sur {count}',
        titleSuggested: 'Si vous le laissez vide : « {title} »',
        titleMore: '{names} et {count} de plus',
        bringPending: 'Reprendre tout ce qui est en attente',
        bringPendingHint: 'Ce qui a été noté un autre jour sur chaque lieu entre dans l’ordre. Retirez le superflu lieu par lieu.',
        bringingPending: 'Recherche de ce qui est en attente sur {count} lieux…',
        broughtPending: '{count} tâches en attente reprises dans l’ordre',
        noPending: 'Rien n’était en attente sur ces lieux',
        addTasksHere: 'Ajouter des tâches',
        noTaskTypes: 'Pas encore de types de tâche : chaque tâche se tape à la main. Si vous répétez toujours les mêmes (tonte, taille, arrosage…), écrivez-les une fois dans Paramètres et elles apparaîtront ici dans une liste.',
        noTaskTypesLink: 'Les écrire dans Paramètres (W.2)',
      },
      equipment: {
        title: 'À emporter',
        tools: 'Outils',
        materials: 'Matériel',
        forTasks: 'pour {types}',
        hint: 'Déduit des types de tâche de la tournée (Paramètres, W.15).',
      },
      myDay: {
        title: 'Ma journée',
        subtitle: 'Ce que vous avez aujourd’hui, et ce que vous avez saisi',
        today: 'Aujourd’hui',
        previous: 'Jour précédent',
        next: 'Jour suivant',
        targets: '{count} objets',
        late: '{count} en retard',
        incidents: '{count} avec incident',
        empty: 'Rien ne vous est assigné aujourd’hui',
        emptyHint: 'Si vous attendiez du travail, voyez avec la planification : il peut être sans date.',
        failed: 'Impossible de charger votre journée',
        failedHint: 'Pas de réseau, ou le serveur ne répond pas. Rien n’est perdu : réessayez dans un instant.',
        retry: 'Réessayer',
      },
      dictate: {
        step: 'La dicter',
        title: 'Dicter l’ordre',
        subtitle: 'Dites-le et le formulaire se remplit. Vous relisez avant d’enregistrer : rien n’est enregistré ici.',
        clip: 'L’enregistrement',
        read: 'Le lire',
        heard: 'Ce qui a été entendu',
        silence: 'Rien n’a été entendu. Réenregistrez plus près du micro.',
        which: 'Lequel est « {words} » ?',
        notFound: 'Je n’ai rien trouvé qui s’appelle « {words} ». Saisissez-le à la main si besoin.',
      },
      board: { title: 'Ouverts par type de travail' },
      filters: {
        oneCustomer: 'Un seul client',

        search: 'Chercher', searchPlaceholder: 'Numéro, objet, client, lieu…',
        status: 'Statut', allStatuses: 'Tous', date: 'Jour',
        team: 'Équipe', allTeams: 'Toutes',
        onlyOpen: 'Seulement ouverts', onlyLate: 'Seulement en retard', onlyUnplanned: 'Seulement notés',
      },
      table: {
        code: 'Numéro', title: 'Travail', planned: 'Prévu', assignee: 'Assigné à',
        progress: 'Étapes', hours: 'Heures', status: 'Statut', crewMore: 'de plus',
      },
      kpi: { today: 'Pour aujourd’hui', open: 'Ouverts', late: 'En retard', hours: 'Heures terminées' },
      empty: {
        none: 'Pas encore d’ordres de travail',
        noneHint: 'Ouvrez le premier, ou acceptez un devis et laissez-le s’ouvrir seul.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essayez d’enlever un filtre ou de chercher autre chose.',
      },
      lateBy: '{days} jours de retard',
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      file: { title: 'Fiche' },
      fields: {
        code: 'Numéro', title: 'Objet', kind: 'Type de travail', priority: 'Priorité',
        customer: 'Client', project: 'Projet', location: 'Lieu', description: 'Description',
        plannedDate: 'Date prévue', assignee: 'Assigné à', crew: 'Qui d’autre y va',
        team: 'Équipe',
        quote: 'Devis', hours: 'Heures saisies',
        source: { incident: 'Issu de l’incident', maintenance: 'Issu de la maintenance' },
      },
      form: {
        what: 'Le travail', who: 'Qui et quand',
        titlePlaceholder: 'Remplacer la pompe du circuit 2…',
        customerPlaceholder: 'Entreprise ou particulier',
        projectPlaceholder: 'À quel projet il appartient',
        projectHelp: 'Si vous l’indiquez, les heures et le matériel de cet ordre iront à son livre de coûts.',
        locationPlaceholder: 'Où aller',
        plannedHelp: 'En mettant une date, l’ordre passe en planifié et apparaît dans la journée de qui y va.',
        nobody: 'Non assigné',
        crewHelp: 'Choisies une par une dans la liste des personnes. Tous verront l’ordre dans leur journée.',
        noTeam: 'Sans équipe',
        teamHelp: 'En choisir une copie ses personnes ci-dessous, et son responsable comme assigné si '
          + 'personne ne l’est. Une copie : qui y va reste inscrit même si l’équipe change ensuite.',
        teamMembers: '{count} personnes',
        noPeople: 'Aucune personne active à assigner.',
        checklistTitle: 'Étapes à suivre',
        checklistHelp: 'Seulement à la création : ensuite elles se modifient sur la fiche, où chaque étape garde qui l’a cochée.',
        titleRequired: 'L’ordre a besoin d’un objet',
      },
      checklist: {
        title: 'Étapes',
        empty: 'Aucune étape',
        emptyHint: 'Ajoutez celles qui se répètent sur ce type de travail.',
        add: 'Ajouter une étape',
        addPlaceholder: 'Vérifier la pression du circuit…',
      },
      // ── Le travail que personne n avait prévu ───────────────────────
      log: {
        unlinked: 'Le travail est enregistré, mais sans lien avec {names} : votre compte ne peut pas voir ces fiches. Prévenez le bureau pour qu’il le relie.',
        action: 'Noter un travail',
        title: 'Noter un travail effectué',
        hint: 'Un travail fait sans ordre. Il ouvre son propre ordre, déjà clôturé, à votre nom.',
        where: 'Où était-ce',
        whereHelp: 'Un site, une machine, un véhicule — ce que cette instance sait nommer.',
        whereRequired: 'Dites où était-ce, ou ce que vous avez fait',
        freeText: 'Le saisir à la main',
        search: 'Lequel',
        searchPlaceholder: 'Commencez à taper…',
        name: 'Quoi ou où',
        namePlaceholder: 'Jardin des Pins, Presse 3…',
        kind: 'Type de travail',
        hours: 'Combien de temps',
        workDate: 'Quel jour',
        workDateHelp: 'Le jour travaillé, pas celui du jour. Samedi peut se noter lundi.',
        description: 'Ce que vous avez fait',
        descriptionPlaceholder: 'Haies taillées et arrosage vérifié…',
        customerHelp: 'Cette instance exige un client sur chaque ordre.',
        tasks: 'Tâches faites', addTask: 'Ajouter une tâche',
        addStop: 'Ajouter un autre endroit',
        quick: {
          where: 'Où êtes-vous allé ?',
          whereHelp: 'Touchez les endroits. Ceux d\'aujourd\'hui d\'abord.',
          today: 'aujourd\'hui',
          noSuggestions: 'Aucun endroit récent : ajoutez-en un.',
          otherSite: 'Un autre endroit',
          pickFirst: 'Touchez où vous êtes allé',
          hours: 'Combien de temps ?',
          otherHours: 'Autre',
          what: 'Qu\'avez-vous fait ?',
          photos: 'Photos',
          issue: 'Quelque chose de cassé',
          issueLabel: 'Le problème',
          issuePlaceholder: 'L\'arrosage du secteur 2 fuit…',
          tell: 'Le raconter',
          more: 'Plus de détails',
          less: 'Moins de détails',
          day: 'La journée',
          todayPill: 'Aujourd\'hui',
          yesterday: 'Hier',
          otherDay: 'Un autre jour',
          with: 'Avec qui ?',
          alone: 'Seul',
          dayMore: 'Type, projet et client',
          clocked: 'sur {hours} pointées',
          send: 'Envoyer',
          issueSaved: 'Incident noté à {name}',
          issueFailed: 'Impossible de noter l\'incident à {name}',
        },
        dropStop: 'Retirer cet endroit',
        stopBlank: 'Endroit sans nom',
        whereType: 'Type de lieu',
        what: 'Le travail',
        noTasks: "Rien de noté pour l'instant.",
        noMaterials: "Rien n'a été consommé.",
        pendingTitle: 'Ce qui restait à faire ici',
        pendingHint: "Cochez ce que vous avez fait : cela rejoint le travail et est marqué fait là où c'était noté.",
        pendingFailed: "Le travail est enregistré, mais «{name}» n'a pas pu être clôturé et reste à faire.",
        leaveTitle: 'Abandonner ce travail ?',
        leaveMessage: "Ce que vous avez écrit n'a été enregistré nulle part.",
        leaveConfirm: 'Abandonner',
        leaveCancel: 'Continuer',
        taskPlaceholder: 'Haie taillée, arroseur remplacé…',
        dictate: {
          title: 'Dites-le et le formulaire se remplit',
          subtitle: 'Ce que vous avez fait, où, combien de temps et avec quoi. Vous vérifiez avant d’enregistrer.',
          clip: 'Note vocale du rapport',
          read: 'Remplir avec ce que j’ai dit',
          heard: 'Ce qui a été entendu',
          silence: 'Rien n’a été entendu. Réenregistrez.',
          which: 'Lequel est « {words} » ?',
          notFound: 'Je n’ai pas trouvé « {words} ». Choisissez-le ci-dessous.',
          noHours: 'Vous n’avez pas dit combien de temps. Mettez-le vous-même : je ne l’invente pas.',
          keeps: 'Rien n’est encore enregistré. Vérifiez le formulaire et notez.',
          clipFailed: 'Le travail est noté, mais la note vocale n’a pas pu être jointe.',
        },
        time15: '+15 min',
        time30: '+30 min',
        time60: '+1 h',
        timeClear: 'Remettre à zéro',
        crew: 'Qui était avec vous',
        crewHelp: 'Leurs heures vont sur ce rapport. Cela n’assigne de travail à personne.',
        crewAdd: 'Ajouter quelqu’un',
        crewPlaceholder: 'Choisissez un collègue…',
        crewHours: 'Heures de {who}',
        crewNobody: 'Personne à ajouter.',
        short: 'Noter',
        save: 'Noter',
        saved: 'Noté sous {code}',
        savedOpen: 'Noté sous {code}. Il reste ouvert : le bureau doit le clôturer.',
      },
      unplanned: {
        badge: 'Non prévu',
        title: 'Ajouté sur place par {name}',
        origin: 'Noté',
        originHelp: 'Personne ne l avait prévu : celui qui a fait le travail l a noté.',
        addTaskPlaceholder: 'Ce qui restait à faire et qui manquait sur la liste…',
      },
      imported: {
        badge: 'Importé',
        help: 'Cet ordre vient de {system} : ses dates, qui a coché chaque tâche et ses rapports sont ceux de là-bas.',
        from: 'Importé de',
        createdThere: 'créé là-bas le {date}',
      },
      report: {
        breakdown: {
          toggle: 'Il y a eu une panne',
          hint: 'Notez l’heure de l’arrêt. C’est ce dont personne ne se souvient une semaine après, et c’est de là que sortent le MTBF et le MTTR.',
          which: 'Ce qui s’est arrêté',
          stoppedAt: 'Arrêt à',
          stoppedAtHint: 'L’heure réelle, pas celle du rapport',
          restartedAt: 'Reparti à',
          restartedAtHint: 'Vide = toujours à l’arrêt',
          didStop: 'La production s’est arrêtée',
          what: 'Ce qui s’est passé',
          whatHint: 'Avec vos mots. La maintenance code le type et la cause ensuite, contre son catalogue',
          whatPlaceholder: 'Le flexible du vérin de fermeture a lâché…',
        },
        title: 'Rapport de {code}',
        hours: 'Heures', hoursPlaceholder: 'p. ex. 6,5',
        hoursRequired: 'Le rapport a besoin des heures travaillées',
        workDate: 'Jour du travail',
        workDateHelp: 'Le jour où le travail a eu lieu, pas aujourd’hui.',
        description: 'Ce qui a été fait',
        descriptionPlaceholder: 'Pompe remplacée et circuit purgé…',
        materials: 'Matériel utilisé', addLine: 'Ajouter une ligne',
        measured: "Métré du jour", addMeasure: 'Ajouter un métré',
        itemPlaceholder: 'Chercher le poste',
        measuredHint: "Ce qui a été fait AUJOURD'HUI, pas le total. Le poste le cumule tout seul.",
        noMaterials: 'Aucun matériel. Ajoutez des lignes si quelque chose a été utilisé.',
        materialPlaceholder: 'Câble, tube, pièce…',
        qty: 'Qté', unit: 'Unité', price: 'Prix',
        media: 'Photos et vidéos',
        mediaHelp: 'Du travail fait. Vous pouvez les prendre ici même.',
        mediaHint: 'Certaines choses ne se photographient pas : un bruit, une fuite, une machine qui ne déraille qu’en marche.',
        photos: 'Photos', photosHelp: 'Du travail fait. L’appareil photo marche aussi.',
        photosPending: '{count} photo(s) à téléverser',
        photoFailed: 'Impossible de téléverser « {name} »',
        save: 'Enregistrer le rapport', saved: 'Rapport enregistré',
        empty: 'Pas encore de rapports',
        emptyHint: 'Saisissez les heures et le matériel en finissant : c’est ce qui sera facturé et imputé.',
        photo: 'Photo du travail',
        audio: 'Note vocale',
      },
      parte: {
        title: 'Rapport du jour',
        day: 'La journée',
        whereWere: 'Les arrêts du jour',
        whereWereHelp: 'Les lieux encore à terminer sont déjà cochés. Décochez celui qui ne convient pas et complétez le reste : ses heures, ses photos et ce qui y a été fait.',
        allStops: 'Je suis allé partout',
        notHere: 'Pas venu',
        here: 'Venu ici',
        hoursHere: 'Combien de temps ici',
        alreadyHere: '{hours} déjà notées',
        tasks: 'Les tâches de cet endroit',
        tasksNone: 'Rien n’était noté ici. Ajoutez ce que vous avez fait.',
        taskNote: 'Ce qui a été fait',
        taskHours: 'Combien de temps sur cette tâche',
        taskHoursShort: 'h',
        fromTasks: 'La somme des tâches',
        taskNotePlaceholder: 'Coupé et ramassé ; la haie du fond reste pour la prochaine fois…',
        taskPending: 'Reste à faire',
        foundPending: 'Il faudra revenir',
        addFound: 'Ajouter ce qui est apparu ici',
        closeStop: 'Considérer cet endroit terminé',
        savedStop: 'Enregistré',
        tally: 'aucun endroit coché | à un endroit | à {count} endroits',
        tallyTasks: '{count} tâche | {count} tâches',
        tallyPhotos: '{count} photo | {count} photos',
        nothing: 'Cochez au moins un endroit où vous êtes allé.',
        hoursMissing: 'Dites combien de temps vous êtes resté à « {name} ».',
        failed: 'Impossible d’enregistrer le rapport de « {name} » : {why}',
        savedMany: '{count} rapports enregistrés',
        savedPartly: '{done} sur {count} enregistrés. Les autres sont toujours là : appuyez de nouveau sur enregistrer.',
        leaveTitle: 'Abandonner le rapport ?',
        leaveMessage: 'Rien de ce que vous avez écrit n’a été enregistré.',
        leaveConfirm: 'L’abandonner',
        leaveCancel: 'Continuer à écrire',
        closed: 'Cet ordre est déjà clôturé',
        closedHint: 'Un rapport de plus demande de le rouvrir, et cela se fait exprès depuis sa fiche.',
        gone: 'L’ordre est introuvable',
        goneHint: 'Il a peut-être été archivé. Revenez en arrière et ouvrez-le depuis la liste.',
        dictateClip: 'Note vocale de cet arrêt',
        mediaPending: 'Les heures sont enregistrées, mais il reste {count} photo(s) ou vidéo(s) à téléverser. Elles sont toujours là : appuyez de nouveau sur enregistrer quand vous aurez du réseau.',
        uploading: 'Envoi {n} sur {count} · {percent} %',
        draftRestored: 'Nous avons récupéré le rapport que vous aviez commencé sur ce téléphone.',
        errClosed: 'Le bureau a clôturé cet ordre pendant que vous écriviez. Ce que vous avez écrit est toujours là : demandez sa réouverture et enregistrez de nouveau.',
        errStopGone: 'Ce lieu ne fait plus partie de l’ordre : le bureau l’a retiré. Décochez-le ou demandez à qui planifie.',
        errHours: 'Les heures doivent être un nombre entre 0 et 24.',
        errQuantity: 'Une quantité de matériel ne peut pas être négative.',
        errTooLong: 'Un des textes est trop long. Raccourcissez-le un peu et enregistrez de nouveau.',
        errOffline: 'Pas de réseau : rien n’a été enregistré. Ce que vous avez écrit est toujours là ; enregistrez de nouveau quand vous aurez du réseau.',
      },
      incidents: {
        title: 'Incidents',
        placeholder: 'Ce qui s’est passé…',
        add: 'Noter',
        empty: 'Aucun incident',
        emptyHint: 'Notez ce qui empêche ou retarde le travail, pour que ce soit écrit.',
        resolve: 'Résoudre', resolved: 'Résolu',
        resolvedBy: 'résolu par {who}',
      },
      history: {
        title: 'Historique',
        notePlaceholder: 'Écrire une note sur l’ordre…',
        add: 'Noter',
        status: 'Statut : {from} → {to}',
        report: 'Rapport : {what}',
        incident: 'Incident : {what}',
      },
      confirm: {
        completeTitle: 'Terminer l’ordre',
        completeBody: 'Cela annoncera {hours} et le matériel saisi, ce qui sera ensuite imputé et facturé.',
        removeReportTitle: 'Supprimer le rapport',
        removeReportBody: 'Le rapport de {hours} sera supprimé et les heures de l’ordre recalculées.',
      },
      completed: 'Ordre terminé',
      reopened: 'Ordre réouvert',
      createdToast: 'Ordre {code} créé',
      savedToast: 'Ordre enregistré',
      search: { subtitle: 'Ouvrir l’ordre' },
      notifications: {
        categories: { late: 'Ordres en retard', assigned: 'Un ordre vous est attribué', breakdown: 'Une panne sur le terrain', reportAdded: 'Un nouveau rapport' },
        assigned: {
          title: 'Nouvel ordre pour vous',
          body: '{code} · {title}{where}',
        },
        breakdown: {
          title: 'Panne signalée',
          body: '{asset} · {code}',
          bodyWithWho: '{asset} · {code} — signalé par {who}',
        },
        // Del campo a la oficina, a los roles del ajuste `reportAlertRoles`.
        reportAdded: {
          title: 'Nouveau rapport',
          body: '{code} · {title}',
          bodyWho: '{code} · {title} — rédigé par {who}',
          bodyWhere: '{code} · {where}',
          bodyWhereWho: '{code} · {where} — rédigé par {who}',
        },
        late: {
          title: 'Ordres de travail en retard',
          body: '{count} ordre(s) ont dépassé leur date prévue. Le premier : {first}',
        },
      },
    },
    modules: { 'work-orders': 'Ordres de travail' },
    assistant: {
      openers: {
        'work-orders': {
          'my-day': 'Qu’est-ce que j’ai aujourd’hui ?',
          'whats-late': 'Quels ordres sont en retard ?',
          'new-order': 'Créer un ordre de travail',
          'log-hours': 'Saisir les heures d’un ordre',
        },
      },
    },
    permissions: {
      workorders: {
        read: 'Voir tous les ordres de travail, pas seulement les siens',
        write: 'Planifier : ouvrir des ordres et les modifier',
        report: 'Voir ses propres ordres et y saisir des rapports, des étapes et des incidents',
        manage: 'Terminer, réouvrir, annuler et archiver (inclut les modèles de rapport)',
        templates: 'Créer et modifier les modèles de rapport',
        import: 'Importer des ordres et des rapports d’un autre système',
      },
    },
    settings: {
      ns: {
        'work-orders': {
          title: 'Ordres de travail',
          subtitle: 'Comment votre entreprise travaille, et ce qu’elle exige pour clore un ordre',
          kinds: {
            label: 'Les types de travail que vous faites',
            help: 'Les vôtres, pas les nôtres. En renommant l’un, les ordres qui l’avaient gardent l’ancien nom.',
          },
          taskTypes: {
            label: 'Les types de tâche que vous répétez à un arrêt',
            help: 'Seulement si vous planifiez des tournées : un jardinier écrit la sienne, un installateur n’en a jamais besoin. Laissez vide et les tâches sont du texte libre.',
          },
          createFromPortalRequest: {
            label: 'Ouvrir un ordre quand un client demande quelque chose dans le portail',
            help: 'Il naît en brouillon et sans date, en nommant la demande dont il provient. N\'a d\'effet qu\'avec le portail client installé.',
          },
          portalRequestTypes: {
            label: 'Types de demande du portail qui ouvrent un ordre',
            help: 'Les ids tels qu\'ils sont configurés dans le portail. Vide signifie aucun.',
          },
          createFromAcceptedQuote: {
            label: 'Ouvrir un ordre quand un devis est accepté',
            help: 'Il naît en brouillon et sans date, et ses lignes deviennent les étapes. Ne fait quelque chose qu’avec le module de devis installé.',
          },
          allowUnplanned: {
            label: 'Laisser l equipe noter un travail sans ordre',
            help: 'Chaque travail ouvre son propre ordre, déjà clôturé et marqué non prévu. Désactivé, le bureau écrit l ordre en premier.',
          },
          requireCustomer: {
            label: 'Exiger un client sur chaque ordre',
            help: 'Laissez désactivé si vous planifiez aussi votre propre travail : une réparation à l’atelier, un stand.',
          },
          requireHoursToComplete: {
            label: 'Exiger au moins un rapport pour terminer',
            help: 'Activé, car terminer sans heures annonce un travail qui n’a rien coûté à qui l’impute.',
          },
          requireChecklistToComplete: {
            label: 'Exiger toutes les étapes faites pour terminer',
            help: 'Désactivé par défaut : une étape qui ne s’applique pas aujourd’hui bloquerait l’ordre.',
          },
          requireTargetsToComplete: {
            label: 'Exiger tous les objets clôturés pour terminer',
            help: 'Désactivé par défaut, pour la même raison : une tournée à court d’après-midi se clôture et le reste se replanifie, elle ne reste pas ouverte.',
          },
          breakdownAlertRoles: {
            label: 'Qui apprend qu’une panne est survenue sur le terrain',
            help: 'Des qu’un rapport dit qu’une machine s’est arretee. Vide le desactive.',
          },
          targetTypes: {
            label: 'Sur quoi peut porter un ordre',
            help: 'Les types de fiche proposés sous «Sur quoi». Vide propose tout ce que l’ERP sait chercher.',
          },
          allowFreeTextTargets: {
            label: 'Permettre de saisir à la main ce sur quoi porte un ordre',
            help: 'Désactivé, un ordre ne peut nommer qu’une fiche des types ci-dessus. Avec un seul type et ceci désactivé, le planificateur ne demande plus de quoi il s’agit et passe directement à la liste.',
          },
          autoCompleteWhenTargetsDone: {
            label: 'Terminer l’ordre tout seul à la clôture de son dernier arrêt',
            help: 'Quand un rapport clôture le dernier arrêt ouvert. Si une règle s’y oppose, l’ordre reste ouvert.',
          },
          reportAlertRoles: {
            label: 'Qui apprend qu’un rapport a été saisi',
            help: 'Avec l’ordre, le lieu et qui l’a rédigé. Vide le désactive.',
          },
          equipmentByTaskType: {
            label: 'À emporter, par type de tâche',
            help: 'Une ligne par type de tâche de W.2 : les outils et le matériel que l’équipe charge. Les ordres l’affichent comme « À emporter ». Un type vide n’affiche rien.',
            keyLabel: 'Type de tâche',
            columns: { tools: 'Outils', materials: 'Matériel' },
          },
          lateAlertRoles: {
            label: 'Qui apprend qu’un ordre dépasse sa date',
            help: 'Vérifié une fois chaque matin.',
          },
        },
      },
    },
    audit: {
      actions: {
        work_orders: {
          created: 'Ordre de travail créé',
          logged: 'Travail noté sans ordre',
          updated: 'Ordre modifié',
          planned: 'Ordre planifié',
          in_progress: 'Ordre commencé',
          on_hold: 'Ordre mis en attente',
          completed: 'Ordre terminé',
          cancelled: 'Ordre annulé',
          draft: 'Ordre remis en brouillon',
          archived: 'Ordre archivé',
          restored: 'Ordre remis dans les listes',
          note_added: 'Note ajoutée à l’ordre',
          report_added: 'Rapport saisi',
          report_removed: 'Rapport supprimé',
          template_created: 'Modèle de rapport créé',
          template_updated: 'Modèle de rapport modifié',
          template_published: 'Modèle de rapport publié',
          template_retired: 'Modèle de rapport retiré',
          worksheet_completed: 'Rapport guidé terminé',
          step_added: 'Étape ajoutée à l’ordre',
          step_done: 'Étape cochée',
          step_undone: 'Étape décochée',
          step_noted: 'Ce qui a été fait noté sur une étape',
          target_done: 'Arrêt clôturé',
          target_undone: 'Arrêt rouvert',
          task_added: 'Tâche ajoutée à un arrêt',
          task_done: 'Tâche faite à un arrêt',
          task_undone: 'Tâche décochée à un arrêt',
          task_noted: 'Ce qui a été fait noté sur une tâche',
          incident_added: 'Incident noté',
          incident_resolved: 'Incident résolu',
          imported: 'Ordre importé d’un autre système',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'workorders')))
